/** ORRERY main — UI, input, XR, sim loop. */

import { clamp, qAxis, qmul, qnorm, qFromTo, qrot, qnlerp, m4, m4persp, m4lookAt, lookRay, showErr } from './math.js';
import { installGlobalErrorHandlers, setErrorSink, endBootDeferral, expected } from './sim/report.js';
import { NC, N_ALLOWED, N, cellKm, DIR, NBR } from './sphere.js';
import { mergeRunRule } from './sim/ruleMode.js';
import { ruleForEra, availableEras, eraPatch } from './sim/timePanel.js';
import { setClockFace, setSeasonHold, livedTick, isLivedClock, SEASON_HOLDS } from './sim/clockFace.js';
import { scrubLivedSeason, setLivedSkyRate, setLivedDayRate } from './sim/sky.js';
import { W, generate, simTick, RULESETS, chronLog, formatAge, treeSummary, downloadSave, serializeRun, changeResolution, loadRunMeta, rerollTerrain, setLifeSpeed, enableWorldAsserts } from './world.js';
import { LANDSCAPES, landscapeById, drawLandscapeThumb, nameWorld } from './sim/landscapes.js';
import { resolvePlayLandscape } from './sim/playLandscape.js';
import { freshSeed } from './sim/rng.js';
import { layoutTree, treeToSvg } from './sim/lifeGuide.js';
import { encodeWorldId, decodeWorldId, parseWorldInput, worldIdOf } from './sim/seedword.js';
import { formatDescent, camDistMin } from './sim/plevel.js';
import { noteDroppedTicks } from './sim/meta.js';
import { detectEnding, finaleArtefact, formatFinaleMarkdown } from './sim/finale.js';
import { darkEnabled } from './sim/darkGate.js';
import {
  vandalDone, markVandalDone, pickVandalTarget, captureGarden, gardenDelta,
  awayTicks, writeAutosave, readAutosave, clearAutosave, saveGardenVisit, autosavePaused,
} from './sim/hooks.js';
import { isPlaytestMode, mountPlaytestUI } from './sim/playtest.js';
import {
  skipReveal, campaignBlurb,
  LESSONS, loadLessonProgress, lessonById, setCurrentLesson,
  completeLesson, lessonDone, shouldOfferDoor, nextIncompleteLesson,
  lessonChipLabel, huntMatches, markDoorSeen,
  resetLessonProgress,
} from './sim/teach.js';
import { ENT, respawnEntities, followTarget, presentAgents } from './agents.js';
import { initGL, gl, canvas, rebuildGeometry, refreshColours, uploadEntities, drawScene, vIdx, updateLocalHighlight, setGuildHighlight, setLocalHover, setOverlayMode, remeshPlanet, rebuildScatterLUTs, setGlobeSubd, GLOBE_SUBD, GLOBE_SUBD_ALLOWED, globeN, globeVertexCount, recommendGlobeSubd, effectiveGlobeSubd } from './render.js';
import {
  climatePanelChrome, refreshClimatePanel, bindClimatePanel,
} from './sim/climatePanel.js';
import { openLimitsSheet } from './sim/limitsSheet.js';
import {
  platesPanelChrome, refreshPlatesPanel, bindPlatesPanel, tectonicsAtCell, plateName,
} from './sim/platesPanel.js';
import { TOOLS, setTool, activeTool, useToolAt, inspectCell, pickCell, fingerOfGod,
  beginToolDrag, moveToolDrag, endToolDrag, undoStroke, redoStroke, canUndo,
  setCrustOceanic, setPinpoint, setBrushInvert,
  pricePreview, setScarcityMode, SCARCITY, scarcityMeta, setSelectedGuild, selectedGuild,
  BRUSH, brushKm, brushForTier, previewBrush,
  cullClade,
} from './tools.js';
import {
  addPaintLayer, duplicateLayer, removeLayer, moveLayer, setActiveLayer,
  setLayerVisible, setLayerOpacity, setLayerBlend, setLayerName,
  setPaintMaskMode, clipLayerToLand, clearLayerMask, flattenLayers,
  layerPanelState,
} from './sim/layers.js';
import { GUILDS, speciesMemoryReadout } from './sim/redox.js';
import {
  SCENARIOS, startScenario, evaluateScenario, CAMPAIGN, dailySeed,
} from './sim/god/scenario.js';
import {
  blankGenesis, PRESETS, applyPreset, randomizeGenesis, rulesetFromGenesis,
  decodeSeedString, applyGenesisToWorld, genesisFromPanel,
} from './sim/god/genesis.js';
import {
  setTimeRate, TIME_RATES, addBookmark, setLetItRun, shouldHaltFF,
  cycleTimeRate, timeClockInfo, cycleGaiaButton, gaiaDriveOf, gaiaModeMeta,
} from './sim/god/observe.js';
import { addToShelf, loadShelf } from './sim/god/shelf.js';
import { tipForTool, tipForId, SUITE_TIPS, RIBBON_TIPS } from './sim/god/tips.js';
import { decorateButton, iconSVG, DOCK_TAB_ICONS } from './sim/god/icons.js';
import {
  CAM_DIST_MAX, XR_SCALE_MIN, XR_SCALE_MAX,
  scaleRung, applyScalePreset, eorefById,
} from './sim/eoref.js';
import { keymapHelpLines } from './sim/keymap.js';
import { bindPlanetKeys, dispatchIntent } from './input.js';
import { onIntent } from './sim/intents.js';
import { dialogFocusables, trapTab } from './sim/focusTrap.js';

installGlobalErrorHandlers();
setErrorSink((detail) => showErr(detail));
try {
  if (new URLSearchParams(location.search).get('debug') === '1') globalThis.__ORRERY_DEBUG = true;
} catch { expected('ORR-EXPECTED-URL', 'debug param'); }
globalThis.__orreryErrors = () =>
  import('./sim/report.js').then((m) => {
    const errs = m.recentErrors().filter((e) => e.level !== 'info' || !e.expected);
    if (errs.length) console.table(errs.map((e) => ({ level: e.level, code: e.code, detail: e.detail })));
    else console.info('[orrery] no notable errors in ring');
    return errs;
  });
import { exportChronicle } from './chronicle.js';
import { audioInit, audioUpdate, playEvent, audioMute, installDarkAudioBus } from './audio.js';
import {
  createOriginSketch, applyOriginDigestToRule, originDigestSummary,
} from './sim/originSketch.js';
import {
  redoxGauge, keelingCurve, diversityCurve, whitakerPoints, transitSpectrum,
  phylogenyView, exportPaper,
} from './sim/instruments.js';
import {
  chartAreaSVG, redoxTowerSVG, spectrumSVG, whitakerSVG, coreStrataSVG,
  diversityStripSVG, synopticChartSVG,
} from './sim/viz.js';
import { momentRGB, legendEntries, legendKeyAt, legendGlossary } from './sim/lifeColour.js';
import {
  drawLocalView, layoutLocalPanel, stepFocus, hoverCellAt, beingAtLocalPixel,
  LOCAL_SIZES, LOCAL_SIZE_LABELS, LOCAL_SIZE_S, LOCAL_SIZE_M, LOCAL_SNAPS, LOCAL_GLOBE, LOCAL_RADII, LOCAL_RADIUS_LABELS,
  LOCAL_SEEK, LOCAL_SEEK_LABELS, resetFocusCache, huntGlance,
  localFrameIndex, localFrameLabel,
} from './localview.js';
import { presentAdvance, placeSentence, cellSun } from './sim/present.js';
import { considerThought, thoughtView, resetThought, thoughtMemory } from './sim/thought.js';
import { stepFlow, resetFlow } from './sim/flowviz.js';
import { ensureCatalogue, catalogueReady } from './sim/catalogueLoad.js';
import { HUD_CADENCE_MS } from './sim/hudCadence.js';
import { parseWorldCsv } from './sim/exophysics.js';
import { makeWorldRecord, applyRecordToRule } from './sim/worldRecord.js';
import { explainDrama, defineTerm, READING_LIST, toolsUnlocked } from './sim/glossary.js';
import { overlaysForPicker, overlayById, markTouch } from './sim/overlay.js';
import { downloadInstrumentPng } from './sim/exportPng.js';
import { EARTH_DIVERSITY, earthOverlaySVG } from './sim/earthRecord.js';
import { tideBudget } from './sim/tides.js';
import { iceShellBudget } from './sim/iceshell.js';
import { createTableState, syncTableFromShelf, pickTableSlotRay, slotToLoadMeta } from './sim/orreryTable.js';
import { getGpgpu } from './sim/gpgpu/index.js';
import { xrSession, camWorld, initXR, setupXR as setupXRModule } from './xr.js';
import { initHUD, updateHUD } from './hud.js';
import { PHONE_MQ, PHONE_LOCAL_PEEK, isPhone, syncPhoneUiClass, bindPhoneLocalDrag, bindPhoneDockDrag, clearLocalFreePos } from './phoneLayout.js';

const TABLE = createTableState();

/** Filled by ensureCatalogue() — null until Worlds opens (K17). */
let Cat = null;
let _catValidated = false;

async function fillCat() {
  if (!Cat) Cat = await ensureCatalogue();
  return Cat;
}

function logCatalogueValidation() {
  if (_catValidated || !Cat) return;
  _catValidated = true;
  const bad = Cat.validateCatalogueWorlds();
  if (bad.length) console.warn('[orrery] catalogue worlds failed sanitize:', bad);
  else console.log(`[orrery] catalogue · ${Cat.CATALOGUE_WORLDS.length} worlds ready`);
}

/** Dark UI modules — quality-400 K16; only pulled when `?dark=1`. */
let _darkHud = null;
let _darkSpec = null;
async function ensureDarkUi() {
  if (!darkEnabled()) return null;
  if (!_darkHud) {
    const [hud, spec] = await Promise.all([
      import('./sim/darkHud.js'),
      import('./sim/darkSpectacle.js'),
    ]);
    _darkHud = hud;
    _darkSpec = spec;
  }
  return { hud: _darkHud, spec: _darkSpec };
}
if (darkEnabled()) ensureDarkUi().catch(() => { expected('ORR-EXPECTED-LAZY', 'dark UI'); });

const S = {
  q: new Float32Array([0, 0, 0, 1]),
  table: TABLE,
  spin: 0.035,
  camDist: 3.1,
  camPanX: 0,
  camPanY: 0,
  scaleXR: 0.22,
  posXR: [0, 1.18, -0.52],
  canvasMode: false,
  landscape: 'auto',
  lookMode: 'photo',
  cloudFree: false,
  paused: false,
  originSketch: null,
  originPending: null,
  pitchShot: false,
  detail: 0,
  entFade: 0,
  exposure: 1.15,
  exposureTarget: 1.15,
  ceremonyUntil: 0,
  sunAng: 0.6,
  fps: 0,
  _fa: 0,
  _ft: 0,
  _t: 0,
  orbitGuides: true,
  orbitFlash: 0,
  activeTool: 'inspect',
  tier: 'Orbital',
  simAlpha: 1,
  inspect: null,
  follow: null,
  opacity: 1,
  xray: 0,
  grid: 0,
  localSize: LOCAL_SIZE_M,
  localSnap: 'br',
  localGlobe: 'rim',
  localRadius: 8,
  localPin: -1,       // >=0 pins the local window; -1 = auto-track
  localSeek: 'life',  // stay = hold densest; life = jump to recent growth
  dayWatch: false,
  thoughtOn: true,
  thoughtMind: false,
  faceCell: -1,
  faceUntil: 0,
  localExpanded: false,
  localParked: false,
  localSizeBeforePark: null,
  localHoverKey: null,   // legend id from map or key hover
  localHoverCell: -1,
  kbCursor: -1,          // keyboard cell cursor (M5/M8)
  planetLive: '',        // last aria-live planet blurb
  localLegendLock: null, // key locked by hovering the legend
  highlightGuild: null,
  angVel: [0, 0],     // spin inertia
  toolDrag: false,
  commitHold: null,
  letItRun: false,
  genesis: null,
  _localFocus: -1,
  _localPatch: null,
  catalogueId: null,
  catFilter: 'all',
  labDesk: 'station',
  catKind: 'TYPE',
  catQuery: '',
};

/** Updated when Local panel chrome is wired; called each frame. */
let syncLocalChrome = () => {};
let applyLocalLayout = () => {};
let syncSeekSeg = () => {};

const tmpQ = new Float32Array(4);
const _faceDir = [0, 0, 0];
const _faceWant = [0, 0.16, 0.987];
const _faceDq = new Float32Array(4);
const _faceTgt = new Float32Array(4);

function requestFace(cell, ms = 1800) {
  if (cell == null || cell < 0) return;
  S.faceCell = cell | 0;
  S.faceUntil = (S._t || 0) + ms;
}

/** Live warhead cell while cinematic follow is armed. */
function followFlightCell() {
  const d = W.dark;
  if (!d || (d.followFlight | 0) < 0) return -1;
  if ((d.followUntil | 0) > 0 && (W._tickIndex | 0) > (d.followUntil | 0)) {
    d.followFlight = -1;
    return -1;
  }
  const flights = W.flight || [];
  let f = flights[d.followFlight | 0];
  if (!f || f.dead) f = flights.find((x) => !x.dead);
  if (!f || f.dead || !f.path?.length) {
    d.followFlight = -1;
    return -1;
  }
  const at = Math.min(f.path.length - 1, Math.floor(f.at || 0));
  return f.path[at] | 0;
}

function faceToward(cell, dt) {
  if (cell == null || cell < 0 || grabbing || dragging || panning || S.toolDrag) return;
  qrot(S.q, DIR[cell * 3], DIR[cell * 3 + 1], DIR[cell * 3 + 2], _faceDir);
  const d = Math.max(-1, Math.min(1, _faceDir[0] * _faceWant[0] + _faceDir[1] * _faceWant[1] + _faceDir[2] * _faceWant[2]));
  const ang = Math.acos(d);
  qFromTo(_faceDq, _faceDir, _faceWant);
  qmul(_faceTgt, _faceDq, S.q);
  const k = ang > 1.05 ? 0.58 : ang > 0.5 ? 0.82 : 1.25;
  qnlerp(S.q, S.q, _faceTgt, 1 - Math.exp(-k * Math.max(0.001, dt)));
}

function cellFacingZ(cell) {
  if (cell == null || cell < 0) return 1;
  qrot(S.q, DIR[cell * 3], DIR[cell * 3 + 1], DIR[cell * 3 + 2], _faceDir);
  return _faceDir[2];
}

function setDayWatch(on) {
  const next = !!on;
  if (next === S.dayWatch) return;
  if (next) {
    S._dayWatchWasPaused = S.paused;
    S.paused = true;
    showMoment('A day', 'The rock holds. The light moves.', 'Shift+D leaves · Space too');
  } else {
    if (S._dayWatchWasPaused === false) {
      S.paused = false;
    }
    S._dayWatchWasPaused = undefined;
  }
  S.dayWatch = next;
  document.getElementById('localpanel')?.classList.toggle('daywatch', S.dayWatch);
}

let _sunSign = 0;
function maybeDayMoment(cell) {
  if (cell < 0) return;
  const sun = cellSun(cell);
  const sign = sun > 0.04 ? 1 : sun < -0.04 ? -1 : _sunSign;
  if (sign !== _sunSign && _sunSign !== 0) {
    // When Cernunnos thought is on, dawn/dusk ride that voice instead of the big toast.
    if (S.thoughtOn && !S.dayWatch) {
      /* thought softBeat handles dusk/dawn on focus */
    } else if (sign > 0) {
      showMoment('Dawn', placeSentence(cell) || 'The light returns', S.dayWatch ? 'Watching a day' : '');
    } else {
      showMoment('Dusk', 'The valley goes dark', S.dayWatch ? 'Watching a day' : '');
    }
  }
  _sunSign = sign;
}

let _thoughtTimer = 0;
let _thoughtAcc = 0;

function showThought(line) {
  const el = document.getElementById('thought');
  if (!el || !line?.text) return;
  el.hidden = false;
  el.classList.remove('warn', 'wild', 'quiet', 'soft', 'show');
  el.classList.add(line.tone || 'soft');
  el.querySelector('.th-kicker').textContent = line.kicker || 'Cernunnos';
  el.querySelector('.th-text').textContent = line.text;
  const sug = el.querySelector('.th-suggest');
  if (sug) {
    if (line.suggest) {
      sug.hidden = false;
      sug.textContent = line.suggest;
    } else {
      sug.hidden = true;
      sug.textContent = '';
    }
  }
  const trail = el.querySelector('.th-trail');
  if (trail) {
    const prev = thoughtMemory().lines.slice(-3, -1);
    trail.textContent = prev.length ? prev.join(' · ') : '';
  }
  // Reflow before show so the transition runs.
  void el.offsetWidth;
  el.classList.add('show');
  clearTimeout(_thoughtTimer);
  const hold = line.tone === 'warn' ? 5200 : line.tone === 'wild' ? 4800 : 6200;
  _thoughtTimer = setTimeout(() => {
    el.classList.remove('show');
    setTimeout(() => { if (!el.classList.contains('show')) el.hidden = true; }, 900);
  }, hold);
}

/** Lazy handle for optional on-device mind (thoughtMind.js). */
let _thoughtMind = null;
let _thoughtMindRewrite = 0;

async function loadThoughtMind() {
  if (_thoughtMind) return _thoughtMind;
  _thoughtMind = await import('./sim/thoughtMind.js');
  return _thoughtMind;
}

function updateThoughtMindHint(snap) {
  const hint = document.getElementById('viewThoughtMindHint');
  const btn = document.getElementById('viewThoughtMind');
  if (btn) {
    const on = snap?.status === 'ready' || snap?.status === 'loading';
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (snap?.status === 'loading') btn.textContent = 'Local mind…';
    else btn.textContent = 'Local mind';
  }
  if (!hint) return;
  hint.classList.toggle('is-error', snap?.status === 'error' || snap?.status === 'unsupported');
  const phone = isPhone();
  if (!snap || snap.status === 'off') {
    hint.textContent = phone
      ? 'Optional on-device voice (~220 MB, WebGPU).'
      : 'Templates always speak. Local mind adds richer lines on-device (WebGPU · ~220 MB once).';
    return;
  }
  if (snap.status === 'loading') {
    hint.textContent = snap.progress || 'Downloading / loading local mind…';
    return;
  }
  if (snap.status === 'ready') {
    const src = snap.source === 'local' ? 'prepackaged' : 'cached download';
    hint.textContent = phone
      ? `Mind ready (${src}).`
      : `Local mind ready (${src} · ${snap.modelLabel}). Soft lines may be rewritten on-device.`;
    return;
  }
  if (snap.status === 'unsupported') {
    hint.textContent = snap.error || 'WebGPU unavailable — templates still speak.';
    return;
  }
  if (snap.status === 'error') {
    const err = snap.error || 'Local mind failed — templates still speak.';
    hint.textContent = phone && err.length > 96 ? `${err.slice(0, 93)}…` : err;
  }
}

function tickThoughtVoice(dt) {
  if (!S.thoughtOn || doorIsOpen() || localKeyOpen() || S.pitchShot) return;
  if (document.body.classList.contains('ceremony')) return;
  const moment = document.getElementById('moment');
  if (moment?.classList.contains('show') && !_pitchHoldMoment) return;
  _thoughtAcc += dt;
  if (_thoughtAcc < 0.45) return;
  _thoughtAcc = 0;
  const view = thoughtView({
    cell: S._localFocus >= 0 ? S._localFocus : (S.localPin >= 0 ? S.localPin : -1),
    paused: S.paused,
  });
  const line = considerThought(view);
  if (!line) return;
  showThought(line);

  // Optional local rewrite — never blocks templates; warn/wild stay literal.
  if (!S.thoughtMind || !_thoughtMind?.shouldRewrite?.(line)) return;
  const gen = ++_thoughtMindRewrite;
  _thoughtMind.rewriteThought(line, view).then((richer) => {
    if (!richer || gen !== _thoughtMindRewrite || !S.thoughtOn || !S.thoughtMind) return;
    showThought(richer);
  }).catch(() => { expected('ORR-EXPECTED-LAZY', 'thought rewrite'); });
}

const VIEW = m4(), PROJ = m4();
let lastT = 0, simAcc = 0, geomDirty = false;
let dragging = false, panning = false, lastX = 0, lastY = 0, grabbing = false;
let _landPickDone = null;

function isDemoMode() {
  try { return new URLSearchParams(location.search).get('demo') === '1'; } catch { return false; }
}

/** Active Vandal first-act coach (NEXT #1). Cleared after one Strike lands. */
let _vandalHook = null;
/** Playtest overlay API when ?playtest=1. */
let _playtest = null;

function startVandalHook(opts = {}) {
  const target = pickVandalTarget(W);
  _vandalHook = { ...target, t0: performance.now() };
  setTool('inspect');
  setDockTab('tools', { open: !isPhone() });
  setSuiteDesk('tools', 'strike');
  syncVandalSuggest();
  lookAtCell(target.cell, { ms: 2200 });
  // Gold stroke so the patch reads from orbit.
  if (!W.strokeMark || W.strokeMark.length !== NC) W.strokeMark = new Float32Array(NC);
  W.strokeMark[target.cell] = 1;
  for (let k = 0; k < 4; k++) {
    const n = NBR[target.cell * 4 + k];
    if (n >= 0) W.strokeMark[n] = Math.max(W.strokeMark[n] || 0, 0.55);
  }
  W._strokeTick = W._tickIndex || 0;
  setPaused(false);
  const strike = target.tool === 'ignite' ? 'Ignite' : 'Meteor';
  const sub = opts.demo
    ? `Drag to spin · pick ${strike} below when you want to try it. Demo uses Strike only — not Evil.`
    : `Drag to spin the globe · pick ${strike} in Strike when you're ready. ${target.hint}`;
  showMoment('Try this', target.label, sub);
}

function syncVandalSuggest() {
  document.querySelectorAll(TOOL_BTN_SEL).forEach((b) => {
    const on = !!(_vandalHook && b.dataset.id === _vandalHook.tool);
    b.classList.toggle('vandal-suggest', on);
  });
}

function finishVandalHook(res) {
  if (!_vandalHook) return;
  const tool = res?.tool || activeTool;
  if (tool !== 'ignite' && tool !== 'meteor') return;
  if (!res?.ok) return;
  if (tool === 'ignite' && !(res.fireLit > 0)) return;
  const cell = (res.cell >= 0 ? res.cell : _vandalHook.cell) | 0;
  markVandalDone();
  _vandalHook = null;
  syncVandalSuggest();
  markDoorSeen();
  skipReveal();
  lookAtCell(cell, { ms: 2600, descend: true });
  S.localExpanded = true;
  applyLocalLayout();
  _playtest?.noteDescend();
  const line = tool === 'ignite'
    ? 'Fire on the square — ash ahead, animals fleeing.'
    : 'A crater in the crust — dust lifting into the air.';
  showMoment('Receipt', line, 'Descend into the map, read the place, then return.');
  armLocalCue(cell, tool, {
    delayMs: 1200,
    title: 'On the map',
    sub: 'Orbit already promised this motion.',
    skipPlace: true,
  });
}

function persistGardenLeave() {
  try {
    const garden = captureGarden(W);
    saveGardenVisit(garden);
    if (!autosavePaused()) {
      writeAutosave({
        data: serializeRun(),
        garden,
        leftAt: Date.now(),
      });
    }
  } catch { expected('ORR-EXPECTED-SAVE', 'serialize mid-boot'); }
}

function rebuildAfterLoad() {
  W._gpgpuDirty = true;
  rebuildGeometry();
  respawnEntities();
  uploadEntities();
  rebuildScatterLUTs();
  updateHUD();
  refreshLab();
  refreshLayerPanel();
}

/** Restore autosave and show a one-line place delta (NEXT #2). */
function maybeGardenReturn() {
  if (isDemoMode() || isPlaytestMode()) return false;
  let pitch = null;
  try { pitch = new URLSearchParams(location.search).get('pitch'); } catch { expected('ORR-EXPECTED-URL', 'pitch param'); }
  if (pitch) return false;
  const auto = readAutosave();
  if (!auto?.data || !auto.garden) return false;
  const leftAt = auto.leftAt || auto.garden.at || 0;
  const elapsed = Date.now() - leftAt;
  if (elapsed < 60_000) return false;
  try {
    loadRunMeta(auto.data);
    rebuildAfterLoad();
    const before = auto.garden;
    const n = awayTicks(elapsed);
    for (let i = 0; i < n; i++) simTick();
    const after = captureGarden(W);
    const line = gardenDelta(before, after) || 'Your world kept its place while you were away.';
    clearAutosave();
    saveGardenVisit(after);
    if (!autosavePaused()) {
      writeAutosave({ data: serializeRun(), garden: after, leftAt: Date.now() });
    }
    setTimeout(() => {
      showMoment('While you were away', line, formatAge(W.ageYr));
    }, 900);
    return true;
  } catch (e) {
    console.warn('[orrery] garden return failed', e);
    return false;
  }
}

function setBootPhase(label, detail = '') {
  const el = document.getElementById('bootload');
  if (!el) return;
  el.classList.remove('hidden');
  const msg = el.querySelector('.boot-msg');
  const sub = el.querySelector('#bootsub') || el.querySelector('.boot-sub');
  if (msg) msg.textContent = label || 'Forming world';
  if (sub) sub.textContent = detail || '';
}

function findCoastalCell() {
  for (let c = 0; c < NC; c++) {
    if (W.h[c] < W.seaLevel) continue;
    for (let k = 0; k < 4; k++) {
      if (W.h[NBR[c * 4 + k]] < W.seaLevel) return c;
    }
  }
  return 0;
}

function focusCoastForLesson(c = findCoastalCell()) {
  requestFace(c);
  S.localGlobe = 'rim';
  if (S.localRadius < 8) S.localRadius = 8;
  return c;
}

function beginHoldEarthLesson() {
  _taughtAlive = true;
  S._holdStep = 0;
  S._lessonSpun = false;
  S._holdQ0 = null;
  S._lessonCam0 = S.camDist;
  S.localPin = -1;
  S.localSeek = 'life';
  setPaused(false);
  applyScalePreset(S, 'hold');
  focusCoastForLesson();
  setTool('inspect');
  setDockTab('tools', { open: false });
  setSuiteDesk('tools', 'land');
  if (isPhone()) setPhoneDock(false);
  else {
    document.getElementById('dock')?.classList.remove('collapsed');
    document.getElementById('docktoggle')?.setAttribute('aria-expanded', 'true');
  }
  syncToolRail();
  showMoment('Hold Earth', 'Hold a planet',
    isPhone()
      ? 'Drag to spin · pinch-zoom · tap the coast for the map'
      : 'Drag to spin · scroll to come closer · click the coast for the map');
}
const CAM_FOV = 50 * Math.PI / 180;
const hands = [
  { active: false, pos: [0, 0, 0], grab: false, prev: null, vel: [0, 0, 0] },
  { active: false, pos: [0, 0, 0], grab: false, prev: null, vel: [0, 0, 0] },
];

function planetPos() {
  return [S.camPanX || 0, S.camPanY || 0, 0];
}

function resetCamPan() {
  S.camPanX = 0;
  S.camPanY = 0;
}

function panGlobe(dxPx, dyPx) {
  const rect = canvas.getBoundingClientRect();
  const worldH = 2 * S.camDist * Math.tan(CAM_FOV / 2);
  const worldW = worldH * (rect.width / Math.max(1, rect.height));
  const lim = S.camDist * 1.25;
  S.camPanX = clamp(S.camPanX + (dxPx / Math.max(1, rect.width)) * worldW, -lim, lim);
  S.camPanY = clamp(S.camPanY - (dyPx / Math.max(1, rect.height)) * worldH, -lim, lim);
}

function needGeom() { geomDirty = true; }

function afterLayerEdit() {
  refreshColours(1);
  needGeom();
  refreshLayerPanel();
}

function refreshLayerPanel() {
  const host = document.getElementById('layerlist');
  if (!host) return;
  if (host.contains(document.activeElement) && document.activeElement.classList?.contains('layer-name')) return;
  const st = layerPanelState(W);
  if (!st) {
    host.innerHTML = '<p class="god-note" style="margin:0">Generate a world to get a stack.</p>';
    return;
  }
  const rows = [];
  rows.push(`<div class="layer-row locked" data-layer="base" role="option">
    <button type="button" class="layer-eye" data-eye="base" title="Hide generated land">${st.baseVisible ? '◉' : '○'}</button>
    <span class="layer-name">${st.baseName}</span>
    <span class="layer-blend">base</span>
  </div>`);
  for (const L of st.layers) {
    rows.push(`<div class="layer-row${L.active ? '' : ''}" data-layer="${L.id}" role="option" aria-selected="${L.active ? 'true' : 'false'}" aria-pressed="${L.active ? 'true' : 'false'}">
      <button type="button" class="layer-eye" data-eye="${L.id}" title="Hide layer">${L.visible ? '◉' : '○'}</button>
      <input class="layer-name" id="layer-name-${L.id}" name="layer-name-${L.id}" data-rename="${L.id}" value="${L.name.replace(/"/g, '&quot;')}" aria-label="Layer name">
      <span class="layer-blend">${L.blend}${L.hasMask ? ' · mask' : ''}</span>
    </div>`);
  }
  host.innerHTML = rows.join('');
  const op = document.getElementById('layeropacity');
  const opv = document.getElementById('layeropacityval');
  const blend = document.getElementById('layerblend');
  const paint = document.getElementById('layerpaint');
  const active = st.layers.find((L) => L.active);
  if (op && active) {
    op.value = String(Math.round(active.opacity * 100));
    if (opv) opv.textContent = `${op.value}%`;
  }
  if (blend && active) blend.value = active.blend;
  if (paint) paint.value = st.paintMask ? 'mask' : 'height';
}

function bindLayerPanel() {
  const host = document.getElementById('layerlist');
  if (!host) return;
  host.addEventListener('click', (e) => {
    const eye = e.target.closest('[data-eye]');
    if (eye) {
      e.stopPropagation();
      const id = eye.dataset.eye === 'base' ? 'base' : +eye.dataset.eye;
      if (id === 'base') {
        setLayerVisible(W, 'base', !(W.layerStack?.baseVisible !== false));
      } else {
        const L = W.layerStack?.layers.find((x) => x.id === id);
        if (L) setLayerVisible(W, id, !L.visible);
      }
      afterLayerEdit();
      return;
    }
    const row = e.target.closest('[data-layer]');
    if (!row || row.dataset.layer === 'base') return;
    setActiveLayer(W, +row.dataset.layer);
    refreshLayerPanel();
  });
  host.addEventListener('change', (e) => {
    const inp = e.target.closest('[data-rename]');
    if (!inp) return;
    setLayerName(W, +inp.dataset.rename, inp.value);
  });
  document.getElementById('layeradd')?.addEventListener('click', () => {
    addPaintLayer(W, 'Stroke');
    afterLayerEdit();
  });
  document.getElementById('layerdup')?.addEventListener('click', () => {
    if (!duplicateLayer(W)) showErr('Layer cap (12) or nothing to copy');
    else afterLayerEdit();
  });
  document.getElementById('layerdel')?.addEventListener('click', () => {
    if (!removeLayer(W, W.layerStack?.activeId)) showErr('Keep at least one paint layer');
    else afterLayerEdit();
  });
  document.getElementById('layerup')?.addEventListener('click', () => {
    moveLayer(W, W.layerStack?.activeId, 1);
    afterLayerEdit();
  });
  document.getElementById('layerdown')?.addEventListener('click', () => {
    moveLayer(W, W.layerStack?.activeId, -1);
    afterLayerEdit();
  });
  document.getElementById('layerflatten')?.addEventListener('click', () => {
    if (!W.layerStack) return;
    if (!confirm('Flatten bakes every layer into Land. You cannot undo this.')) return;
    flattenLayers(W);
    afterLayerEdit();
    showErr('Flattened — the stack is Land now');
  });
  document.getElementById('layeropacity')?.addEventListener('input', (e) => {
    const v = (+e.target.value) / 100;
    const id = W.layerStack?.activeId;
    if (id) setLayerOpacity(W, id, v);
    const opv = document.getElementById('layeropacityval');
    if (opv) opv.textContent = `${e.target.value}%`;
    refreshColours(1);
    needGeom();
  });
  document.getElementById('layerblend')?.addEventListener('change', (e) => {
    const id = W.layerStack?.activeId;
    if (id) setLayerBlend(W, id, e.target.value);
    afterLayerEdit();
  });
  document.getElementById('layerpaint')?.addEventListener('change', (e) => {
    setPaintMaskMode(W, e.target.value === 'mask');
    showErr(e.target.value === 'mask' ? 'Raise reveals the mask, Lower conceals' : 'Raise / Lower write height');
  });
  document.getElementById('layerclipland')?.addEventListener('click', () => {
    clipLayerToLand(W);
    afterLayerEdit();
  });
  document.getElementById('layerclearmask')?.addEventListener('click', () => {
    clearLayerMask(W);
    afterLayerEdit();
  });
  refreshLayerPanel();
}

function runGenerate(seed, ruleIn, opts = {}) {
  setBootPhase('Forming world', '');
  const session = {
    deepTime: W.rule?.deepTime,
    startAgeGa: W.rule?.startAgeGa,
    fixedDtYr: W.rule?.fixedDtYr,
    tutorial: W.rule?.tutorial,
  };
  const rule = mergeRunRule(ruleIn, session);
  if (!opts.skipOrigin && wantsOriginCeremony(rule) && !S.originSketch) {
    beginOriginCeremony(seed, rule, opts.afterMeta);
    return;
  }
  W._bootPhase = setBootPhase;
  generate(seed, rule);
  W._bootPhase = null;
  W._canvasMode = !!S.canvasMode;
  resetFlow();
  W._gpgpuDirty = true;
  rebuildGeometry();
  refreshColours(1);
  respawnEntities();
  uploadEntities();
  try { rebuildScatterLUTs(); } catch { expected('ORR-EXPECTED-GL', 'scatter LUTs'); }
  resetFocusCache();
  S.follow = null;
  if (S.localSeek === 'life') S.localPin = -1;
  document.getElementById('bootload')?.classList.add('hidden');
  resetMomentAnnouncer();
  refreshToolGates();
  updateHUD();
  refreshLab();
  refreshLayerPanel();
  if (TABLE.enabled) syncTableFromShelf(TABLE, W);
  opts.afterMeta?.(W.originDigest || null);
}

function wantsOriginCeremony(rule) {
  try {
    const url = new URL(location.href);
    if (url.searchParams.get('skipOrigin') === '1') return false;
    if (url.searchParams.get('origin') === '1') return true;
  } catch { expected('ORR-EXPECTED-URL', 'origin params'); }
  if (!(rule?.earthLike || rule?.thrive)) return false;
  try {
    if (sessionStorage.getItem('orrery-origin-skip') === '1') return false;
  } catch { expected('ORR-EXPECTED-STORAGE', 'origin-skip read'); }
  return true;
}

function beginOriginCeremony(seed, rule, afterMeta = null) {
  S.originPending = { seed, rule, afterMeta };
  S.originSketch = createOriginSketch(seed, { earthLike: rule?.earthLike !== false });
  S._originWasPaused = S.paused;
  S.paused = true;
  S.camDist = Math.max(S.camDist || 2.4, 2.7);
  document.getElementById('bootload')?.classList.add('hidden');
  document.body.classList.add('origin-play');
  const cap = S.originSketch.caption;
  showMoment(cap.title, cap.body, 'Esc skips · formation writes the sphere');
}

function finishOriginCeremony() {
  const sk = S.originSketch;
  const pending = S.originPending;
  if (!sk || !pending) {
    S.originSketch = null;
    S.originPending = null;
    document.body.classList.remove('origin-play');
    return;
  }
  if (!sk.done) sk.skip();
  const dig = sk.digest || sk.pendingDigest;
  const rule = applyOriginDigestToRule(pending.rule, dig);
  S.originSketch = null;
  S.originPending = null;
  document.body.classList.remove('origin-play');
  W._pendingOriginDigest = dig;
  runGenerate(pending.seed, rule, { skipOrigin: true, afterMeta: pending.afterMeta });
  S.paused = !!S._originWasPaused;
  showMoment(
    dig.hasMoon ? 'A world with a Moon' : 'A world alone',
    originDigestSummary(dig),
    'The sphere remembers the hit',
  );
}

function skipOriginCeremony() {
  if (!S.originSketch) return;
  try { sessionStorage.setItem('orrery-origin-skip', '1'); } catch { expected('ORR-EXPECTED-STORAGE', 'origin-skip write'); }
  S.originSketch.skip();
  finishOriginCeremony();
}

/** Load a shelf / table slot onto the main planet. */
function loadTableSlot(slot) {
  const meta = slotToLoadMeta(slot);
  if (!meta) return false;
  try {
    const before = slot.garden || meta.garden || null;
    loadRunMeta(meta);
    W.worldName = meta.worldName || slot.name || W.worldName;
    rebuildAfterLoad();
    TABLE.activeId = slot.id;
    if (TABLE.enabled) syncTableFromShelf(TABLE, W);
    if (before) {
      const n = awayTicks(Math.max(0, Date.now() - (before.at || 0)));
      for (let i = 0; i < n; i++) simTick();
      const after = captureGarden(W);
      const line = gardenDelta(before, after);
      showMoment(
        'While you were away',
        line || (slot.name || 'World'),
        line ? formatAge(W.ageYr) : `seed ${meta.seed}`,
      );
    } else {
      showMoment('Orrery', slot.name || 'World', `seed ${meta.seed}`);
    }
    return true;
  } catch (e) {
    showErr(String(e.message || e));
    return false;
  }
}

const TOOL_BTN_SEL = '#toolsLand button, #toolsLife button, #toolsStrike button, #toolsClimate button, #toolsSample button, #toolsEvil button';

/** Phone minimap: Peek (200px) ↔ Full — skip the I/C/S/M/L ladder. */
function stepPhoneLocalFrame(dir) {
  if (S.localParked && dir > 0) {
    unparkLocalMap();
    return true;
  }
  if (dir > 0) {
    if (S.localExpanded) return true;
    S.localSize = PHONE_LOCAL_PEEK;
    S.localExpanded = true;
    const focus = S.focusCell ?? S.pinCell ?? S.kbCursor ?? S._localFocus ?? 0;
    const last = W.receipts?.[W.receipts.length - 1]?.tool;
    armLocalCue(focus, last);
  } else if (S.localExpanded) {
    S.localExpanded = false;
    S.localSize = PHONE_LOCAL_PEEK;
  } else {
    parkLocalMap();
    return true;
  }
  try { applyLocalLayout?.(); } catch { expected('ORR-EXPECTED-LAYOUT', 'local layout'); }
  return true;
}

function syncPhoneLocalChrome() {
  const panel = document.getElementById('localpanel');
  const toggle = document.getElementById('localmaptoggle');
  const frame = document.getElementById('localframe');
  const park = document.getElementById('localpark');
  const phone = isPhone();
  panel?.classList.toggle('phone-map', phone);
  panel?.classList.toggle('phone-peek', phone && !S.localExpanded && !S.localParked);
  panel?.classList.toggle('phone-full', phone && !!S.localExpanded);
  if (toggle) {
    const show = phone && !S.localParked;
    toggle.hidden = !show;
    toggle.textContent = S.localExpanded ? 'Peek' : 'Full map';
    toggle.setAttribute('aria-pressed', S.localExpanded ? 'true' : 'false');
    toggle.setAttribute('aria-label', S.localExpanded ? 'Shrink map to peek' : 'Expand map to full screen');
  }
  if (frame) frame.hidden = phone;
  const tag = document.getElementById('localframetag');
  if (phone && tag) {
    tag.textContent = S.localExpanded ? 'Full' : 'Map';
    tag.title = S.localExpanded ? '' : 'Drag bar to move · double-tap bar to snap corner';
  }
  if (park && phone) {
    park.title = S.localExpanded ? 'Hide map' : 'Tuck map away';
    park.setAttribute('aria-label', park.title);
  }
}

/** Phone bottom-sheet dock: open/close + scrim so the globe stays tappable. */
function setPhoneDock(open) {
  const dock = document.getElementById('dock');
  const btn = document.getElementById('docktoggle');
  const fab = document.getElementById('dockfab');
  const scrim = document.getElementById('dockscrim');
  const ui = document.getElementById('ui');
  if (!dock) return;
  // Phone uses is-open, not collapsed — collapsed hides .dock-tabs/.dock-body in index.html.
  if (open) dock.classList.remove('collapsed');
  dock.classList.toggle('is-open', !!open);
  scrim?.classList.toggle('on', !!open);
  ui?.classList.toggle('phone-dock-open', !!open);
  btn?.setAttribute('aria-expanded', open ? 'true' : 'false');
  btn?.setAttribute('aria-pressed', open ? 'true' : 'false');
  fab?.setAttribute('aria-expanded', open ? 'true' : 'false');
  fab?.setAttribute('aria-pressed', open ? 'true' : 'false');
  syncChromeFabs();
}

/** Hide dock for overlays — desktop collapses; phone closes the bottom sheet. */
function hideDockForOverlay() {
  if (isPhone()) setPhoneDock(false);
  else document.getElementById('dock')?.classList.add('collapsed');
}

/** Floating menu / map icons — visible only when their panel is tucked. */
function syncChromeFabs() {
  const dock = document.getElementById('dock');
  const dockFab = document.getElementById('dockfab');
  const dockToggle = document.getElementById('docktoggle');
  const localFab = document.getElementById('localfab');
  const localPanel = document.getElementById('localpanel');
  const phone = isPhone();
  if (dockToggle) {
    const dockOpen = phone
      ? dock?.classList.contains('is-open')
      : !dock?.classList.contains('collapsed');
    dockToggle.setAttribute('aria-expanded', dockOpen ? 'true' : 'false');
    dockToggle.setAttribute('aria-pressed', dockOpen ? 'true' : 'false');
    dockToggle.title = dockOpen ? 'Close menu' : 'Open menu';
  }
  if (dockFab) {
    /* Desktop uses the topbar menu by the brand — FAB is phone-only. */
    const dockOpen = phone && dock?.classList.contains('is-open');
    const showDockFab = phone && !dockOpen;
    dockFab.classList.toggle('is-on', showDockFab);
    dockFab.hidden = !showDockFab;
    dockFab.setAttribute('aria-expanded', dockOpen ? 'true' : 'false');
    dockFab.title = dockOpen ? 'Close menu' : 'Open menu';
  }
  if (localFab && localPanel) {
    const parked = !!S.localParked;
    localPanel.classList.toggle('parked', parked);
    localFab.hidden = !parked;
    localFab.classList.toggle('is-on', parked);
    localFab.setAttribute('aria-expanded', parked ? 'false' : 'true');
  }
}

function flashChromeFast(el, ms = 180) {
  if (!el) return;
  el.classList.add('chrome-fast');
  clearTimeout(flashChromeFast._t);
  flashChromeFast._t = setTimeout(() => el.classList.remove('chrome-fast'), ms);
}

function parkLocalMap() {
  if (S.localParked) return;
  S.localSizeBeforePark = {
    size: S.localSize,
    expanded: !!S.localExpanded,
  };
  S.localParked = true;
  S.localExpanded = false;
  flashChromeFast(document.getElementById('localpanel'));
  try { applyLocalLayout?.(); } catch { expected('ORR-EXPECTED-LAYOUT', 'local layout'); }
  syncChromeFabs();
}

function unparkLocalMap() {
  if (!S.localParked) return;
  const prev = S.localSizeBeforePark;
  S.localParked = false;
  if (prev) {
    S.localSize = prev.size || (isPhone() ? PHONE_LOCAL_PEEK : LOCAL_SIZES[0]);
    S.localExpanded = !!prev.expanded;
  } else {
    S.localSize = isPhone() ? PHONE_LOCAL_PEEK : LOCAL_SIZE_M;
    S.localExpanded = false;
  }
  flashChromeFast(document.getElementById('localpanel'));
  try { applyLocalLayout?.(); } catch { expected('ORR-EXPECTED-LAYOUT', 'local layout'); }
  syncChromeFabs();
}

/** Collapse local map quickly: Full → icon, or icon → parked FAB. */
function tuckLocalMap() {
  flashChromeFast(document.getElementById('localpanel'));
  if (isPhone()) {
    if (S.localExpanded) {
      S.localExpanded = false;
      S.localSize = PHONE_LOCAL_PEEK;
      try { applyLocalLayout?.(); } catch { expected('ORR-EXPECTED-LAYOUT', 'local layout'); }
      return;
    }
    parkLocalMap();
    return;
  }
  if (S.localExpanded) {
    S.localExpanded = false;
    S.localSize = LOCAL_SIZES[0];
    try { applyLocalLayout?.(); } catch { expected('ORR-EXPECTED-LAYOUT', 'local layout'); }
    return;
  }
  const fi = localFrameIndex(S.localSize, false);
  if (fi > 0) {
    S.localSize = LOCAL_SIZES[0];
    try { applyLocalLayout?.(); } catch { expected('ORR-EXPECTED-LAYOUT', 'local layout'); }
    return;
  }
  parkLocalMap();
}

/** Floating chip: which verb is armed, and a one-tap return to Look. */
function syncToolRail() {
  const rail = document.getElementById('toolrail');
  const arm = document.getElementById('toolrailArm');
  const clear = document.getElementById('toolrailClear');
  if (!rail || !arm) return;
  const tool = TOOLS.find((t) => t.id === activeTool) || TOOLS[0];
  const armed = tool.id !== 'inspect';
  rail.dataset.armed = armed ? 'true' : 'false';
  arm.dataset.armed = armed ? 'true' : 'false';
  decorateButton(arm, tool.id, tool.name);
  arm.title = armed ? `${tool.name} — change tool` : 'Tools — pick a verb';
  if (clear) {
    clear.hidden = !armed;
    clear.innerHTML = iconSVG('inspect');
    clear.title = 'Look / spin the globe';
  }
}

function refreshToolGates() {
  const unlocked = toolsUnlocked(W);
  const buttons = document.querySelectorAll(TOOL_BTN_SEL);
  let needInspect = false;
  for (const b of buttons) {
    const id = b.dataset?.id;
    if (!id) continue;
    const locked = unlocked[id] === false;
    b.disabled = locked;
    b.classList.toggle('tool-locked', locked);
    if (locked && id === activeTool) needInspect = true;
  }
  if (needInspect) setTool('inspect');
  buttons.forEach((x) => x.setAttribute('aria-pressed', x.dataset.id === activeTool ? 'true' : 'false'));
  syncToolRail();
  syncVandalSuggest();
}

function setRuleset(i) {
  const r = RULESETS[i];
  if (!r) return;
  S.catalogueId = null;
  clearCatalogueSelection();
  /* A ruleset that declares its own clock owns it. Carrying the previous
     world's deepTime / startAgeGa into Earth Thrive turned the demo Earth into a
     lifeless deep-time run, because the Holocene biosphere is only seeded on the
     modern path. */
  const ownsClock = !!r.thrive || r.startAgeGa != null;
  runGenerate(W.seed, ownsClock
    ? mergeRunRule(r)
    : mergeRunRule(r, { deepTime: W.rule?.deepTime, startAgeGa: W.rule?.startAgeGa }));
}

function clearCatalogueSelection() {
  const list = document.getElementById('catlist');
  if (!list) return;
  [...list.querySelectorAll('.cat-item')].forEach((b) => b.setAttribute('aria-pressed', 'false'));
}

async function loadCatalogueItem(item) {
  if (!item || item.k !== 'BODY') return;
  await fillCat();
  const r = Cat.rulesetFromCatalogue(item);
  if (!r) return;
  S.catalogueId = item.id;
  const list = document.getElementById('catlist');
  if (list) {
    [...list.querySelectorAll('.cat-item')].forEach((b) =>
      b.setAttribute('aria-pressed', b.dataset.id === String(item.id) ? 'true' : 'false'));
  }
  const detail = document.getElementById('catdetail');
  if (detail) {
    const wr = r.worldRecord;
    let params = '';
    if (wr) {
      const bits = [
        wr.radius?.v != null ? `${Number(wr.radius.v.toPrecision(3))} R⊕` : null,
        wr.mass?.v != null ? `${Number(wr.mass.v.toPrecision(3))} ${wr.massProv === 'Msini' ? 'M⊕ sin i' : 'M⊕'}` : null,
        wr.S?.v != null ? `S=${Number(wr.S.v.toPrecision(3))}` : null,
        wr.teq?.v != null ? `T<sub>eq</sub> ${wr.teq.v | 0} K` : null,
        wr.press?.v != null ? `${wr.press.v} bar` : null,
        wr.tidallyLocked ? 'locked' : null,
        wr.contested ? '⚠ contested' : null,
      ].filter(Boolean);
      params = `<br><span style="color:#c8b56f">${bits.join(' · ')}</span>`
        + (wr.assumptions?.length ? `<br><span style="color:#6f7c93">assumed: ${wr.assumptions.join(', ')}</span>` : '');
    }
    detail.classList.add('show');
    detail.innerHTML =
      `<b>${item.b || item.t}</b> · playable now<br>${item.d}${params}` +
      (item.p?.length ? `<br><span style="color:#7f8ca6">deeper physics needs ${item.p.map((p) => `<code>${p}</code>`).join(' ')}</span>` : '');
  }
  runGenerate(W.seed, mergeRunRule(r, {
    deepTime: r.earthLike ? W.rule?.deepTime : false,
    startAgeGa: r.earthLike ? W.rule?.startAgeGa : undefined,
  }));
}

async function stepCatalogueWorld(dir) {
  await fillCat();
  const next = Cat.adjacentCatalogueWorld(S.catalogueId ?? Cat.CATALOGUE_WORLDS[0]?.id, dir);
  if (next) await loadCatalogueItem(next);
}

async function renderCatalogue() {
  const list = document.getElementById('catlist');
  const meta = document.getElementById('catmeta');
  if (!list) return;
  const q = (S.catQuery || '').trim().toLowerCase();
  const cat = S.catFilter || 'all';
  const kind = S.catKind || 'TYPE';
  list.innerHTML = '';

  const match = (hay) => !q || hay.toLowerCase().includes(q);

  if (kind === 'TYPE') {
    RULESETS.forEach((r, i) => {
      if (!match(`${r.name} ${r.blurb} ${r.id}`)) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cat-item BODY';
      b.dataset.rule = r.id;
      b.setAttribute('aria-pressed', !S.catalogueId && W.rule?.id === r.id ? 'true' : 'false');
      b.innerHTML =
        `<span class="cid">${r.id}</span>` +
        `<span class="ct">${r.name}</span>` +
        `<span class="cm">Type · synthetic · play</span>`;
      b.title = r.blurb;
      b.onclick = () => setRuleset(i);
      list.appendChild(b);
    });
  }

  if (kind === 'BODY') {
    if (!Cat) await fillCat();
    const items = Cat.CATALOGUE.filter((x) => {
      if (x.k !== 'BODY') return false;
      if (cat !== 'all' && x.c !== cat) return false;
      if (!q) return true;
      const hay = `${x.t} ${x.d} ${x.b} ${x.s} ${(x.p || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
    const sort = S.catSort || 'id';
    if (sort !== 'id') {
      items.sort((a, b) => {
        const ra = a.k === 'BODY' ? Cat.recordForCatalogueItem(a) : null;
        const rb = b.k === 'BODY' ? Cat.recordForCatalogueItem(b) : null;
        if (sort === 'dist') return (ra?.distPc?.v ?? 9e9) - (rb?.distPc?.v ?? 9e9);
        if (sort === 'obs') return (rb?.observability?.v ?? 0) - (ra?.observability?.v ?? 0);
        if (sort === 'known') {
          const ka = ra ? (ra.gaps?.length || 0) + (ra.assumptions?.length || 0) : 99;
          const kb = rb ? (rb.gaps?.length || 0) + (rb.assumptions?.length || 0) : 99;
          return ka - kb;
        }
        return 0;
      });
    }
    for (const x of items) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `cat-item ${x.k}`;
      b.dataset.id = String(x.id);
      b.setAttribute('aria-pressed', S.catalogueId === x.id ? 'true' : 'false');
      const label = x.b || x.t;
      b.innerHTML =
        `<span class="cid">${x.id}</span>` +
        `<span class="ct">${label}</span>` +
        `<span class="cm">${Cat.CATALOGUE_KIND[x.k]} · ${x.e} · i${x.i}` +
        (x.k === 'BODY' ? ' · play' : '') + `</span>`;
      b.title = x.d;
      b.onclick = () => { void loadCatalogueItem(x); };
      list.appendChild(b);
    }
    if (meta) {
      const worlds = items.length;
      meta.textContent = `${list.children.length} shown · ${worlds} on this shelf · ${Cat.CATALOGUE_WORLDS.length} bodies`;
    }
    return;
  }

  if (meta) meta.textContent = `${list.children.length} planet types · Earth is the calibration world`;
}

function setupCatalogue() {
  const panel = document.getElementById('catpanel');
  const btn = document.getElementById('catbtn');
  const close = document.getElementById('catclose');
  const cats = document.getElementById('catcats');
  const kinds = document.getElementById('catkinds');
  const tools = document.getElementById('cattools');
  const q = document.getElementById('catq');
  if (!panel || !btn || !cats) return;
  const showBodyFilters = (on) => {
    cats.hidden = !on;
    if (tools) tools.hidden = !on;
  };

  let shelvesBuilt = false;
  const BODY_CAT_LABEL = {
    sol: 'Solar Sys', moons: 'Moons', temperate: 'Temperate', furnace: 'Furnace',
    giant: 'Giants', arch: 'Systems', dark: 'Dark',
  };

  const addCat = (id, label) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.dataset.id = id;
    b.setAttribute('aria-pressed', S.catFilter === id ? 'true' : 'false');
    b.onclick = () => {
      S.catFilter = id;
      [...cats.children].forEach((x) =>
        x.setAttribute('aria-pressed', x.dataset.id === id ? 'true' : 'false'));
      void renderCatalogue();
    };
    cats.appendChild(b);
  };

  const buildShelves = () => {
    if (shelvesBuilt || !Cat) return;
    shelvesBuilt = true;
    for (const c of Cat.CATALOGUE_CATS) {
      if (!Cat.CATALOGUE.some((x) => x.c === c.id && x.k === 'BODY')) continue;
      addCat(c.id, BODY_CAT_LABEL[c.id] || c.id);
    }
  };

  const openWithCatalogue = async () => {
    await fillCat();
    buildShelves();
    logCatalogueValidation();
    await renderCatalogue();
  };

  const toggle = async (open) => {
    const on = open ?? !panel.classList.contains('open');
    if (on === panel.classList.contains('open')) return;
    if (on) await openWithCatalogue();
    panel.classList.toggle('open', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (on) enterOverlayFocus(panel, '#catclose');
    else restoreOverlayFocus();
  };
  btn.onclick = () => { void toggle(); };
  document.getElementById('worldchip')?.addEventListener('click', () => { void toggle(true); });
  if (close) close.onclick = () => { void toggle(false); };
  document.getElementById('catprev')?.addEventListener('click', () => { void stepCatalogueWorld(-1); });
  document.getElementById('catnext')?.addEventListener('click', () => { void stepCatalogueWorld(1); });

  if (kinds) {
    kinds.innerHTML = '';
    for (const [id, label] of [['TYPE', 'Types'], ['BODY', 'Bodies']]) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.dataset.id = id;
      b.setAttribute('aria-pressed', S.catKind === id ? 'true' : 'false');
      b.onclick = async () => {
        S.catKind = id;
        [...kinds.children].forEach((x) =>
          x.setAttribute('aria-pressed', x.dataset.id === id ? 'true' : 'false'));
        showBodyFilters(id === 'BODY');
        if (id === 'BODY') {
          await fillCat();
          buildShelves();
          logCatalogueValidation();
        }
        await renderCatalogue();
      };
      kinds.appendChild(b);
    }
  }
  showBodyFilters(S.catKind === 'BODY');

  addCat('all', 'All shelves');
  q?.addEventListener('input', () => {
    S.catQuery = q.value;
    void renderCatalogue();
  });
  document.getElementById('catsort')?.addEventListener('change', (e) => {
    S.catSort = e.target.value;
    void renderCatalogue();
  });
  document.getElementById('catcsv')?.addEventListener('change', async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseWorldCsv(text);
    if (!rows.length) return;
    const seed = rows[0];
    const rec = makeWorldRecord(seed);
    const terra = RULESETS.find((r) => r.id === 'terra') || RULESETS[0];
    const rule = { ...terra, gases: { ...terra.gases }, atmo: terra.atmo?.slice?.(), sky: terra.sky?.slice?.() };
    applyRecordToRule(rule, rec);
    rule.name = rec.name;
    rule.blurb = `Player table · ${rows.length} row(s)`;
    runGenerate(W.seed, rule);
  });
  // TYPE shelf only — BODY shelves wait for first open / kind switch (K17).
  void renderCatalogue();
}

function togglePause() {
  setPaused(!S.paused);
}

function setPaused(on) {
  if (S.dayWatch && on) { setDayWatch(false); return; }
  S.paused = !!on;
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.sig;
  updateHUD();
}

function applyTimeRate(idOrDir) {
  const r = typeof idOrDir === 'number'
    ? cycleTimeRate(idOrDir)
    : setTimeRate(idOrDir);
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.sig;
  updateHUD();
  return r;
}

function applyClockFace(id) {
  setClockFace(W, id);
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.sig;
  updateHUD();
}

function applySeasonHold(id) {
  setClockFace(W, 'years');
  setSeasonHold(W, id);
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.sig;
  updateHUD();
}

function toggleFastForward() {
  W.fastForward = !W.fastForward;
  W.stopOnAnomaly = true;
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.sig;
  updateHUD();
}

function applyLifeSpeed(n) {
  setLifeSpeed(+n || 1);
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.sig;
  updateHUD();
}

function applyLivedSeasonHold(id) {
  const h = SEASON_HOLDS.find((x) => x.id === id);
  if (!h) return;
  scrubLivedSeason(W, (h.deg * Math.PI) / 180);
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.seasonDrag;
  updateHUD();
}

function applyLivedSeasonDeg(deg) {
  scrubLivedSeason(W, (deg * Math.PI) / 180);
  updateHUD();
}

function applyLivedSkyRate(rate) {
  setLivedSkyRate(W, +rate);
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.sig;
  updateHUD();
}

function applyLivedDayRate(rate) {
  setLivedDayRate(W, +rate);
  const ribbon = document.getElementById('timeribbon');
  if (ribbon) delete ribbon.dataset.sig;
  updateHUD();
}

function refreshLivedRibbonDom(ribbon, panel) {
  if (panel.clockFace !== 'now') return;
  const tickEl = ribbon.querySelector('[data-lived-tick]');
  if (tickEl && panel.livedLabel) tickEl.textContent = panel.livedLabel;
  if (ribbon.dataset.seasonDrag) return;
  const deg = Math.round(panel.seasonDeg ?? 0);
  const range = ribbon.querySelector('[data-lived-season-range]');
  const val = ribbon.querySelector('[data-lived-season-val]');
  if (range) range.value = deg;
  if (val) val.textContent = `${deg}°`;
  const holdId = panel.livedSeasonHoldId || 'mar';
  ribbon.querySelectorAll('[data-lived-season]').forEach((btn) => {
    btn.classList.toggle('on', btn.dataset.livedSeason === holdId);
  });
}

function applyEra(eraId) {
  if (!eraId || !availableEras(W.rule).some((e) => e.id === eraId)) return;
  const rule = ruleForEra(W.rule, eraId);
  if (!eraPatch(eraId)?.landscape) rule.landscape = S.landscape || rule.landscape || 'auto';
  runGenerate(W.seed, rule);
}

function bindRibbonTips(ribbon) {
  const map = [
    ['[data-era-select]', RIBBON_TIPS.era],
    ['[data-rate-select]', RIBBON_TIPS.rate],
    ['[data-time-pause]', RIBBON_TIPS.pause],
    ['[data-time-ff]', RIBBON_TIPS.ff],
    ['.rib-mode', RIBBON_TIPS.mode],
    ['.rib-track', RIBBON_TIPS.track],
    ['.rib-faces', RIBBON_TIPS.face],
    ['.rib-hold', RIBBON_TIPS.hold],
    ['.rib-life', RIBBON_TIPS.life],
  ];
  for (const [sel, tip] of map) {
    const el = ribbon.querySelector(sel);
    if (el && tip) bindTip(el, tip.title, tip.body);
  }
  ribbon.querySelectorAll('[data-rate-step]').forEach((el) => {
    const tip = el.dataset.rateStep === '-1' ? RIBBON_TIPS.slower : RIBBON_TIPS.faster;
    bindTip(el, tip.title, tip.body);
  });
}

function bindTimeRibbon() {
  const ribbon = document.getElementById('timeribbon');
  if (!ribbon || ribbon.dataset.bound) return;
  ribbon.dataset.bound = '1';
  ribbon.addEventListener('click', (e) => {
    if (e.target.closest('[data-time-pause]')) {
      e.preventDefault();
      togglePause();
      return;
    }
    if (e.target.closest('[data-time-ff]')) {
      e.preventDefault();
      toggleFastForward();
      return;
    }
    const face = e.target.closest('[data-clock-face]');
    if (face) {
      e.preventDefault();
      applyClockFace(face.dataset.clockFace);
      return;
    }
    const hold = e.target.closest('[data-season-hold]');
    if (hold) {
      e.preventDefault();
      applySeasonHold(hold.dataset.seasonHold);
      return;
    }
    const life = e.target.closest('[data-life-speed]');
    if (life) {
      e.preventDefault();
      applyLifeSpeed(life.dataset.lifeSpeed);
      return;
    }
    const livedSeason = e.target.closest('[data-lived-season]');
    if (livedSeason) {
      e.preventDefault();
      applyLivedSeasonHold(livedSeason.dataset.livedSeason);
      return;
    }
    const skyRate = e.target.closest('[data-lived-sky-rate]');
    if (skyRate) {
      e.preventDefault();
      applyLivedSkyRate(skyRate.dataset.livedSkyRate);
      return;
    }
    const dayRate = e.target.closest('[data-lived-day-rate]');
    if (dayRate) {
      e.preventDefault();
      applyLivedDayRate(dayRate.dataset.livedDayRate);
      return;
    }
    const step = e.target.closest('[data-rate-step]');
    if (step) {
      e.preventDefault();
      applyTimeRate(+step.dataset.rateStep);
    }
  });
  ribbon.addEventListener('change', (e) => {
    const eraSel = e.target.closest('[data-era-select]');
    if (eraSel) {
      applyEra(eraSel.value);
      return;
    }
    const rateSel = e.target.closest('[data-rate-select]');
    if (rateSel) applyTimeRate(rateSel.value);
  });
  ribbon.addEventListener('input', (e) => {
    const range = e.target.closest('[data-lived-season-range]');
    if (!range) return;
    ribbon.dataset.seasonDrag = '1';
    const deg = +range.value || 0;
    const val = ribbon.querySelector('[data-lived-season-val]');
    if (val) val.textContent = `${deg}°`;
    applyLivedSeasonDeg(deg);
  });
  ribbon.addEventListener('change', (e) => {
    const range = e.target.closest('[data-lived-season-range]');
    if (!range) return;
    delete ribbon.dataset.seasonDrag;
    applyLivedSeasonDeg(+range.value || 0);
  });
  ribbon.addEventListener('pointerup', (e) => {
    if (e.target.closest('[data-lived-season-range]')) delete ribbon.dataset.seasonDrag;
  });
}

/** The most populous living body, described from its own genome, plus the sense this
 *  world actually delivers. Nothing here is a lookup — every word is expressed. */

// Frame-loop element handles — looked up once, not 60× a second.
let _lvEl = null, _rungEl = null;

function update(t) {
  const dt = Math.min(0.05, (t - lastT) / 1000 || 0);
  lastT = t;
  S._t = t;
  if (S.paused) {
    S.sunAng += dt * (S.dayWatch ? 0.42 : 0.055);
  }
  presentAdvance(dt * (S.dayWatch ? 2.2 : 1));
  if (isLivedClock(W)) livedTick(W, dt * (S.dayWatch ? 1.6 : 1));
  stepFlow(dt * (S.dayWatch ? 1.8 : 1));
  presentAgents();
  uploadEntities();
  const hunting = S.localSeek === 'life' && S.localPin < 0 && !S.follow;
  const glance = hunting ? huntGlance() : -1;
  const missileCell = followFlightCell();
  const face = S.follow?.cell >= 0 ? S.follow.cell
    : glance >= 0 ? glance
    : missileCell >= 0 ? missileCell
    : ((S.faceUntil > (S._t || 0)) ? S.faceCell : -1);
  if (face >= 0) faceToward(face, dt);

  // Origin ceremony — formation sketch owns the frame until the sphere is born.
  if (S.originSketch) {
    const prevPh = S.originSketch.phase;
    S.originSketch.tick(dt);
    if (S.originSketch.phase !== prevPh && S.originSketch.caption) {
      const c = S.originSketch.caption;
      showMoment(c.title, c.body, 'Esc skips · formation writes the sphere');
    }
    // Slow tumble so the cloud reads in 3D.
    qAxis(tmpQ, 0, 1, 0, 0.22 * dt);
    qmul(S.q, S.q, tmpQ);
    qnorm(S.q);
    if (S.originSketch.done) finishOriginCeremony();
    S._fa++;
    return;
  }

  // Cinematic follow: ease camera in toward the warhead.
  if (missileCell >= 0) {
    const want = clamp(Math.min(S.camDist, 2.15), 1.55, 2.4);
    S.camDist += (want - S.camDist) * Math.min(1, dt * 1.4);
    requestFace(missileCell, 900);
  }

  // Spin inertia — planet resists. Item 11.
  if (!grabbing && !dragging && !panning && !S.toolDrag) {
    const av = S.angVel;
    if (Math.abs(av[0]) + Math.abs(av[1]) > 1e-4) {
      qAxis(tmpQ, 0, 1, 0, av[1] * dt * 2.2); qmul(S.q, tmpQ, S.q);
      qAxis(tmpQ, 1, 0, 0, av[0] * dt * 2.2); qmul(S.q, tmpQ, S.q);
      qnorm(S.q);
      av[0] *= Math.pow(0.15, dt); // damping
      av[1] *= Math.pow(0.15, dt);
    } else if (!S.paused && !grabbing && face < 0) {
      qAxis(tmpQ, 0, 1, 0, S.spin * dt);
      qmul(S.q, S.q, tmpQ);
      qnorm(S.q);
    }
  } else if (!S.paused && !grabbing && !dragging && !panning && face < 0) {
    qAxis(tmpQ, 0, 1, 0, S.spin * dt);
    qmul(S.q, S.q, tmpQ);
    qnorm(S.q);
  }

  // Day length drives visual spin
  S.spin = 0.035 / Math.max(0.2, W.rotationPeriod || 1);
  S.activeTool = activeTool;

  if (!S.paused) {
    if (W.fastForward) {
      const halt = shouldHaltFF(W);
      if (halt) {
        W.fastForward = false;
        showMoment('Anomaly', halt.label, formatAge(W.ageYr));
      }
    }
    simAcc += dt * (W.fastForward ? 4 : 1);
    let simRan = false;
    // Decoupled tick; skip if previous tick was heavy (budget ~8ms soft)
    while (simAcc > 0.09) {
      simAcc -= 0.09;
      const t0 = performance.now();
      simTick(); // agentsTick + fireTick run inside it now — see world.js
      simRan = true;
      uploadEntities();
      if (W._buildsDirty) { needGeom(); W._buildsDirty = false; }
      else if (W.year % 4000 < 200) needGeom(); // occasional elev rebuild for erosion/sculpt
      const elapsed = performance.now() - t0;
      W._msSim = elapsed;
      if (elapsed > 12) {
        noteDroppedTicks(W, Math.max(1, (simAcc / 0.09) | 0));
        simAcc = 0;
        break;
      } // never block frames — but record the miss
      S.simAlpha = 0;
    }
    if (simRan) refreshColours(1);
    S.simAlpha = Math.min(1, S.simAlpha + dt * 11);
    if (!simRan && S.simAlpha < 0.99) refreshColours(S.simAlpha);
  }

  if (geomDirty) { rebuildGeometry(); refreshColours(1); geomDirty = false; }

  S.camDist = clamp(S.camDist, camDistMin(W), CAM_DIST_MAX);
  const alt = xrSession
    ? (camWorld ? (Math.hypot(camWorld[0] - S.posXR[0], camWorld[1] - S.posXR[1], camWorld[2] - S.posXR[2]) / S.scaleXR - 1) : 1.2)
    : (S.camDist - 1);
  S.detail = clamp(1 - (alt - 0.08) / 1.2, 0, 1);
  /* Beings at every scale, not only from the ground.
     This used to read "Hide sprites at orbit — Earth biomes carry the look":
     `entFade` was forced to 0 above alt 0.85 on any Earth-like world and
     multiplied by 0.55 twice below it, so the animal layer was invisible from
     orbit and at 30% opacity in the regional view. That is a defensible look for
     a calibration render and the wrong one for a planet whose point is that it
     is alive. There is a floor now, and sprites grow with distance
     (`S.entGain`) so a being stays a few pixels across instead of shrinking to
     nothing — the layer thins with altitude rather than vanishing. */
  S.entFade = clamp(1 - (alt - 0.38) / 1.6, 0.34, 1);
  if (W.rule?.earthLike) S.entFade *= 0.82;
  if (alt > 0.9) S.entFade *= 0.72;
  else if (alt > 0.5) S.entFade *= 0.86;
  // Roughly constant apparent size out to orbit, then held so it cannot bloat.
  S.entGain = clamp(1 + Math.min(alt, 1.6) * 1.35, 1, 3.2);
  S.tier = alt > 8 ? 'Dot' : alt > 1.1 ? 'Orbital' : alt > 0.45 ? 'Regional' : alt > 0.16 ? 'Local' : 'Surface';
  brushForTier(S.tier === 'Dot' ? 'Orbital' : S.tier, S.camDist);
  const rungEl = _rungEl || (_rungEl = document.getElementById('scalerung'));
  if (rungEl) {
    const rung = scaleRung(S.camDist, W.noSurface);
    const down = W.noSurface && S.camDist < 1.08 ? formatDescent(S.camDist, W) : '';
    rungEl.textContent = down ? `${rung} · ${down}` : rung;
  }

  // Eye adaptation across scale / terminator (next hdr item)
  const nightish = Math.max(0, Math.min(1, (alt < 0.2 ? 0.15 : 0) + (W.gases?.dust || 0) * 0.3));
  const baseExpo = clamp(0.85 + Math.log2(Math.max(0.05, W.solar || 1)) * 0.12, 0.55, 1.85);
  // Nuclear blast: a brief exposure kick — never bleach the whole frame.
  const blastPunch = Math.min(0.55, (W._blastPunch || 0) + (W._blastFlash || 0) * 0.18);
  S.exposureTarget = baseExpo * (1.15 - nightish * 0.25) * (S.ceremonyUntil > performance.now() ? 1.08 : 1)
    * (1 + blastPunch * 0.28);
  S.exposure = (S.exposure ?? S.exposureTarget) + (S.exposureTarget - (S.exposure ?? S.exposureTarget)) * Math.min(1, dt * (blastPunch > 0.15 ? 3.2 : 1.8));

  audioUpdate(S._localFocus);

  if (!xrSession) {
    const lv = _lvEl || (_lvEl = document.getElementById('localview'));
    if (lv) {
      const hoverKey = S.localLegendLock || S.localHoverKey;
      // A laid-out box already proves the panel is displayed; the old
      // getComputedStyle() forced a style recalc on every frame.
      const lvOn = lv.offsetWidth > 8 && lv.offsetHeight > 8;
      const patch = lvOn ? drawLocalView(lv, S.inspect, {
        radius: S.localRadius,
        pin: S.localPin,
        seek: S.localSeek,
        highlightGuild: S.highlightGuild,
        hoverKey,
        hoverCell: S.localHoverCell,
        simAlpha: S.simAlpha,
        followId: S.follow?.id,
        net: S.canvasMode,
        cueKind: (S.localCueUntil && performance.now() < S.localCueUntil) ? S.localCueKind : null,
      }) : null;
      const prevFocus = S._localFocus;
      updateLocalHighlight(patch, S.localGlobe);
      S._localFocus = patch?.focus ?? -1;
      S._localPatch = patch;
      if (S.follow) {
        let live = null;
        for (let i = 0; i < ENT.n; i++) {
          if (ENT.meta[i]?.id === S.follow.id && !ENT.meta[i].dead) { live = ENT.meta[i]; break; }
        }
        S.follow = live || followTarget();
        if (S.follow?.cell >= 0 && S.localPin !== S.follow.cell) S.localPin = S.follow.cell;
      }
      // First-person drone feed (§136): W.dark.droneFocus / _localFocusCell → pin.
      if (W.dark?.droneFocus >= 0 || (W.dark?._localFocusCell | 0) >= 0) {
        const dc = (W.dark._localFocusCell | 0) >= 0
          ? (W.dark._localFocusCell | 0)
          : (W.drones?.[W.dark.droneFocus | 0]?.cell ?? -1);
        if (dc >= 0 && S.localPin !== dc) S.localPin = dc;
      }
      // Cinematic camera follow missile (§Q): W.dark.followFlight → pin to flight cell.
      if ((W.dark?.followFlight | 0) >= 0 && W.flight?.length) {
        const fi = W.dark.followFlight | 0;
        const f = W.flight[fi] || W.flight.find((x) => !x.dead);
        if (f && !f.dead) {
          const at = Math.min((f.path?.length || 1) - 1, Math.floor(f.at || 0));
          const fc = f.path?.[at] ?? f.to ?? f.from;
          if (fc >= 0) S.localPin = fc | 0;
        }
      }
      if (patch?.status) patch.status.behind = cellFacingZ(patch.focus) < 0.1;
      syncLocalChrome(patch, hoverKey);
      if ((S.localGlobe === 'wash' || S.localGlobe === 'both') && S._localFocus !== prevFocus) {
        refreshColours(1);
      }
      maybeDayMoment(S._localFocus);
      checkLessonProgress();
    }
  }

  tickThoughtVoice(dt);

  if (!S.pitchShot && !doorIsOpen()) maybeTeachWindow(t);

  S._fa++;
  if (t - S._ft > HUD_CADENCE_MS.hud) {
    S.fps = Math.round((S._fa * 1000) / (t - S._ft));
    S._fa = 0; S._ft = t;
    updateHUD();
  }
}

function desktopRay(clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  const ndcX = ((clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
  const ndcY = -(((clientY - rect.top) / Math.max(1, rect.height)) * 2 - 1);
  const eye = [0, 0.28, S.camDist];
  const asp = rect.width / Math.max(1, rect.height);
  return lookRay(ndcX, ndcY, eye, [0, 0, 0], [0, 1, 0], CAM_FOV, asp);
}

function desktopPick(clientX, clientY) {
  const { origin, dir } = desktopRay(clientX, clientY);
  return pickCell(origin, dir, planetPos(), 1, S.q);
}

let _previewRaf = 0;
function requestPreview() {
  if (_previewRaf) return;
  _previewRaf = requestAnimationFrame(() => {
    _previewRaf = 0;
    refreshColours(S.simAlpha ?? 1);
  });
}

function desktopTablePick(clientX, clientY) {
  if (!TABLE.enabled) return null;
  const { origin, dir } = desktopRay(clientX, clientY);
  return pickTableSlotRay(TABLE, origin, dir, [0, -1.15, 0], 0.38, 0.12);
}

function onToolResult(res) {
  if (!res) return;
  if (res.error) showErr(res.error);
  if (res.refused) showErr(res.note || 'Life refused');
  if (res.needCommit || res.needConfirm) {
    startCommitHold(res.cell ?? 0);
    return;
  }
  if (res.orbitFlash) S.orbitFlash = (S._t || performance.now()) + 5500;
  if (res.ok && (res.note || (res.orbitFlash && res.said))) {
    showMoment(res.note ? 'Orbit' : 'Lever', res.note || res.said, formatAge(W.ageYr));
  }
  if (res.ok) {
    playEvent(activeTool === 'meteor' ? 'impact' : (activeTool === 'seed' || activeTool === 'seedGuild') ? 'seed' : 'tool', 0.6);
    const geomTools = new Set(['raise', 'lower', 'flatten', 'smooth', 'sharpen', 'roughen', 'crust', 'meteor', 'buster', 'volcano', 'quake', 'plume', 'lip', 'river', 'ice']);
    if (geomTools.has(activeTool)) needGeom();
    if (res.cell != null && res.cell >= 0) markTouch(W, res.cell);
    refreshColours(1);
    showReceiptToast(res);
    finishVandalHook(res);
    if (_playtest && (res.tool === 'ignite' || res.tool === 'meteor' || activeTool === 'ignite' || activeTool === 'meteor')) {
      _playtest.noteAct(res.tool || activeTool);
    }
  }
  if (res.cell != null && activeTool === 'inspect') {
    inspectAt(res.cell, res);
  }
  if (res.sample) {
    S.lastSample = res.sample;
    setDockTab('lab');
    setSuiteDesk('lab', 'station');
    refreshLab();
  }
  updateHUD();
}

/** Pin a cell, face it, and open the Lab station readout. */
function inspectAt(cell, payload = null) {
  if (cell == null || cell < 0) return;
  const data = payload?.h != null ? payload : inspectCell(cell);
  S.inspect = data?.cell != null ? data : { ...inspectCell(cell), cell };
  lookAtCell(cell, { ms: 1800 });
  setTool('inspect');
  setDockTab('lab');
  setSuiteDesk('lab', 'station');
  if (S.localGlobe === 'wash' || S.localGlobe === 'both') refreshColours(1);
  updateHUD();
}

/** Swing the globe so `cell` faces the camera, and pin the mini-map there. */
function lookAtCell(cell, opts = {}) {
  if (cell == null || cell < 0) return;
  const c = cell | 0;
  S.localPin = c;
  S.localSeek = 'stay'; // hold the mini-map on this spot (don't hunt life)
  S.follow = null;
  if (W.dark) {
    W.dark.followFlight = -1;
    W.dark.droneFocus = -1;
    W.dark._localFocusCell = -1;
  }
  S.inspect = { ...(inspectCell(c) || {}), cell: c };
  S._localFocus = c;
  S.kbCursor = c;
  setLocalHover(c);
  resetCamPan();
  S.angVel[0] = 0;
  S.angVel[1] = 0;
  requestFace(c, opts.ms ?? 2400);
  if (opts.zoom !== false && S.camDist > 2.6) {
    S.camDist = Math.max(2.05, S.camDist * 0.82);
  }
  syncSeekSeg();
  if (S.localGlobe === 'wash' || S.localGlobe === 'both' || S.localGlobe === 'rim') {
    refreshColours(1);
  }
  if (_playtest && (S.localExpanded || opts.descend)) _playtest.noteDescend();
  updateHUD();
}

function planetCanvasFocused() {
  return typeof document !== 'undefined' && document.activeElement === canvas;
}

function announcePlanet(msg, opts = {}) {
  const text = String(msg || '');
  if (!text) return;
  // M18 — do not spam the live region on every WASD step.
  if (opts.throttleMs) {
    const now = performance.now();
    if (now - (S._planetLiveAt || 0) < opts.throttleMs && text === S.planetLive) return;
    if (now - (S._planetLiveAt || 0) < opts.throttleMs * 0.5 && opts.soft) return;
    S._planetLiveAt = now;
  }
  S.planetLive = text;
  const el = document.getElementById('planetLive');
  if (el) el.textContent = text;
}

function ensureKbCursor() {
  if (S.kbCursor >= 0) return S.kbCursor;
  const seed = S._localFocus >= 0 ? S._localFocus
    : (S.localPin >= 0 ? S.localPin : (S.localHoverCell >= 0 ? S.localHoverCell : 0));
  S.kbCursor = seed | 0;
  return S.kbCursor;
}

function setKbCursor(cell, opts = {}) {
  if (cell == null || cell < 0) return;
  S.kbCursor = cell | 0;
  S.localHoverCell = S.kbCursor;
  setLocalHover(S.kbCursor);
  S._localFocus = S.kbCursor;
  if (opts.announce) {
    const sea = (W.h?.[S.kbCursor] ?? 0) < (W.seaLevel ?? 0);
    announcePlanet(`Cursor ${sea ? 'ocean' : 'land'} cell ${S.kbCursor}`, { throttleMs: 900, soft: true });
  }
}

function moveKbCursor(dx, dy) {
  const base = ensureKbCursor();
  const next = stepFocus(base, dx, dy);
  setKbCursor(next, { announce: true });
  requestFace(next, 700);
  updateHUD();
}

function spinPlanet(yaw, pitch) {
  const step = 0.09;
  if (yaw) {
    qAxis(tmpQ, 0, 1, 0, yaw * step);
    qmul(S.q, tmpQ, S.q);
    qnorm(S.q);
  }
  if (pitch) {
    qAxis(tmpQ, 1, 0, 0, pitch * step);
    qmul(S.q, tmpQ, S.q);
    qnorm(S.q);
  }
  S.angVel[0] = 0;
  S.angVel[1] = 0;
  S.faceUntil = 0;
}

function zoomPlanet(dir) {
  const factor = 1 + Math.sign(dir) * (S.camDist > 8 ? 0.14 : 0.09);
  S.camDist = clamp(S.camDist * factor, camDistMin(W), CAM_DIST_MAX);
  const rung = document.getElementById('scalerung');
  if (rung) rung.textContent = scaleRung(S.camDist, W.noSurface);
}

function descendKeyboard() {
  const cell = ensureKbCursor();
  lookAtCell(cell, { ms: 1800, descend: true, zoom: true });
  S.localExpanded = true;
  if (S.localParked) unparkLocalMap();
  try { applyLocalLayout(); } catch { expected('ORR-EXPECTED-LAYOUT', 'local layout'); }
  const last = W.receipts?.[W.receipts.length - 1]?.tool;
  armLocalCue(cell, last);
  const t = W.meanTemp != null ? ` · meanT ${(W.meanTemp).toFixed(2)}` : '';
  const life = W.meanLife != null ? ` · life ${(W.meanLife * 100).toFixed(0)}%` : '';
  announcePlanet(`Descended to cell ${cell}${t}${life}`);
  showErr('Local map — Esc steps back');
}

/** Fire local-map cue pulse + sprite motion for a few seconds. */
function armLocalCue(cell, prefer = null, opts = {}) {
  import('./sim/localCue.js').then(({ localMotionCue }) => {
    const cue = localMotionCue(W, cell, 3, prefer);
    if (!cue) return;
    S.localCueKind = cue.kind;
    S.localCueUntil = performance.now() + 4500;
    if (opts.silent || !cue.line) return;
    const title = opts.title || 'Local';
    const sub = opts.sub || 'Motion on the map.';
    const show = () => {
      if (cue.kind === 'place' && opts.skipPlace) return;
      showMoment(title, cue.line, sub);
    };
    if (opts.delayMs) setTimeout(show, opts.delayMs);
    else show();
  }).catch(() => { expected('ORR-EXPECTED-LAZY', 'local cue'); });
}

/** M7 — apply the armed tool at the keyboard cursor. */
function applyToolAtKbCursor(opts = {}) {
  const cell = ensureKbCursor();
  const tool = TOOLS.find((t) => t.id === activeTool) || TOOLS[0];
  if (!tool) return;
  if (toolsUnlocked(W)[tool.id] === false) {
    showErr('Tool locked');
    announcePlanet(`${tool.name} locked`);
    return;
  }
  // Inspect / see → descend (Observe → Read). Shift forces descend for any tool.
  if (opts.forceDescend || tool.id === 'inspect' || tool.group === 'see') {
    descendKeyboard();
    return;
  }
  const extra = { ...(opts.extra || {}) };
  if (tool.id === 'solar' && extra.delta == null) extra.delta = 0.05;
  if (tool.id === 'buster' || tool.irreversible) {
    // Irreversible tools still need the hold/confirm path — arm a toast instead.
    showErr(`${tool.name}: click the globe to commit (hold where required)`);
    lookAtCell(cell, { ms: 900, zoom: false });
    announcePlanet(`${tool.name} armed at ${cell} — click to commit`);
    return;
  }
  onToolResult(useToolAt(cell, extra));
  announcePlanet(`${tool.name} at cell ${cell}`);
}

function showReceiptToast(res) {
  const el = document.getElementById('receipt');
  if (!el) return;
  const last = W.receipts?.[W.receipts.length - 1];
  const said = res.said || res.note;
  if (!last && !res.pay && !said) return;
  const title = said || last?.intent || last?.tool || activeTool;
  const sub = last?.expected && last.expected !== said ? last.expected : '';
  const notable = !!(last && ['ignite', 'meteor', 'nuke', 'raise', 'lower', 'co2', 'solar', 'buster'].includes(last.tool));
  const canUndo = !!(W._actUndo && notable);
  /* Fork twin is art-face affordance, not a verb for every brush stroke. On
     phone the toast already eats the lower third — offering fork on every
     receipt made accidental twins the default. Notable acts only, and a
     cooldown so it does not reappear every tap. */
  const phone = isPhone();
  const forkCooldownMs = phone ? 120000 : 45000;
  const now = performance.now();
  const cooled = (now - (showReceiptToast._lastForkOffer || 0)) >= forkCooldownMs;
  const offerFork = notable && cooled;
  if (offerFork) showReceiptToast._lastForkOffer = now;
  el.innerHTML = `<b>${title}</b>`
    + (sub ? `<br><small>${sub}</small>` : '')
    + (last?.cost ? ` · −${last.cost}` : '')
    + (res.settling ? `<br><small>settles: ${res.settling}</small>` : '')
    + ((canUndo || offerFork)
      ? `<br><span class="receipt-acts">`
        + (canUndo ? `<button type="button" id="receiptUndo" class="receipt-btn">What if I hadn’t</button> ` : '')
        + (offerFork ? `<button type="button" id="receiptFork" class="receipt-btn">Fork twin</button>` : '')
        + `</span>`
      : '');
  el.classList.add('show');
  el.querySelector('#receiptUndo')?.addEventListener('click', () => {
    try {
      const snap = W._actUndo;
      if (!snap) return;
      loadRunMeta(snap);
      W._actUndo = null;
      showMoment('Counterfactual', 'Before that act', 'The tape rewound one intervention.');
      needGeom();
      refreshColours(1);
      updateHUD();
    } catch (e) {
      showMoment('Couldn’t rewind', String(e.message || e), '');
    }
    el.classList.remove('show');
  });
  el.querySelector('#receiptFork')?.addEventListener('click', async () => {
    try {
      const { captureRun, forkRunObject, applyRun } = await import('./sim/run.js');
      const parent = captureRun(last?.tool || 'act');
      const child = forkRunObject(`what-if-${last?.tool || 'x'}`, parent);
      applyRun(child);
      needGeom();
      refreshColours(1);
      updateHUD();
      refreshLab();
      showMoment('Twin forked', child.label, `Seed ${child.seed} — same land, forked RNG from here.`);
    } catch (e) {
      showMoment('Fork failed', String(e.message || e), '');
    }
    el.classList.remove('show');
  });
  clearTimeout(showReceiptToast._t);
  const holdMs = phone ? (offerFork || canUndo ? 5500 : 3200) : 9000;
  showReceiptToast._t = setTimeout(() => el.classList.remove('show'), holdMs);
}

function startCommitHold(cell) {
  const ring = document.getElementById('commitring');
  S.commitHold = { cell, t0: performance.now(), ms: 2800 };
  if (ring) {
    ring.classList.add('show');
    ring.querySelector('.cr-label').textContent = 'Hold to commit — irreversible';
  }
  const tick = () => {
    if (!S.commitHold) return;
    const p = (performance.now() - S.commitHold.t0) / S.commitHold.ms;
    if (ring) ring.style.setProperty('--p', clamp(p, 0, 1));
    if (p >= 1) {
      const c = S.commitHold.cell;
      cancelCommitHold(true);
      onToolResult(useToolAt(c, { commit: true, confirm: true }));
      playEvent('buster', 1);
      needGeom();
      return;
    }
    S.commitHold.raf = requestAnimationFrame(tick);
  };
  S.commitHold.raf = requestAnimationFrame(tick);
}

function cancelCommitHold(done) {
  if (S.commitHold?.raf) cancelAnimationFrame(S.commitHold.raf);
  S.commitHold = null;
  document.getElementById('commitring')?.classList.remove('show');
}

function setupTips() {
  // Play / World / chrome controls by id
  const ids = [
    'guildsel', 'brushmask', 'brushsnap', 'brushhard',
    'godundo', 'godredo', 'godcull', 'godwatch', 'godbookmark',
    'scenariosel', 'scenariostart', 'lessonchip', 'tourbtn', 'landpickcontinue',
    'genesisname', 'genesisseed', 'genesispreset', 'genesisland',
    'genesisrand', 'genesisgo', 'dailyseed', 'godshelf', 'godshare',
    'budget', 'autopilot',
    'pause', 'newseed', 'catbtn', 'catprev', 'catnext', 'worldchip',
    'docktoggle', 'dockfab', 'dockshrink', 'localfab', 'localpark', 'vrbtn', 'tourbtn',
    'opacity', 'grid', 'xray', 'xrayAmt', 'viewClear', 'viewGhost', 'viewOrbitGuides', 'viewOpenKey', 'viewThought', 'viewThoughtMind',
    'lookPhoto', 'lookDiagram', 'cloudFree', 'canvasmode', 'rerolland', 'landshape', 'landpickbtn',
    'layeradd', 'layerdup', 'layerdel', 'layerup', 'layerdown', 'layerflatten',
    'layeropacity', 'layerblend', 'layerpaint', 'layerclipland', 'layerclearmask',
    'genesisplates', 'genesiswater', 'genesislandfrac', 'genesissolvent', 'genesischirality', 'genesisorigindiff', 'origindiff',
    'simN', 'globeSubd', 'orreryTable', 'export',
    'labRefresh', 'labPaper', 'labSave', 'labFinale', 'labPng', 'labDual', 'labDiag',
    'catsort', 'catcsv',
    'climDay', 'climTilt', 'climSeason', 'climMoonOn', 'climMoonMass', 'climMoonDist',
    'stormGenesis', 'stormStrict', 'stormSize', 'stormVigor',
    'rockHeat', 'rockMag',
    'localkeybtn',
    'localSeek',
  ];
  for (const id of ids) {
    const el = document.getElementById(id);
    const tip = tipForId(id);
    if (el && tip) bindTip(el, tip.title, tip.body);
  }
  // Labels that wrap controls
  document.querySelectorAll('#pane-god label[for], #pane-tools label[for], #pane-view label[for], #pane-climate label[for], #pane-rock label[for], #pane-sandbox label[for]').forEach((lab) => {
    const tip = tipForId(lab.htmlFor);
    if (tip) bindTip(lab, tip.title, tip.body);
  });
  // Dock tabs — icon stacked above label (Sky metaphor for all)
  const tabTips = {
    tools: ['Tools', 'The verbs — raise land, seed life, strike. Select one, then right-click the planet (phone: tap). Looking and cores live in Lab.'],
    god: ['Play', 'This run: undo, watch, optional goals, and a named world. Brush and seed settings live with the tools they change. Time lives on the ribbon.'],
    climate: ['Sky', 'Atmosphere desks: circulation & tides, storm track, coast flood risk, spin A/B compare.'],
    rock: ['Rock', 'Core, plates, boundaries, fire, crust age — interiors drive dynamos and lids.'],
    view: ['View', 'Look, field overlays, x-ray cut, and orbit guides. Overlays live here — Sky/Rock only jump to one.'],
    lab: ['Lab', 'Station first: inspect a cell, take a core. Then the instruments — redox tower, Keeling, diversity, transit.'],
    sandbox: ['World', 'Energy mode, Gaia, resolution, and the archive for this run.'],
  };
  const tabLabels = {
    tools: 'Tools', god: 'Play', climate: 'Sky', rock: 'Rock', view: 'View', lab: 'Lab', sandbox: 'World',
  };
  document.querySelectorAll('.dock-tabs button').forEach((b) => {
    const tab = b.dataset.tab;
    const t = tabTips[tab];
    if (t) bindTip(b, t[0], t[1]);
    const icon = DOCK_TAB_ICONS[tab];
    if (icon && !b.querySelector('.ico')) {
      decorateButton(b, icon, tabLabels[tab] || b.textContent.trim());
    }
  });

  // Topbar chrome — icons on phone (labels hide ≤900px).
  for (const [id, icon, label] of [
    ['tourbtn', 'chronicle', 'Tour'],
    ['catbtn', 'planet', 'Worlds'],
    ['newseed', 'genesisrand', 'Reseed'],
    ['docktoggle', 'menu', 'Menu'],
    ['dockfab', 'menu', 'Menu'],
    ['localpark', 'eye', 'Tuck'],
  ]) {
    const el = document.getElementById(id);
    if (el && !el.querySelector('.ico')) decorateButton(el, icon, label);
  }
  {
    const lf = document.getElementById('localfab');
    if (lf) {
      lf.innerHTML = `${iconSVG('localmap')}<span class="localfab-peek" aria-hidden="true"></span><span class="btn-label">Map</span>`;
    }
  }

  // Suite desk tabs (Play / View / Lab / World / Tools)
  const suiteMeta = {
    tools: {
      land: ['raise', 'Land'],
      life: ['seedGuild', 'Life'],
      strike: ['meteor', 'Strike'],
      evil: ['plague', 'Evil'],
    },
    god: {
      aim: ['godwatch', 'Run'],
      challenge: ['challenge', 'Challenge'],
      genesis: ['genesis', 'Genesis'],
    },
    view: {
      look: ['appear', 'Look'],
      layers: ['survey', 'Layers'],
      slice: ['slice', 'Slice'],
      guides: ['orbitguides', 'Guides'],
    },
    lab: {
      station: ['station', 'Station'],
      all: ['tablab', 'All'],
      tower: ['o2', 'Tower'],
      curves: ['curves', 'Curves'],
      survey: ['survey', 'Survey'],
      notes: ['notes', 'Notes'],
    },
    sandbox: {
      modes: ['modes', 'Modes'],
      archive: ['archive', 'Archive'],
    },
  };
  document.querySelectorAll('.suite-desk-tab').forEach((b) => {
    const suite = b.dataset.suite;
    const desk = b.dataset.desk;
    if (desk === 'evil' && !darkEnabled()) {
      b.hidden = true;
      b.setAttribute('aria-hidden', 'true');
      return;
    }
    const meta = suiteMeta[suite]?.[desk];
    if (meta) decorateButton(b, meta[0], meta[1]);
    const tip = SUITE_TIPS[suite]?.[desk];
    if (tip) bindTip(b, tip.title, tip.body);
    b.addEventListener('click', () => setSuiteDesk(suite, desk));
  });
  if (!darkEnabled()) {
    const evilPanel = document.querySelector('.suite-desk[data-desk-panel="evil"]');
    if (evilPanel) evilPanel.hidden = true;
  }

  // Section headings with icons
  document.querySelectorAll('[data-sec-icon]').forEach((el) => {
    const id = el.dataset.secIcon;
    const label = el.textContent.trim();
    el.innerHTML = `${iconSVG(id)}<span>${label}</span>`;
  });

  // Play / World action icons
  const iconIds = [
    'godundo', 'godredo', 'godcull', 'godwatch', 'godbookmark',
    'scenariostart', 'genesisrand', 'genesisgo', 'dailyseed',
    'godshelf', 'godshare', 'budget', 'autopilot',
  ];
  for (const id of iconIds) {
    const el = document.getElementById(id);
    if (!el || el.querySelector('.ico')) continue;
    const label = el.textContent.trim();
    decorateButton(el, id, label);
  }
  const extraIcons = [
    ['labRefresh', 'refresh', 'Refresh'],
    ['labPaper', 'paper', 'Paper'],
    ['labSave', 'save', 'Save'],
    ['labFinale', 'finale', 'Finale'],
    ['labPng', 'png', 'PNG'],
    ['labDual', 'dual', 'Dual'],
    ['labDiag', 'keys', 'Diagnostics'],
    ['orreryTable', 'table', 'Table'],
    ['export', 'chronicle', 'Chronicle'],
  ];
  for (const [id, icon, label] of extraIcons) {
    const el = document.getElementById(id);
    if (!el || el.querySelector('.ico')) continue;
    decorateButton(el, icon, label);
  }

  document.querySelectorAll('#catkinds button').forEach((b) => {
    if (b.dataset.id === 'TYPE') {
      bindTip(b, 'Types', 'Synthetic Type worlds — Earth, Vermis, Selene, Ares, Venus, Titan, Europa, Daisy. Calibration worlds, not archive rows.');
    } else if (b.dataset.id === 'BODY') {
      bindTip(b, 'Bodies', 'Real planets and moons from the catalogue. Physics they still need is listed on the detail card.');
    }
  });
  const overlayJumps = [
    ['rockOverlayPlates', 'plates'],
    ['rockOverlayBounds', 'bounds'],
    ['rockOverlayCrust', 'crust'],
    ['stormOverlay', 'storm'],
  ];
  for (const [id, oid] of overlayJumps) {
    const el = document.getElementById(id);
    const o = overlayById(oid);
    if (el && o?.tip) bindTip(el, o.label, o.tip);
  }
  bindOverlayTips();
}

/** Switch a suite sub-desk (Tools / Play / View / Lab / World). */
function setSuiteDesk(suite, desk) {
  document.querySelectorAll(`.suite-desk-tab[data-suite="${suite}"]`).forEach((b) => {
    b.setAttribute('aria-pressed', b.dataset.desk === desk ? 'true' : 'false');
  });
  if (suite === 'lab') {
    S.labDesk = desk;
    applyLabFilter();
    return;
  }
  document.querySelectorAll(`.suite-desk[data-suite-panel="${suite}"]`).forEach((p) => {
    const on = p.dataset.deskPanel === desk;
    p.classList.toggle('on', on);
    p.hidden = !on;
  });
  if (suite === 'sandbox' && desk === 'modes') refreshWorldModeStrip();
}

function applyLabFilter() {
  const desk = S.labDesk || 'station';
  const stats = document.getElementById('stats');
  if (stats) stats.hidden = desk !== 'station';
  document.querySelectorAll('#labstats .lab-card').forEach((card) => {
    const cat = card.dataset.labCat || 'notes';
    if (desk === 'station') card.hidden = cat !== 'sample';
    else card.hidden = desk !== 'all' && cat !== desk;
  });
}

function bindOverlayTips() {
  document.querySelectorAll('[data-overlay]').forEach((b) => {
    const o = overlayById(b.dataset.overlay);
    if (o?.tip) bindTip(b, o.label, o.tip);
  });
}

function applyOverlayChoice(mode) {
  setOverlayMode(mode || 'none');
  refreshColours(1);
  syncViewOverlayButtons(mode || 'none');
}

function syncViewOverlayButtons(mode) {
  document.querySelectorAll('#viewOverlays button, #climOverlays button, #coastOverlays button, #rockAgeOverlays button').forEach((b) => {
    b.setAttribute('aria-pressed', b.dataset.overlay === mode ? 'true' : 'false');
  });
  const hint = document.getElementById('viewOverlayHint');
  if (hint) {
    const o = overlayById(mode);
    hint.textContent = o ? `${o.label}${mode === 'none' ? '' : ' painted on the globe'}.` : '';
  }
}

function refreshWorldModeStrip() {
  const strip = document.getElementById('worldModeStrip');
  if (!strip) return;
  const I = W.interior;
  const mode = W.scarcityMode || (W.budgetMode ? SCARCITY.budgeted : SCARCITY.free);
  const eMeta = scarcityMeta(mode);
  const gMeta = gaiaModeMeta(W);
  strip.innerHTML = `
    <div class="clim-chip" title="${eMeta.blurb}"><span>Energy</span><b>${eMeta.label}</b></div>
    <div class="clim-chip" title="${gMeta.blurb}"><span>Gaia</span><b>${gMeta.label}</b></div>
    <div class="clim-chip" title="Simulation grid size — climate and life run here"><span>Sim N</span><b>${N}</b></div>
    <div class="clim-chip" title="Tectonic lid: mobile plates vs stagnant"><span>Lid</span><b>${I?.lidMode || '—'}</b></div>
    <div class="clim-chip" title="Magnetosphere strength — aurora and atmosphere loss"><span>Field</span><b>${(W.magnetosphere ?? 0).toFixed(2)}</b></div>
    <div class="clim-chip" title="Mantle heat flow — volcanoes and plate vigor"><span>Heat</span><b>${I ? I.heatFlow.toFixed(2) : '—'}</b></div>
  `;
  const energyBlurb = document.getElementById('energyBlurb');
  const gaiaBlurb = document.getElementById('gaiaBlurb');
  if (energyBlurb) energyBlurb.textContent = eMeta.blurb;
  if (gaiaBlurb) gaiaBlurb.textContent = gMeta.blurb;
  const budgetBtn = document.getElementById('budget');
  if (budgetBtn) {
    budgetBtn.setAttribute('aria-pressed', mode !== SCARCITY.free ? 'true' : 'false');
    decorateButton(budgetBtn, 'budget', eMeta.label);
  }
  const gaiaBtn = document.getElementById('autopilot');
  if (gaiaBtn) {
    gaiaBtn.setAttribute('aria-pressed', W.autopilot ? 'true' : 'false');
    decorateButton(gaiaBtn, 'autopilot', gMeta.label);
  }
}

let _tipTimer = 0;
function bindTip(el, title, body, meta = '') {
  if (!el || el.dataset.tipBound) return;
  el.dataset.tipBound = '1';
  el.removeAttribute('title'); // avoid native double-tip
  el.addEventListener('pointerenter', (e) => {
    clearTimeout(_tipTimer);
    _tipTimer = setTimeout(() => showTip(e.currentTarget, title, body, meta), 280);
  });
  el.addEventListener('pointerleave', () => {
    clearTimeout(_tipTimer);
    hideTip();
  });
  el.addEventListener('pointerdown', () => {
    clearTimeout(_tipTimer);
    hideTip();
  });
}

function showTip(anchor, title, body, meta = '') {
  const tip = document.getElementById('tip');
  if (!tip || !anchor) return;
  tip.querySelector('.tip-title').textContent = title;
  tip.querySelector('.tip-body').textContent = body;
  const metaEl = tip.querySelector('.tip-meta');
  if (metaEl) {
    metaEl.textContent = meta || '';
    metaEl.hidden = !meta;
  }
  tip.hidden = false;
  tip.classList.add('show');
  const r = anchor.getBoundingClientRect();
  const tw = tip.offsetWidth || 260;
  const th = tip.offsetHeight || 80;
  let left = r.right + 10;
  let top = r.top;
  if (left + tw > innerWidth - 8) left = Math.max(8, r.left - tw - 10);
  if (top + th > innerHeight - 8) top = Math.max(8, innerHeight - th - 8);
  if (top < 8) top = 8;
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

function hideTip() {
  const tip = document.getElementById('tip');
  if (!tip) return;
  tip.classList.remove('show');
  tip.hidden = true;
}

function doorIsOpen() {
  return document.getElementById('door')?.classList.contains('open');
}

function closeDoor() {
  document.getElementById('door')?.classList.remove('open');
}

async function catalogueBodyNamed(name) {
  await fillCat();
  return Cat.CATALOGUE_WORLDS.find((x) => x.b === name) || null;
}

function paintLessonChip() {
  const el = document.getElementById('lessonchip');
  if (!el) return;
  const p = loadLessonProgress();
  const next = nextIncompleteLesson(p);
  if (!p.seenDoor && !p.current && !isDemoMode()) {
    el.hidden = true;
    return;
  }
  el.hidden = false;
  el.textContent = lessonChipLabel(p);
  el.title = next ? next.body : 'The Solar System is the tutorial. Play → Challenge has the rest.';
}

function onTourButton() {
  // Always open the door — never trap on "re-show current lesson" while the
  // matching world is loaded (that made Tour feel dead mid-crisis).
  setPaused(true);
  hideDockForOverlay();
  openDoor();
}

function continueTour() {
  const p = loadLessonProgress();
  const next = lessonById(p.current) || nextIncompleteLesson(p);
  if (next) {
    void startLesson(next.id);
    return;
  }
  closeDoor();
  setDockTab('god');
  setSuiteDesk('god', 'challenge');
  showMoment('Tour', 'The Solar System is the tutorial', 'Challenges live in Play.');
}

function skipLessonAhead() {
  const p = loadLessonProgress();
  const cur = lessonById(p.current) || nextIncompleteLesson(p);
  if (!cur || lessonDone(cur.id, p)) {
    continueTour();
    return;
  }
  const nextP = completeLesson(cur.id, p);
  S.lessonId = nextP.current;
  paintLessonChip();
  paintCampaignTrack();
  const nxt = lessonById(nextP.current) || nextIncompleteLesson(nextP);
  if (nxt) void startLesson(nxt.id);
  else {
    closeDoor();
    showMoment('Tour', 'Tour complete', 'Challenges live in Play → Challenge.');
  }
}

function resetTour() {
  resetLessonProgress();
  S.lessonId = null;
  S._lessonFailNoted = false;
  paintLessonChip();
  paintCampaignTrack();
  openDoor();
  showMoment('Tour', 'Start over', 'Pick a way in — Continue begins at Hold Earth.');
}

function paintCampaignTrack() {
  const track = document.getElementById('campaigntrack');
  if (!track) return;
  const p = loadLessonProgress();
  track.innerHTML = '';
  const addRow = (id, title, meta, on, done, start) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'camp-row' + (on ? ' on' : '') + (done ? ' done' : '');
    b.innerHTML = `<span class="ck">${done ? '✓' : '○'}</span><span class="ct">${title}</span><span class="cm">${meta}</span>`;
    b.addEventListener('click', start);
    track.appendChild(b);
  };
  for (const lesson of LESSONS) {
    addRow(
      lesson.id,
      lesson.title,
      lesson.kicker,
      p.current === lesson.id,
      !!p.done[lesson.id],
      () => { void startLesson(lesson.id); },
    );
  }
  for (const id of CAMPAIGN) {
    if (LESSONS.some((l) => l.scenario === id)) continue;
    const s = SCENARIOS.find((x) => x.id === id);
    if (!s) continue;
    addRow(id, s.title, 'Challenge', false, false, () => beginScenario(id, { moment: true }));
  }
  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'camp-row camp-reset';
  reset.innerHTML = '<span class="ck">↺</span><span class="ct">Reset tour</span><span class="cm">Start over</span>';
  reset.title = 'Clear lesson progress and reopen the front door';
  reset.addEventListener('click', resetTour);
  track.appendChild(reset);
}

function finishLesson(id, foundKey = null) {
  const lesson = lessonById(id);
  if (!lesson || lessonDone(id)) return;
  const nextP = completeLesson(id);
  S.lessonId = nextP.current;
  const nxt = lessonById(nextP.current) || nextIncompleteLesson(nextP);
  const found = foundKey
    ? `Found ${foundKey}.`
    : (lesson.winHint || '');
  const sub = nxt
    ? `${found ? `${found} ` : ''}Next: ${nxt.title}`
    : (found || 'The Solar System was the tutorial.');
  showMoment('Lesson', lesson.title, sub);
  playEvent('quiet', 0.7);
  paintLessonChip();
  paintCampaignTrack();
}

function beginScenario(id, opts = {}) {
  const s = SCENARIOS.find((x) => x.id === id);
  if (!s) return null;
  const baseRule = RULESETS.find((r) => r.id === s.ruleId) || W.rule;
  const rule = s.epochId
    ? ruleForEra(baseRule, s.epochId)
    : mergeRunRule(baseRule, {
      deepTime: !!s.deepTime,
      startAgeGa: s.startAgeGa,
      landscape: s.landscape,
    });
  // A scenario that pins its own tick length needs that carried through on both
  // paths, or the clock it was designed around is not the clock it gets.
  if (s.fixedDtYr) rule.fixedDtYr = s.fixedDtYr;
  runGenerate(W.seed ^ 0x51, rule);
  startScenario(id);
  S._lessonStartedAt = performance.now();
  if (opts.moment !== false) showMoment('Scenario', s.title, s.objective);
  updateHUD();
  return s;
}

async function startLesson(id) {
  const lesson = lessonById(id);
  if (!lesson) return;
  closeDoor();
  closeLandPicker();
  skipReveal();
  document.getElementById('reveal')?.setAttribute('hidden', '');
  setCurrentLesson(id);
  S.lessonId = id;
  S._lessonStartedAt = performance.now();
  S._lessonFailNoted = false;
  if (lesson.catalogue) {
    const item = await catalogueBodyNamed(lesson.catalogue);
    if (item) await loadCatalogueItem(item);
    applyScalePreset(S, 'hold');
    S.localExpanded = true;
    applyLocalLayout();
    setPaused(true);
    _taughtAlive = true;
    showMoment(lesson.kicker || 'Tour', lesson.title, lesson.body);
  } else if (lesson.scenario) {
    beginScenario(lesson.scenario, { moment: false });
    _taughtAlive = true;
    setPaused(lesson.id === 'crisis');
    if (lesson.id === 'crisis') {
      setDockTab('tools');
      setSuiteDesk('tools', 'strike');
    }
    showMoment(lesson.kicker || 'Lesson', lesson.title, lesson.body);
  } else {
    beginHoldEarthLesson();
  }
  paintLessonChip();
  paintCampaignTrack();
}

function openDoor() {
  const panel = document.getElementById('door');
  const grid = document.getElementById('doorgrid');
  if (!panel || !grid) return;
  closeLandPicker();
  closeLocalKey();
  markDoorSeen();
  const p = loadLessonProgress();
  const next = lessonById(p.current) || nextIncompleteLesson(p);
  const sub = panel.querySelector('.lp-sub');
  if (sub) {
    sub.textContent = next
      ? `${lessonChipLabel(p)}. Pick a beat, continue, skip ahead, or reset.`
      : 'Tour complete — pick any beat again, or reset to start over.';
  }
  grid.innerHTML = '';
  for (const lesson of LESSONS) {
    const done = !!p.done[lesson.id];
    const on = p.current === lesson.id || (!p.current && next?.id === lesson.id);
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'door-card' + (done ? ' done' : '') + (on ? ' on' : '');
    const mark = done ? '✓ ' : (on ? '→ ' : '');
    b.innerHTML = `<div class="dk">${lesson.kicker}</div><div class="dt">${mark}${lesson.title}</div><div class="db">${lesson.body}</div>`;
    b.addEventListener('click', () => { void startLesson(lesson.id); });
    grid.appendChild(b);
  }
  const cont = document.getElementById('doorcontinue');
  if (cont) {
    cont.hidden = !next;
    cont.textContent = next ? `Continue · ${next.title}` : 'Continue';
  }
  const skipBtn = document.getElementById('doorskipahead');
  if (skipBtn) skipBtn.hidden = !next;
  skipReveal();
  document.getElementById('reveal')?.setAttribute('hidden', '');
  panel.classList.add('open');
  paintLessonChip();
}

function lessonWorldReady(lesson) {
  if (!lesson) return false;
  if (lesson.catalogue) {
    if (!catalogueReady() || !Cat) return false;
    const item = Cat.CATALOGUE_WORLDS.find((x) => x.b === lesson.catalogue) || null;
    return !!(item && S.catalogueId === item.id);
  }
  if (lesson.scenario) return W.scenarioId === lesson.scenario;
  return true;
}

function onLessonChip() {
  const p = loadLessonProgress();
  const cur = lessonById(p.current);
  const next = (!cur || lessonDone(cur.id, p)) ? nextIncompleteLesson(p) : cur;
  if (!next) {
    setDockTab('god');
    setSuiteDesk('god', 'challenge');
    showMoment('Tour', 'The Solar System is the tutorial', 'Challenges live in Play.');
    return;
  }
  if (p.current === next.id && !lessonDone(next.id, p) && lessonWorldReady(next)) {
    showMoment(next.kicker || 'Lesson', next.title, next.body);
    return;
  }
  void startLesson(next.id);
}

function checkLessonProgress() {
  const id = S.lessonId;
  if (!id || doorIsOpen()) return;
  const lesson = lessonById(id);
  if (!lesson || lessonDone(id)) return;
  if (id === 'hold-earth') {
    const step = S._holdStep | 0;
    /* Any way of turning the planet counts.
     *
     * `_lessonSpun` is set on pointer-up, but only for a left-drag of more than
     * 22 px *while the inspect tool is held* — so a player who picked up a tool
     * first, or span with a right-drag, or is on a touchpad, or nudged it with
     * the keyboard, could turn the world all day and the first lesson would never
     * advance. The orientation is the honest test: has this planet been rotated
     * since the lesson opened. */
    if (S._holdQ0 == null) S._holdQ0 = Array.from(S.q || [0, 0, 0, 1]);
    else if (!S._lessonSpun) {
      const a = S._holdQ0, b = S.q || a;
      // |dot| of two unit quaternions: 1 is no rotation, cos(θ/2) otherwise.
      const dot = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
      if (dot < 0.985) S._lessonSpun = true;   // ~20° of turn
    }
    if (step === 0 && S._lessonSpun) {
      S._holdStep = 1;
      showMoment('Hold Earth', 'Come closer', 'Scroll toward the globe — or press 4 for ISS height.');
    } else if (step === 1 && (S.camDist < (S._lessonCam0 || 2.45) - 0.22 || S.camDist < 2.05)) {
      S._holdStep = 2;
      showMoment('Hold Earth', 'Open the map', 'With Inspect (Q), click the coast. The square is where you can stand.');
      setTool('inspect');
      syncToolRail();
    } else if (step === 2 && S.localPin >= 0) {
      finishLesson(id);
    }
    return;
  }
  if (lesson.hunt) {
    const hover = S.localHoverKey;
    const cellKey = S.localHoverCell >= 0 ? legendKeyAt(W, S.localHoverCell) : null;
    const hit = [hover, cellKey].find((k) => huntMatches(k, lesson));
    if (hit) finishLesson(lesson.id, hit);
    return;
  }
  if (lesson.scenario === 'daisy-tutorial') {
    const report = evaluateScenario(W);
    if (report?.regulated && (performance.now() - (S._lessonStartedAt || 0)) > 3500) {
      finishLesson(lesson.id);
    }
    return;
  }
  if (lesson.scenario === 'save-snowball') {
    const report = evaluateScenario(W);
    if (report?.failed) {
      if (!S._lessonFailNoted) {
        S._lessonFailNoted = true;
        showMoment('Ended', report.ending?.title || 'The run ended', report.ending?.epitaph || '');
      }
      return;
    }
    if (report?.broken) finishLesson(lesson.id);
  }
}

function setupReveal() {
  const el = document.getElementById('reveal');
  if (el) el.hidden = true;
  skipReveal();
  document.getElementById('reveal')?.setAttribute('hidden', '');
  S.lessonId = loadLessonProgress().current;
  document.getElementById('doorskip')?.addEventListener('click', () => { void startLesson('hold-earth'); });
  document.getElementById('doorcontinue')?.addEventListener('click', continueTour);
  document.getElementById('doorskipahead')?.addEventListener('click', skipLessonAhead);
  document.getElementById('doorreset')?.addEventListener('click', resetTour);
  document.getElementById('lessonchip')?.addEventListener('click', onLessonChip);
  paintCampaignTrack();
  paintLessonChip();
}

function setupClimatePanel() {
  const pane = document.getElementById('pane-climate');
  if (!pane) return;
  pane.innerHTML = climatePanelChrome();
  bindClimatePanel({
    setOverlay: (mode) => applyOverlayChoice(mode),
    showMoment,
    getInspectCell: () => S.inspect?.cell ?? -1,
    onChange: () => {
      refreshColours(0.6);
      S.spin = 0.035 / Math.max(0.2, W.rotationPeriod || 1);
    },
  });
}

function setupPlatesPanel() {
  const pane = document.getElementById('pane-rock');
  if (!pane) return;
  pane.innerHTML = platesPanelChrome();
  bindPlatesPanel({
    setOverlay: (mode) => applyOverlayChoice(mode),
    showMoment,
    getInspectCell: () => S.inspect?.cell ?? -1,
    onChange: () => { refreshColours(0.7); needGeom(); },
  });
}

let refreshGuildHint = () => {};

function setupGodPanel() {
  const guildSel = document.getElementById('guildsel');
  if (guildSel) {
    const labels = {
      cyanobacteria: 'cyanobacteria (make O₂)',
      aerobe: 'aerobes (breathe O₂)',
      purpleSulfur: 'purple sulfur (early photosynthesis)',
      greenSulfur: 'green sulfur',
      methanogen: 'methanogens',
      fermenter: 'fermenters',
      decomposer: 'decomposers',
      chemolithotroph: 'vent chemolithotrophs',
    };
    guildSel.innerHTML = GUILDS.map((g) =>
      `<option value="${g.id}"${g.id === selectedGuild ? ' selected' : ''}>${labels[g.id] || g.id}</option>`).join('');
    refreshGuildHint = () => {
      const hint = document.getElementById('guildhint');
      if (!hint) return;
      const name = labels[guildSel.value] || guildSel.value;
      hint.textContent = activeTool === 'seedGuild'
        ? `Click the planet to plant ${name}.`
        : `Seed guild plants ${name}. Pick that verb, then click.`;
    };
    guildSel.onchange = () => { setSelectedGuild(guildSel.value); refreshGuildHint(); };
    refreshGuildHint();
  }
  const scen = document.getElementById('scenariosel');
  const syncScenarioBlurb = () => {
    const id = scen?.value;
    const s = SCENARIOS.find((x) => x.id === id);
    const el = document.getElementById('scenarioblurb');
    if (!el) return;
    const camp = id ? campaignBlurb(id) : '';
    const detail = s?.blurb || s?.objective || '';
    el.textContent = camp && camp !== id
      ? (detail && detail !== camp ? `${camp} ${detail}` : camp)
      : (detail || 'A short goal with limits — optional.');
  };
  if (scen) {
    const preferred = CAMPAIGN.map((id) => SCENARIOS.find((s) => s.id === id)).filter(Boolean);
    const rest = SCENARIOS.filter((s) => !CAMPAIGN.includes(s.id));
    const ordered = [...preferred, ...rest];
    scen.innerHTML = ordered.map((s) => {
      const blurb = campaignBlurb(s.id);
      const tip = blurb && blurb !== s.id ? blurb : (s.blurb || s.objective || '');
      return `<option value="${s.id}" title="${tip.replace(/"/g, '&quot;')}">${s.title}</option>`;
    }).join('');
    scen.onchange = syncScenarioBlurb;
    syncScenarioBlurb();
  }
  document.getElementById('scenariostart')?.addEventListener('click', () => {
    const id = document.getElementById('scenariosel')?.value;
    beginScenario(id, { moment: true });
  });
  document.getElementById('godundo')?.addEventListener('click', () => {
    const r = undoStroke();
    if (r) { showErr(r.note); refreshColours(1); needGeom(); refreshLayerPanel(); }
    else showErr('Nothing to undo');
  });
  document.getElementById('godredo')?.addEventListener('click', () => {
    const r = redoStroke();
    if (r) { showErr(r.note); refreshColours(1); needGeom(); refreshLayerPanel(); }
    else showErr('Nothing to redo');
  });
  document.getElementById('godbookmark')?.addEventListener('click', () => {
    const b = addBookmark();
    showMoment('Bookmark', b.label, formatAge(b.ageYr));
  });
  bindTimeRibbon();
  document.getElementById('godwatch')?.addEventListener('click', () => {
    S.letItRun = !S.letItRun;
    setLetItRun(S.letItRun);
    document.body.classList.toggle('letitrun', S.letItRun);
    document.getElementById('godwatch')?.setAttribute('aria-pressed', S.letItRun ? 'true' : 'false');
  });
  document.getElementById('godshelf')?.addEventListener('click', () => {
    addToShelf(W, serializeRun);
    if (TABLE.enabled) syncTableFromShelf(TABLE, W);
    showMoment('Shelf', W.worldName || W.rule.name, `${loadShelf().length} worlds saved`);
  });
  document.getElementById('godshare')?.addEventListener('click', () => {
    const id = worldIdOf(W);
    let text = id;
    try {
      const u = new URL(location.href);
      u.searchParams.set('world', id);
      u.searchParams.delete('seed');
      u.searchParams.delete('land');
      text = `${id}\n${u.toString()}`;
    } catch { expected('ORR-EXPECTED-URL', 'no location'); }
    navigator.clipboard?.writeText(text);
    showMoment('World id', 'Copied', id);
  });
  const mask = document.getElementById('brushmask');
  if (mask) {
    mask.onchange = () => { BRUSH.mask = mask.value || null; };
  }
  const snap = document.getElementById('brushsnap');
  if (snap) {
    snap.onchange = () => { BRUSH.snap = snap.value || null; };
  }
  document.getElementById('brushhard')?.addEventListener('input', (e) => {
    BRUSH.hardness = (+e.target.value) / 100;
  });
  const brushSym = document.getElementById('brushsym');
  if (brushSym) {
    brushSym.onchange = () => { BRUSH.symmetry = brushSym.value || null; };
  }
  bindLayerPanel();
  const landOpts = LANDSCAPES.map((l) => `<option value="${l.id}">${l.name}</option>`).join('');
  const landSel = document.getElementById('landshape');
  const genLandSel = document.getElementById('genesisland');
  const landBlurb = document.getElementById('landshapeblurb');
  const syncLandPick = (id, suggestLand = false) => syncLandscapeUi(id, suggestLand);
  if (landSel) {
    landSel.innerHTML = landOpts;
    landSel.value = S.landscape || 'auto';
    syncLandPick(landSel.value);
    landSel.addEventListener('change', () => syncLandPick(landSel.value));
  }
  if (genLandSel) {
    genLandSel.innerHTML = landOpts;
    genLandSel.value = S.landscape || 'auto';
    genLandSel.addEventListener('change', () => syncLandPick(genLandSel.value, true));
  }
  const presetSel = document.getElementById('genesispreset');
  if (presetSel) {
    presetSel.innerHTML = '<option value="">— preset —</option>' +
      PRESETS.map((p) => `<option value="${p.id}">${p.name}</option>`).join('');
    presetSel.addEventListener('change', () => {
      if (presetSel.value !== 'no-plates') return;
      const plates = document.getElementById('genesisplates');
      if (plates) { plates.value = 1; plates.dispatchEvent(new Event('input')); }
    });
  }
  const bindRange = (id, valId, fmt, on) => {
    const el = document.getElementById(id);
    const lab = document.getElementById(valId);
    if (!el) return;
    const sync = () => { if (lab) lab.textContent = fmt(+el.value); on?.(+el.value); };
    el.addEventListener('input', sync);
    sync();
  };
  bindRange('genesisplates', 'genesisplatesVal', (v) => String(v));
  bindRange('genesiswater', 'genesiswaterVal', (v) => `${(v / 100).toFixed(2)}×`);
  bindRange('genesislandfrac', 'genesislandfracVal', (v) => `${v}%`);
  bindRange('genesisorigindiff', 'genesisorigindiffVal', (v) => `${(+v).toFixed(1)}×`);
  bindRange('origindiff', 'origindiffVal', (v) => {
    W.originDifficulty = +v;
    if (W.rule) W.rule.originDifficulty = +v;
    return `${(+v).toFixed(1)}×`;
  });
  document.getElementById('godcull')?.addEventListener('click', () => {
    let best = null;
    for (const id of W.tree?.living || []) {
      const n = W.tree.byId.get(id);
      if (n && (!best || (n.pop || 0) > (best.pop || 0))) best = n;
    }
    if (!best) { showMoment('Cull', 'Nothing living', 'No lineage to remove.'); return; }
    const r = cullClade(best.id);
    showMoment('Cull', r.name || best.name, r.ok ? `${r.killed || 0} cells cleared` : (r.note || 'failed'));
  });
  document.getElementById('genesisgo')?.addEventListener('click', () => {
    if (!confirmLeaveLand()) return;
    const base = W.rule || RULESETS[0];
    const g = genesisFromPanel(document);
    g.rulesetId = base.id || 'terra';
    if (!g.name) g.name = nameWorld(g.seed, g.landscape);
    const rule = rulesetFromGenesis(g);
    // Genesis Create always plays formation (unless ?skipOrigin=1).
    try { sessionStorage.removeItem('orrery-origin-skip'); } catch { expected('ORR-EXPECTED-STORAGE', 'origin-skip clear'); }
    runGenerate(g.seed, rule, {
      afterMeta() {
        syncLandscapeUi(g.landscape);
        applyGenesisToWorld(W, g);
        S.genesis = g;
        const seedEl = document.getElementById('genesisseed');
        if (seedEl) seedEl.value = g.seedLabel || encodeWorldId(g.seed, g.landscape);
        rememberWorldId(worldIdOf(W));
        refreshGenesisReport(g);
        applyOverlayChoice('plates');
        showMoment('Genesis', g.name, genesisSummary(g, W) + (W.originDigest ? ` · ${originDigestSummary(W.originDigest)}` : ''));
      },
    });
  });
  document.getElementById('genesisrand')?.addEventListener('click', () => {
    const g = randomizeGenesis({ habitable: true });
    document.getElementById('genesisname').value = g.name;
    document.getElementById('genesisseed').value = encodeWorldId(g.seed, g.landscape);
    if (genLandSel) genLandSel.value = g.landscape;
    const plates = document.getElementById('genesisplates');
    const water = document.getElementById('genesiswater');
    const landf = document.getElementById('genesislandfrac');
    if (plates) { plates.value = g.nPlates; plates.dispatchEvent(new Event('input')); }
    if (water) { water.value = Math.round(g.waterInventory * 100); water.dispatchEvent(new Event('input')); }
    if (landf) { landf.value = Math.round((g.landFrac ?? g.continentFrac) * 100); landf.dispatchEvent(new Event('input')); }
  });
  document.getElementById('dailyseed')?.addEventListener('click', () => {
    if (!confirmLeaveLand()) return;
    const g = genesisFromPanel(document);
    g.seed = dailySeed();
    g.rulesetId = W.rule?.id || 'terra';
    const rule = rulesetFromGenesis(g);
    runGenerate(g.seed, rule);
    applyGenesisToWorld(W, g);
    S.genesis = g;
    syncLandscapeUi(g.landscape);
    rememberWorldId(worldIdOf(W));
    refreshGenesisReport(g);
    showMoment('Daily world', g.name, genesisSummary(g));
  });
}

function setDockTab(tab, opts = {}) {
  document.querySelectorAll('.dock-tabs button').forEach((b) => {
    b.setAttribute('aria-pressed', b.dataset.tab === tab ? 'true' : 'false');
  });
  document.querySelectorAll('.dock-pane').forEach((p) => {
    p.classList.toggle('on', p.id === `pane-${tab}`);
  });
  const dock = document.getElementById('dock');
  dock?.classList.toggle('lab-mode', tab === 'lab');
  // Phone: open the sheet when browsing tabs; tool pick passes { open:false } so the globe stays free.
  if (isPhone() && opts.open !== false) setPhoneDock(true);
  if (tab === 'lab') refreshLab();
  if (tab === 'climate') refreshClimatePanel({ forceChart: true });
  if (tab === 'rock') refreshPlatesPanel({ forceAll: true });
  if (tab === 'sandbox') {
    const modesOn = document.querySelector('.suite-desk[data-suite-panel="sandbox"][data-desk-panel="modes"].on');
    if (modesOn) refreshWorldModeStrip();
  }
}

function refreshLab() {
  const el = document.getElementById('labstats');
  if (!el) return;
  const gauge = redoxGauge(W);
  const chemMem = speciesMemoryReadout(W);
  const keeling = keelingCurve(W);
  const diver = diversityCurve(W);
  const whit = whitakerPoints(W, 280);
  const spec = transitSpectrum(W);
  const phy = phylogenyView(W);
  const co2s = keeling.slice(-80).map((p) => p.co2ppm);
  const divs = diver.slice(-80).map((p) => p.n);
  const ldg = W.latDiversity;
  const h = (icon, title) => `${iconSVG(icon)}<span>${title}</span>`;
  const card = (cat, icon, title, body, hero = false) =>
    `<div class="lab-card${hero ? ' lab-hero' : ''}" data-lab-cat="${cat}"><h3>${h(icon, title)}</h3>${body}</div>`;

  let sampleCard = '';
  if (S.lastSample?.layers) {
    sampleCard = card('sample', 'core', 'Core sample',
      `${coreStrataSVG(S.lastSample.layers)}
      <div class="lab-meta">cell <b>${S.lastSample.cell}</b> · ${S.lastSample.biome}
      ${S.lastSample.proxies ? ` · δ¹³C <b>${S.lastSample.proxies.d13C.toFixed(1)}</b> · pH <b>${S.lastSample.proxies.pH.toFixed(2)}</b>` : ''}
      </div>`);
  } else if (S.lastSample?.samples) {
    const ppm = S.lastSample.samples.map((s) => s.co2 * 1e6);
    sampleCard = card('sample', 'icecore', 'Ice core',
      `${chartAreaSVG(ppm, { id: 'ice', color: '#9fc0ff', label: ' ppm', digits: 0 })}
      <div class="lab-meta">${S.lastSample.note || ''}</div>`);
  }

  el.innerHTML =
    sampleCard +
    card('tower', 'o2', 'Redox tower',
      `${redoxTowerSVG(gauge, 300, S.highlightGuild)}
      <div class="lab-meta">hover a guild to light it on the globe · high yield at top` +
      (chemMem.length
        ? `<br>chemistry memory · ${chemMem.map((s) =>
            `<b>${s.id}</b> ${s.mean.toFixed(3)}`).join(' · ')}`
        : '') +
      `</div>`, true) +
    card('curves', 'co2', 'Keeling curve',
      `${chartAreaSVG(co2s, { id: 'co2', color: '#e4b86a', label: ' ppm', digits: 0, h: 88, w: 280, axis: true })}
      <div class="lab-meta">atmospheric CO₂ · burial drives the long slope</div>`) +
    card('curves', 'seedGuild', 'Diversity',
      `${diversityStripSVG(phy, { w: 280, h: 56 })}
      ${chartAreaSVG(divs, { id: 'div', color: '#6fd6a4', digits: 0, h: 72, w: 280, axis: true })}
      ${earthOverlaySVG(
        diver.slice(-40).map((p) => ({ t: p.t || p.year || 0, v: (p.n || 0) / Math.max(1, Math.max(...divs, 1)) })),
        EARTH_DIVERSITY,
        { w: 280, h: 48 }
      )}
      <div class="lab-meta">clades <b>${phy.living}</b> living` +
      (W.endemicCount ? ` · endemic <b>${W.endemicCount}</b>` : '') +
      (ldg ? ` · LDG Δ<b>${ldg.gradient}</b>` : '') +
      ` · Earth curve in grey</div>`) +
    card('survey', 'seed', 'Tree',
      `${treeToSvg(layoutTree(W.tree, 280, 140)) || '<div class="lab-meta">No phylogeny yet.</div>'}` +
      `<div class="lab-meta">depth <b>${treeSummary(W.tree).maxDepth || 0}</b> · ${phy.living} living` +
      (W.foodWeb?.links?.length ? ` · ${W.foodWeb.links.length} trophic links` : '') +
      `</div>`) +
    card('survey', 'survey', 'Whittaker space',
      `${whitakerSVG(whit)}
      <div class="lab-meta">${whit.length} of ${NC} cells (~250 km each) in temperature–rainfall space</div>`) +
    card('survey', 'solar', 'Transit spectrum',
      `${spectrumSVG(spec.lines)}
      <div class="lab-meta">${spec.note}</div>`) +
    card('tower', 'autopilot', 'Gaia',
      `<div class="lab-meta">feedback <b>${(W.feedbackGain || 0).toFixed(2)}</b> · Medea <b>${((W.medeaScore || 0) * 100) | 0}</b>` +
      (W.carbon ? ` · Ω <b>${W.carbon.omegaAragonite.toFixed(2)}</b>` : '') +
      ` · mode <b>${W.gaiaMode || '—'}</b>` +
      ` · drive <b>${W.autopilot ? gaiaDriveOf(W).label : 'off'}</b>` +
      (W.gaiaFailed ? ` · <b style="color:#c44">overwhelmed</b>` : '') +
      (W.gaiaObjective ? `<br>aims to <b>${W.gaiaObjective}</b>` : '') +
      (W.gaiaLastAct ? `<br>last: ${W.gaiaLastAct}` : '') +
      (W.mood?.label ? ` · mood <b>${W.mood.label}</b>` : '') +
      `</div>`) +
    card('survey', 'weather', 'Synoptic chart',
      `${synopticChartSVG(W)}
      <div class="lab-meta">${W._windRegime || '—'} · spin reorganises banding · forecast limit ~2 weeks</div>`) +
    card('notes', 'moon', 'Tides & shells',
      `<div class="lab-meta">${(() => {
        const t = tideBudget(W);
        const ice = W.rule?.iceShell ? iceShellBudget(W.rule) : null;
        return `${t.note}` +
          (t.phase ? `<br>phase <b>${t.phase}</b>` : '') +
          (t.highInHours != null ? ` · high in ~<b>${t.highInHours}</b> h` : '') +
          (t.springsInDays != null ? ` · springs in <b>${t.springsInDays}</b> d` : '') +
          (t.meanRange != null ? ` · range <b>${t.meanRange}</b>` : '') +
          (ice ? `<br>ice-shell vents <b>${W._shellVentCount || 0}</b> · ${ice.note}` : '') +
          (W.civPop ? `<br>civ pop ~<b>${W.civPop | 0}</b>` : '');
      })()}</div>`) +
    card('notes', 'fork', 'Forked runs',
      `<div class="lab-meta" id="labRunsHost">Loading runs…</div>`) +
    card('notes', 'notes', 'Model limits',
      `<div class="lab-meta" id="labProvChip">Provenance…</div>
      <div class="lab-meta">Cube-sphere N=<b>${N}</b> (~${cellKm(N)} km/cell). Climate may run on GPGPU.
      O₂ from burial, not raw photosynthesis. Traits ≈11 floats, not genomes.
      <button type="button" id="labLimitsOpen" class="lab-linkbtn">limits summary</button>
      · <a href="../briefs/model-limits.md" target="_blank" rel="noopener">full limits</a>
      · water drift <b>${((W.waterDrift || 0) * 100).toFixed(1)}%</b>
      · save v<b>9</b> · seed <b>${W.seed}</b></div>
      <div id="labLimitsHost"></div>`) +
    card('notes', 'inspect', 'Glossary',
      `<div class="lab-meta">${['GOE', 'redox', 'NPP', 'euxinia', 'LUCA'].map((t) => {
        const d = defineTerm(t);
        return d ? `<div><b>${t}</b> — ${d}</div>` : '';
      }).join('')}
      <div style="margin-top:6px;color:var(--dim)">${READING_LIST.slice(0, 3).map((r) =>
        `${r.author}: <i>${r.title}</i>`).join(' · ')}</div></div>`);

  applyLabFilter();
  wireGuildHover(el);
  const runsHost = el.querySelector('#labRunsHost');
  if (runsHost) {
    import('./sim/run.js').then(({ listRuns, applyRun }) => {
      const runs = listRuns().slice().reverse();
      if (!runs.length) {
        runsHost.innerHTML = 'No forks yet — <b>Fork twin</b> appears on notable receipts (Strike / climate / land), not every brush.';
        return;
      }
      runsHost.innerHTML = runs.slice(0, 8).map((r) =>
        `<div class="lab-runrow">`
        + `<button type="button" class="lab-linkbtn" data-run="${r.id}">${r.label || r.id}</button>`
        + ` <span style="color:var(--dim)">seed ${r.seed}`
        + (r.parentId ? ' · twin' : '')
        + `</span></div>`).join('');
      runsHost.querySelectorAll('[data-run]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const run = runs.find((x) => x.id === btn.getAttribute('data-run'));
          if (!run) return;
          try {
            applyRun(run);
            needGeom();
            refreshColours(1);
            updateHUD();
            showMoment('Loaded twin', run.label, `Seed ${run.seed}`);
            refreshLab();
          } catch (e) {
            showMoment('Load failed', String(e.message || e), '');
          }
        });
      });
    }).catch(() => { runsHost.textContent = 'Run shelf unavailable.'; });
  }
  const limBtn = el.querySelector('#labLimitsOpen');
  const limHost = el.querySelector('#labLimitsHost');
  const chip = el.querySelector('#labProvChip');
  if (chip) {
    import('./sim/limitsSheet.js').then(({ loadLimitsSummary, provenanceChipText }) => {
      loadLimitsSummary().then((s) => {
        chip.textContent = provenanceChipText(s, W);
      }).catch(() => { chip.textContent = 'Provenance: run npm run provenance'; });
    }).catch(() => { expected('ORR-EXPECTED-LAZY', 'limits sheet'); });
  }
  if (limBtn && limHost) {
    limBtn.addEventListener('click', () => openLimitsSheet(limHost, W));
    try {
      if (!sessionStorage.getItem('orrery.limits.seen')) {
        sessionStorage.setItem('orrery.limits.seen', '1');
        openLimitsSheet(limHost, W);
      }
    } catch { expected('ORR-EXPECTED-STORAGE', 'limits.seen'); }
  }
}

function wireGuildHover(el) {
  el.querySelectorAll('[data-guild]').forEach((row) => {
    const id = row.getAttribute('data-guild');
    row.addEventListener('pointerenter', () => {
      S.highlightGuild = id;
      setGuildHighlight(id);
      refreshColours(1);
      // Restyle tower without full lab rebuild
      el.querySelectorAll('[data-guild]').forEach((r) => {
        r.classList.toggle('on', r.getAttribute('data-guild') === id);
      });
    });
    row.addEventListener('pointerleave', () => {
      S.highlightGuild = null;
      setGuildHighlight(null);
      refreshColours(1);
      el.querySelectorAll('[data-guild]').forEach((r) => r.classList.remove('on'));
    });
  });
}

let _announcedMoments = new Set();
let _announcedDrama = new Set();
let _momentTimer = 0;

function announceNewMoments() {
  const moments = W.moments || {};
  for (const [key, m] of Object.entries(moments)) {
    if (_announcedMoments.has(key)) continue;
    _announcedMoments.add(key);
    const firstLife = /life|cell|abiogen|luca|photosynth/i.test(key + m.label);
    showMoment(firstLife ? 'The first life' : 'First occurrence', m.label, formatAge(m.ageYr), momentRGB(key));
    playEvent(firstLife ? 'first' : 'seed', firstLife ? 1.1 : 0.85);
    if (firstLife) {
      S.ceremonyUntil = performance.now() + 4500;
      document.body.classList.add('ceremony');
      setTimeout(() => document.body.classList.remove('ceremony'), 4500);
      // Soft pause the rush so the moment lands
      const was = S.paused;
      S.paused = true;
      setTimeout(() => { S.paused = was; }, 2200);
    }
    break;
  }
  const eras = W.chron?.eras || [];
  if (eras.length) {
    const last = eras[eras.length - 1];
    const ek = `era:${last.name}:${last.start}`;
    if (!_announcedMoments.has(ek) && eras.length > 0) {
      _announcedMoments.add(ek);
      if (eras.length > 1) showMoment('New age', last.name, formatAge(last.start), momentRGB(ek));
    }
  }
  const drama = explainDrama(W);
  if (drama?.title) {
    const dk = `drama:${drama.title}`;
    if (!_announcedDrama.has(dk)) {
      _announcedDrama.add(dk);
      showMoment(drama.title, drama.body, drama.settle || '');
    }
  }
}

let _pitchHoldMoment = false;

function showMoment(kicker, title, sub, rgb = null) {
  const el = document.getElementById('moment');
  if (!el) return;
  el.querySelector('.m-kicker').textContent = kicker;
  el.querySelector('.m-title').textContent = title;
  el.querySelector('.m-sub').textContent = sub || '';
  const chip = el.querySelector('.m-chip');
  if (chip) {
    if (rgb) {
      chip.style.background = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
      chip.style.boxShadow = `0 0 28px rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.55)`;
      chip.hidden = false;
    } else {
      chip.hidden = true;
    }
  }
  el.classList.add('show');
  clearTimeout(_momentTimer);
  if (!_pitchHoldMoment) _momentTimer = setTimeout(() => el.classList.remove('show'), 4200);
}

function resetMomentAnnouncer() {
  _announcedMoments = new Set();
  _announcedDrama = new Set();
  resetThought();
}

let _taughtAlive = false;
let _teachT0 = 0;
function maybeTeachWindow(t) {
  if (_taughtAlive) return;
  if (doorIsOpen()) { _teachT0 = 0; return; }
  if (!_teachT0) _teachT0 = t || 0;
  if ((t || 0) - _teachT0 < 1100) return;
  _taughtAlive = true;
  try {
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  } catch { expected('ORR-EXPECTED-MEDIA', 'matchMedia'); }
  let best = -1, score = -1;
  for (let c = 0; c < NC; c++) {
    if (W.h[c] < W.seaLevel || (W.ice[c] || 0) > 0.4) continue;
    let coast = false;
    for (let k = 0; k < 4; k++) {
      const n = NBR[c * 4 + k];
      if (n >= 0 && W.h[n] < W.seaLevel) { coast = true; break; }
    }
    if (!coast) continue;
    const s = (W.life[c] || 0) * 2.2 + (W.moist[c] || 0) + (W.build[c] || 0) * 1.4;
    if (s > score) { score = s; best = c; }
  }
  if (best < 0) return;
  if (S.localSeek !== 'life') S.localPin = best;
  else S.localPin = -1;
  S.localGlobe = 'rim';
  if (S.localRadius < 8) S.localRadius = 8;
  requestFace(best);
  showMoment('The window', 'This patch is the rim on the globe.', 'Click a being to follow · Shift+D watches a day');
  refreshColours(1);
}

function syncLandscapeUi(id, suggestLand = false) {
  S.landscape = id;
  for (const elId of ['landshape', 'genesisland']) {
    const el = document.getElementById(elId);
    if (el) el.value = id;
  }
  const landBlurb = document.getElementById('landshapeblurb');
  if (landBlurb) landBlurb.textContent = landscapeById(id).blurb;
  if (suggestLand) {
    const ls = landscapeById(id);
    if (ls.land != null) {
      const landf = document.getElementById('genesislandfrac');
      if (landf) {
        landf.value = Math.round(ls.land * 100);
        landf.dispatchEvent(new Event('input'));
      }
    }
  }
}

/** Openings — hand-checked (seed, archetype) pairs.
 *  Boot used to be `runGenerate(20260808, RULESETS[0])`: one constant, so every
 *  first run this project has ever shown was the same planet. A URL `?seed=` or
 *  `?land=` or `?world=` still pins it exactly, so a shared link is reproducible. */
const OPENINGS = [
  { seed: 20260808, landscape: 'familiar' },
  { seed: 1043, landscape: 'shattered' },
  { seed: 88117, landscape: 'twoworlds' },
  { seed: 4402, landscape: 'archipelago' },
  { seed: 71230, landscape: 'pangaea' },
  { seed: 5150, landscape: 'belt' },
  { seed: 33871, landscape: 'inland' },
  { seed: 9004, landscape: 'polar' },
  { seed: 61207, landscape: 'highland' },
  { seed: 2718, landscape: 'ridge' },
];

const WORLD_HIST_KEY = 'orrery-world-ids';

function rememberWorldId(id) {
  if (!id) return;
  try {
    const prev = JSON.parse(localStorage.getItem(WORLD_HIST_KEY) || '[]');
    const next = [id, ...prev.filter((x) => x !== id)].slice(0, 12);
    localStorage.setItem(WORLD_HIST_KEY, JSON.stringify(next));
  } catch { expected('ORR-EXPECTED-STORAGE', 'private mode'); }
}

function worldHistory() {
  try { return JSON.parse(localStorage.getItem(WORLD_HIST_KEY) || '[]'); }
  catch { return []; }
}

function confirmLeaveLand() {
  if (!W._sculpted) return true;
  return typeof confirm === 'function'
    ? confirm('This world has carved land that is not in the seed. Leave it?')
    : true;
}

function genesisSummary(g, Wref = W) {
  const rep = Wref._landReport;
  const n = Wref.plates?.length || g.nPlates;
  const land = rep ? `${(rep.landFrac * 100).toFixed(0)}% land · ${rep.count} masses` : '';
  return `${landscapeById(g.landscape).name} · ${n} plates · ${land}`;
}

function refreshGenesisReport(g) {
  const el = document.getElementById('genesisreport');
  if (!el || !W._landReport) return;
  const rep = W._landReport;
  const n = W.plates?.length || g.nPlates;
  const seedNote = g.seedLabel ? `"${g.seedLabel}" → ${g.seed}` : String(g.seed);
  el.textContent = `Built ${landscapeById(g.landscape).name} · ${n} plates · `
    + `water ${(g.waterInventory || 1).toFixed(2)}× · `
    + `${(rep.landFrac * 100).toFixed(0)}% land (${rep.count} masses) · seed ${seedNote}`;
}

function readUrlOpening() {
  let q = null;
  try { q = new URLSearchParams(location.search); } catch { return null; }
  const world = q.get('world');
  if (world) {
    const d = decodeWorldId(world) || parseWorldInput(world);
    if (d?.seed != null) {
      return { seed: d.seed, landscape: d.landscape || 'auto', epoch: d.epoch || null, pinned: true };
    }
  }
  const urlSeed = parseInt(q.get('seed') || '', 10);
  const urlLand = q.get('land');
  const urlEra = q.get('era');
  if (Number.isFinite(urlSeed) || urlLand || urlEra) {
    return {
      seed: Number.isFinite(urlSeed) ? urlSeed >>> 0 : freshSeed(),
      landscape: urlLand && landscapeById(urlLand).id === urlLand ? urlLand : 'auto',
      epoch: urlEra || null,
      pinned: true,
    };
  }
  return null;
}

function pickOpening() {
  const pinned = readUrlOpening();
  if (pinned) return pinned;
  const weighted = OPENINGS.filter((o) => o.landscape !== 'auto');
  const pool = weighted.length ? weighted.concat(OPENINGS) : OPENINGS;
  return { ...pool[(Math.random() * pool.length) | 0], pinned: false };
}

function applyOpening(opening, opts = {}) {
  const base = opts.rule || W.rule || RULESETS[0];
  const g = genesisFromPanel(document);
  g.seed = opening.seed;
  g.landscape = resolvePlayLandscape(opening.landscape, base);
  g.rulesetId = base.id || 'terra';
  if (!g.name) g.name = opening.name || nameWorld(g.seed, g.landscape);
  const born = rulesetFromGenesis(g);
  const rule = opening.epoch ? ruleForEra(born, opening.epoch) : born;
  S.landscape = g.landscape;
  runGenerate(opening.seed, rule, {
    afterMeta() {
      W.worldName = g.name;
      applyGenesisToWorld(W, g);
      S.genesis = g;
      rememberWorldId(worldIdOf(W));
      syncLandscapeUi(g.landscape);
      const seedEl = document.getElementById('genesisseed');
      if (seedEl) seedEl.value = encodeWorldId(opening.seed, g.landscape, opening.epoch);
      refreshGenesisReport(g);
      updateHUD();
      if (opts.welcome) {
        const rep = W._landReport;
        const masses = rep ? `${rep.count} landmasses · ${(rep.landFrac * 100).toFixed(0)}% ocean` : '';
        showMoment(
          'Holocene Earth',
          g.name,
          `${landscapeById(g.landscape).name} — familiar blue marble, invented coastlines. ${masses} Tap a coast · open the map top-right.`,
        );
      }
    },
  });
}

function pickerCandidates(current) {
  const out = [];
  const seen = new Set();
  const add = (seed, landscape) => {
    const id = encodeWorldId(seed, landscape);
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ seed, landscape, id, name: nameWorld(seed, landscape) });
  };
  if (current) add(current.seed, current.landscape);
  for (const o of OPENINGS) add(o.seed, o.landscape);
  const extras = ['shattered', 'twoworlds', 'archipelago', 'inland', 'highland', 'ridge', 'pangaea'];
  let n = 0;
  while (out.length < 9 && n < 24) {
    add(freshSeed(), extras[n % extras.length]);
    n++;
  }
  return out.slice(0, 9);
}

function closeLandPicker() {
  const panel = document.getElementById('landpick');
  panel?.classList.remove('open');
  resetLandPickerUi();
  restoreOverlayFocus();
}

/** Where Tab returns after closing a modal dialog (M14). */
let _overlayFocusRestore = null;

function enterOverlayFocus(root, preferSel = null) {
  if (!root) return;
  _overlayFocusRestore = document.activeElement;
  requestAnimationFrame(() => {
    const prefer = preferSel ? root.querySelector(preferSel) : null;
    const list = dialogFocusables(root);
    const target = prefer || list[0];
    try { target?.focus({ preventScroll: true }); } catch { expected('ORR-EXPECTED-FOCUS', 'overlay focus'); }
  });
}

function restoreOverlayFocus() {
  const el = _overlayFocusRestore;
  _overlayFocusRestore = null;
  try {
    if (el && typeof el.focus === 'function' && document.contains(el)) {
      el.focus({ preventScroll: true });
    } else {
      canvas?.focus({ preventScroll: true });
    }
  } catch { expected('ORR-EXPECTED-FOCUS', 'restore overlay'); }
}

/** Topmost open dialog that should trap Tab. */
function activeOverlayRoot() {
  const kbd = document.getElementById('kbdSheet');
  if (kbd && !kbd.hidden) return kbd;
  const land = document.getElementById('landpick');
  if (land?.classList.contains('open')) return land;
  const cat = document.getElementById('catpanel');
  if (cat?.classList.contains('open')) return cat;
  const legend = document.getElementById('localkey');
  if (legend?.classList.contains('open')) return legend;
  return null;
}

function resetLandPickerUi() {
  _landPickDone = null;
  document.getElementById('landpickcontinue')?.setAttribute('hidden', '');
  const sub = document.getElementById('landpicksub');
  if (sub) {
    sub.textContent = 'Nine continent layouts. Same planet type. The four-word id under each globe is what you send someone.';
  }
}

function finishLandPicker() {
  const done = _landPickDone;
  _landPickDone = null;
  closeLandPicker();
  if (done) done();
}

function localKeyOpen() {
  return document.getElementById('localkey')?.classList.contains('open');
}

function closeLocalKey() {
  const panel = document.getElementById('localkey');
  if (!panel?.classList.contains('open')) return;
  panel.classList.remove('open');
  document.getElementById('localkeybtn')?.setAttribute('aria-pressed', 'false');
  S.localLegendLock = null;
  restoreOverlayFocus();
}

function openLocalKey(focusId = null) {
  const panel = document.getElementById('localkey');
  const list = document.getElementById('localkeylist');
  const sub = document.getElementById('localkeysub');
  if (!panel || !list) return;
  closeLandPicker();
  if (sub) {
    sub.textContent = `One language. Fill colour is shared. Trees on the map are plants; green crosses on the globe are herds; amber is towns; gold rim is this map window.`;
  }
  const highlightable = new Set();
  for (const sec of legendGlossary(W)) {
    if (sec.highlight === false) continue;
    for (const e of sec.entries) {
      if (e.lock === false) continue;
      highlightable.add(e.id);
    }
  }
  const highlight = (focusId && highlightable.has(focusId)) ? focusId : null;
  if (highlight) {
    S.localLegendLock = highlight;
    S.localHoverKey = highlight;
  }
  list.innerHTML = '';
  const addSec = (title, blurb) => {
    const h = document.createElement('div');
    h.className = 'lk-sec';
    h.textContent = title;
    list.appendChild(h);
    if (blurb) {
      const p = document.createElement('p');
      p.className = 'lk-blurb';
      p.textContent = blurb;
      list.appendChild(p);
    }
  };
  const addGrid = (className) => {
    const g = document.createElement('div');
    g.className = 'lk-grid' + (className ? ` ${className}` : '');
    list.appendChild(g);
    return g;
  };
  const addRow = (parent, e) => {
    const canLock = e.lock !== false;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'lk-row' + (canLock ? '' : ' nolock');
    b.dataset.key = e.id;
    const sw = document.createElement('i');
    const swatch = e.swatch || '';
    sw.className = 'lk-swatch' + (swatch ? ` ${swatch}` : '');
    const rgb = e.rgb || [180, 190, 200];
    const css = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
    const shapeOnly = /^(frame|hole|cross|rim|spark|wedge|dashes|tend|arc|glyph-tri|tree|scrub|roof)$/.test(swatch);
    if (!shapeOnly) sw.style.background = css;
    if (/cross|rim|light|streak|spark|wedge|dashes|leaf|tend|arc|glyph-|tree|scrub|block/.test(swatch) || !swatch) {
      sw.style.color = css;
    }
    const body = document.createElement('div');
    const where = e.where === 'globe' || e.where === 'map' ? e.where : '';
    const whereHtml = where
      ? `<span class="lk-where ${where}">${where}</span>`
      : '';
    body.innerHTML = `<div class="lk-name"><span>${e.tip || e.label}</span>${whereHtml}</div><div class="lk-why">${e.why || ''}</div>`;
    b.append(sw, body);
    const paintOn = () => {
      for (const row of list.querySelectorAll('.lk-row')) {
        row.classList.toggle('on', row.dataset.key === e.id);
      }
    };
    b.addEventListener('pointerenter', () => {
      if (canLock) {
        S.localLegendLock = e.id;
        S.localHoverKey = e.id;
      }
      paintOn();
    });
    b.addEventListener('pointerleave', () => {
      if (canLock && S.localLegendLock === e.id) S.localLegendLock = null;
    });
    b.addEventListener('click', () => {
      if (!canLock) {
        paintOn();
        return;
      }
      S.localLegendLock = e.id;
      S.localHoverKey = e.id;
      paintOn();
    });
    parent.appendChild(b);
  };
  for (const sec of legendGlossary(W)) {
    addSec(sec.title, sec.blurb);
    const grid = addGrid(sec.grid || '');
    for (const e of sec.entries) addRow(grid, e);
  }
  for (const row of list.querySelectorAll('.lk-row')) {
    row.classList.toggle('on', !!highlight && row.dataset.key === highlight);
  }
  panel.classList.add('open');
  document.getElementById('localkeybtn')?.setAttribute('aria-pressed', 'true');
  if (highlight) {
    list.querySelector(`.lk-row[data-key="${highlight}"]`)?.scrollIntoView({ block: 'nearest' });
  } else if (focusId) {
    list.querySelector(`.lk-row[data-key="${focusId}"]`)?.scrollIntoView({ block: 'nearest' });
    list.querySelector(`.lk-row[data-key="${focusId}"]`)?.classList.add('on');
  } else {
    list.scrollTop = 0;
  }
  enterOverlayFocus(panel, '#localkeyclose');
}

function openLandPicker(opts = {}) {
  const panel = document.getElementById('landpick');
  const grid = document.getElementById('landpickgrid');
  const foot = document.getElementById('landpickfoot');
  if (!panel || !grid) return;
  closeLocalKey();
  closeDoor();
  _landPickDone = opts.onDone || null;
  const sub = document.getElementById('landpicksub');
  const cont = document.getElementById('landpickcontinue');
  if (opts.firstVisit) {
    cont?.removeAttribute('hidden');
    if (sub) {
      sub.textContent = 'Pick a continent layout — or keep the one behind this panel. Then continue to choose how to play.';
    }
  } else {
    cont?.setAttribute('hidden', '');
  }
  const current = {
    seed: (W.landSeed ?? W.seed) >>> 0,
    landscape: W._landscape || S.landscape || 'auto',
  };
  const cards = pickerCandidates(current);
  grid.innerHTML = '';
  for (const c of cards) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'lp-card';
    b.setAttribute('aria-pressed', c.seed === current.seed && c.landscape === current.landscape ? 'true' : 'false');
    const cvs = document.createElement('canvas');
    cvs.width = 160; cvs.height = 160;
    drawLandscapeThumb(cvs.getContext('2d'), c.seed, c.landscape, 160);
    const meta = document.createElement('div');
    meta.className = 'lp-meta';
    const idBits = c.id.split('.');
    const shortId = idBits[0].split('-').slice(0, 2).join('-') + (idBits[1] ? ` · ${idBits[1]}` : '');
    meta.innerHTML = `<div class="lp-name">${c.name}</div><div class="lp-id" title="${c.id}">${shortId}</div>`;
    b.append(cvs, meta);
    b.addEventListener('click', () => {
      if (!confirmLeaveLand()) return;
      applyOpening(c, { rule: W.rule });
      setPaused(true);
      showMoment('Starting world', c.name, genesisSummary(S.genesis || { landscape: c.landscape, nPlates: W.rule?.nPlates }));
      if (_landPickDone) {
        grid.querySelectorAll('.lp-card').forEach((x) => x.setAttribute('aria-pressed', 'false'));
        b.setAttribute('aria-pressed', 'true');
      } else {
        closeLandPicker();
      }
    });
    grid.appendChild(b);
  }
  const hist = worldHistory();
  if (foot) {
    const recent = hist.slice(0, 3).map((id) => {
      const p = id.split('.');
      return p[0].split('-').slice(0, 2).join('-') + (p[1] ? `.${p[1]}` : '');
    });
    foot.textContent = recent.length
      ? `Recent: ${recent.join(' · ')}`
      : 'The id under a globe is what you send someone.';
  }
  panel.classList.add('open');
  if (opts.pause) setPaused(true);
  hideDockForOverlay();
  enterOverlayFocus(panel, '#landpickclose');
}

/* ---------- boot UI ---------- */
export function boot() {
  endBootDeferral();
  // P21/P41 — opt-in asserts via ?assert=1 (wrap only when requested — Proxy costs generate)
  try {
    if (new URLSearchParams(location.search).get('assert') === '1') {
      enableWorldAsserts(W, true);
    }
  } catch { expected('ORR-EXPECTED-URL', 'assert param'); }
  const cvs = document.getElementById('c');
  const bootN = parseInt(document.getElementById('simN')?.value || '96', 10);
  if (bootN !== N && N_ALLOWED.includes(bootN)) changeResolution(bootN);
  const bootSubd = parseInt(document.getElementById('globeSubd')?.value || '2', 10);
  const rec = recommendGlobeSubd(bootN);
  const subdEl = document.getElementById('globeSubd');
  if (rec < bootSubd && subdEl) {
    subdEl.value = String(rec);
    setGlobeSubd(rec);
  } else if (GLOBE_SUBD_ALLOWED.includes(bootSubd) && bootSubd !== GLOBE_SUBD) {
    setGlobeSubd(bootSubd);
  }
  remeshPlanet();
  initGL(cvs);
  // Q41 — start the frame loop before docks/generate so sky clears onto the canvas early.
  requestAnimationFrame(desktopFrame);

  setupCatalogue();
  setupGodPanel();
  setupClimatePanel();
  setupPlatesPanel();
  setupTips();
  setupReveal();

  const TOOL_DESK = {
    land: 'land',
    life: 'life',
    dis: 'strike',
    clim: 'strike',
    evil: 'evil',
  };
  const deskEls = {
    land: document.getElementById('toolsLand'),
    life: document.getElementById('toolsLife'),
    strike: document.getElementById('toolsStrike'),
    evil: document.getElementById('toolsEvil'),
    clim: document.getElementById('toolsClimate'),
    see: document.getElementById('toolsSample'),
  };
  const syncToolPress = () => {
    document.querySelectorAll(TOOL_BTN_SEL)
      .forEach((x) => x.setAttribute('aria-pressed', x.dataset.id === activeTool ? 'true' : 'false'));
    syncToolRail();
  };
  const adoptTool = (t) => {
    setTool(t.id);
    syncToolPress();
    refreshGuildHint();
    const phone = isPhone();
    if (t.group === 'see') {
      // Inspect is also spin-the-globe — don't yank the dock open.
      if (t.id !== 'inspect') {
        setDockTab('lab', { open: !phone });
        setSuiteDesk('lab', 'station');
      }
      if (phone) setPhoneDock(false);
      return;
    }
    // Arm the verb on its desk, then on phone tuck the sheet so the globe is free to tap.
    setDockTab('tools', { open: !phone });
    setSuiteDesk('tools', TOOL_DESK[t.group] || 'land');
    if (phone) setPhoneDock(false);
  };
  TOOLS.forEach((t) => {
    if (t.group === 'evil' && !darkEnabled()) return;
    const b = document.createElement('button');
    decorateButton(b, t.id, t.name);
    b.dataset.id = t.id;
    const tip = tipForTool(t.id);
    const meta = [
      t.key ? `Key ${t.key.toUpperCase()}` : null,
      t.drag ? 'Drag to stroke' : (t.group === 'clim' ? 'Whole planet' : 'Click the planet'),
      t.irreversible ? 'Hold to commit' : null,
      t.cost ? `Listed cost ~${t.cost}` : 'Free',
    ].filter(Boolean).join(' · ');
    if (tip) bindTip(b, tip.title, tip.body, meta);
    else b.title = meta;
    b.onclick = () => {
      if (toolsUnlocked(W)[t.id] === false) return;
      adoptTool(t);
    };
    if (t.id === 'inspect') b.setAttribute('aria-pressed', 'true');
    const host = t.group === 'clim' ? deskEls.clim
      : t.group === 'see' ? deskEls.see
      : deskEls[TOOL_DESK[t.group] || 'land'];
    host?.appendChild(b);
    // Inspect is the default look — also first on Land so it is not buried in Lab.
    if (t.id === 'inspect' && deskEls.land && host !== deskEls.land) {
      const landBtn = b.cloneNode(true);
      landBtn.onclick = b.onclick;
      if (tip) bindTip(landBtn, tip.title, tip.body, meta);
      deskEls.land.prepend(landBtn);
    }
  });

  const viewOverlays = document.getElementById('viewOverlays');
  if (viewOverlays) {
    viewOverlays.innerHTML = overlaysForPicker().map((o) =>
      `<button type="button" data-overlay="${o.id}">${iconSVG(o.icon || 'inspect')}<span class="btn-label">${o.label}</span></button>`
    ).join('');
    viewOverlays.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-overlay]');
      if (!btn) return;
      applyOverlayChoice(btn.dataset.overlay);
    });
    syncViewOverlayButtons('none');
    bindOverlayTips();
  }

  const viewGuides = document.getElementById('viewOrbitGuides');
  if (viewGuides) {
    decorateButton(viewGuides, 'orbitguides', 'Axis guides');
    viewGuides.setAttribute('aria-pressed', S.orbitGuides ? 'true' : 'false');
    viewGuides.addEventListener('click', () => {
      S.orbitGuides = !S.orbitGuides;
      viewGuides.setAttribute('aria-pressed', S.orbitGuides ? 'true' : 'false');
    });
  }
  const viewThought = document.getElementById('viewThought');
  if (viewThought) {
    decorateButton(viewThought, 'viewThought', 'Thought');
    viewThought.setAttribute('aria-pressed', S.thoughtOn ? 'true' : 'false');
    viewThought.addEventListener('click', () => {
      S.thoughtOn = !S.thoughtOn;
      viewThought.setAttribute('aria-pressed', S.thoughtOn ? 'true' : 'false');
      if (!S.thoughtOn) {
        const el = document.getElementById('thought');
        if (el) { el.classList.remove('show'); el.hidden = true; }
      }
    });
  }
  const viewThoughtMind = document.getElementById('viewThoughtMind');
  if (viewThoughtMind) {
    decorateButton(viewThoughtMind, 'viewThoughtMind', 'Local mind');
    viewThoughtMind.addEventListener('click', async () => {
      const mind = await loadThoughtMind();
      mind.onMindStatus(updateThoughtMindHint);
      if (S.thoughtMind && mind.mindStatus().ready) {
        S.thoughtMind = false;
        await mind.unloadMind();
        updateThoughtMindHint(mind.mindStatus());
        return;
      }
      S.thoughtMind = true;
      S.thoughtOn = true;
      document.getElementById('viewThought')?.setAttribute('aria-pressed', 'true');
      updateThoughtMindHint({ status: 'loading', progress: 'Starting…' });
      const ok = await mind.ensureMind();
      S.thoughtMind = ok;
      updateThoughtMindHint(mind.mindStatus());
    });
    // Resume: auto-load only when prepackaged weights are present; CDN needs a click.
    queueMicrotask(async () => {
      try {
        const mind = await loadThoughtMind();
        mind.onMindStatus(updateThoughtMindHint);
        if (mind.mindPrefOn() && (await mind.probeLocalModel())) {
          updateThoughtMindHint({ status: 'loading', progress: 'Loading prepackaged mind…' });
          const ok = await mind.ensureMind();
          S.thoughtMind = ok;
          updateThoughtMindHint(mind.mindStatus());
        } else if (mind.mindPrefOn()) {
          const hint = document.getElementById('viewThoughtMindHint');
          if (hint) {
            hint.textContent = 'Local mind was on — click to restore from browser cache (or prepack with npm run cernunnos:fetch).';
          }
        } else {
          updateThoughtMindHint(mind.mindStatus());
        }
      } catch {
        updateThoughtMindHint({ status: 'off' });
      }
    });
  }
  document.getElementById('viewOpenKey')?.addEventListener('click', () => {
    if (localKeyOpen()) closeLocalKey();
    else openLocalKey();
  });
  {
    const kbdHint = document.getElementById('viewKeysKbd');
    if (kbdHint) {
      kbdHint.innerHTML = keymapHelpLines()
        .map((line) => line.replace(/</g, '&lt;'))
        .join('<br>');
    }
    document.getElementById('kbdSheetClose')?.addEventListener('click', () => {
      closeKbdSheet();
    });
    document.getElementById('kbdSheet')?.addEventListener('click', (ev) => {
      if (ev.target?.id === 'kbdSheet') closeKbdSheet();
    });
  }

  function closeKbdSheet() {
    const sheet = document.getElementById('kbdSheet');
    if (!sheet || sheet.hidden) return;
    sheet.hidden = true;
    restoreOverlayFocus();
  }

  function openKbdSheet() {
    const sheet = document.getElementById('kbdSheet');
    if (!sheet) return;
    sheet.hidden = false;
    const body = sheet.querySelector('.kbd-body');
    if (body) {
      body.innerHTML = keymapHelpLines()
        .map((l) => `<p>${l.replace(/</g, '&lt;')}</p>`)
        .join('');
    }
    enterOverlayFocus(sheet, '#kbdSheetClose');
  }

  // Dock tabs
  document.querySelectorAll('.dock-tabs button').forEach((b) => {
    b.onclick = () => setDockTab(b.dataset.tab);
  });
  document.getElementById('docktoggle')?.addEventListener('click', () => {
    const dock = document.getElementById('dock');
    const btn = document.getElementById('docktoggle');
    if (!dock) return;
    if (isPhone()) {
      setPhoneDock(!dock.classList.contains('is-open'));
      return;
    }
    flashChromeFast(dock);
    dock.classList.toggle('collapsed');
    const open = !dock.classList.contains('collapsed');
    btn?.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn?.setAttribute('aria-pressed', open ? 'true' : 'false');
    syncChromeFabs();
  });
  const toggleDockFromFab = () => {
    const dock = document.getElementById('dock');
    if (!dock) return;
    if (isPhone()) {
      setPhoneDock(!dock.classList.contains('is-open'));
      return;
    }
    flashChromeFast(dock);
    dock.classList.toggle('collapsed');
    const open = !dock.classList.contains('collapsed');
    document.getElementById('docktoggle')?.setAttribute('aria-expanded', open ? 'true' : 'false');
    syncChromeFabs();
  };
  document.getElementById('dockfab')?.addEventListener('click', toggleDockFromFab);
  document.getElementById('dockshrink')?.addEventListener('click', () => {
    if (isPhone()) setPhoneDock(false);
    else {
      const dock = document.getElementById('dock');
      flashChromeFast(dock);
      dock?.classList.add('collapsed');
      document.getElementById('docktoggle')?.setAttribute('aria-expanded', 'false');
      syncChromeFabs();
    }
  });
  bindPhoneDockDrag(document.getElementById('dockSheetBar'), {
    onDismiss: () => setPhoneDock(false),
  });
  document.getElementById('dockscrim')?.addEventListener('click', () => {
    if (isPhone()) setPhoneDock(false);
  });
  document.getElementById('toolrailArm')?.addEventListener('click', () => {
    const tool = TOOLS.find((t) => t.id === activeTool);
    const desk = tool?.group === 'see' ? null : (TOOL_DESK[tool?.group] || 'land');
    if (tool?.group === 'see' && tool.id !== 'inspect') {
      setDockTab('lab');
      setSuiteDesk('lab', 'station');
    } else {
      setDockTab('tools');
      setSuiteDesk('tools', desk || 'land');
    }
    if (isPhone()) setPhoneDock(true);
  });
  document.getElementById('toolrailClear')?.addEventListener('click', () => {
    const inspect = TOOLS.find((t) => t.id === 'inspect');
    if (inspect) adoptTool(inspect);
    else {
      setTool('inspect');
      syncToolPress();
      if (isPhone()) setPhoneDock(false);
    }
  });
  if (typeof matchMedia === 'function') {
    const phoneQ = matchMedia(PHONE_MQ);
    const onPhone = (e) => {
      syncPhoneUiClass();
      const dock = document.getElementById('dock');
      const btn = document.getElementById('docktoggle');
      if (!dock) return;
    if (phoneQ.matches) {
      dock.classList.remove('collapsed');
      setPhoneDock(false);
      } else {
        document.getElementById('dockscrim')?.classList.remove('on');
        document.getElementById('ui')?.classList.remove('phone-dock-open');
        dock.classList.remove('is-open');
        btn?.setAttribute('aria-expanded', dock.classList.contains('collapsed') ? 'false' : 'true');
        syncChromeFabs();
      }
      syncToolRail();
    };
    phoneQ.addEventListener('change', onPhone);
    syncPhoneUiClass();
    if (phoneQ.matches) setPhoneDock(false);
    else syncChromeFabs();
  }
  syncToolRail();
  syncChromeFabs();

  document.getElementById('newseed').onclick = () => {
    if (!confirmLeaveLand()) return;
    const g = genesisFromPanel(document);
    g.seed = freshSeed();
    g.rulesetId = W.rule?.id || 'terra';
    const rule = rulesetFromGenesis(g);
    runGenerate(g.seed, rule);
    applyGenesisToWorld(W, g);
    S.genesis = g;
    syncLandscapeUi(g.landscape);
    rememberWorldId(worldIdOf(W));
    refreshGenesisReport(g);
    showMoment('Reseed', g.name, genesisSummary(g));
  };
  document.getElementById('budget').onclick = () => {
    const modes = [SCARCITY.free, SCARCITY.observe, SCARCITY.budgeted];
    const cur = W.scarcityMode || (W.budgetMode ? SCARCITY.budgeted : SCARCITY.free);
    const next = modes[(modes.indexOf(cur) + 1) % modes.length];
    setScarcityMode(next);
    const meta = scarcityMeta(next);
    refreshWorldModeStrip();
    updateHUD();
    showMoment('Your power', meta.label, meta.blurb);
  };
  document.getElementById('autopilot').onclick = () => {
    cycleGaiaButton(W);
    const meta = gaiaModeMeta(W);
    chronLog(W.year, 'gaia', 0, 1,
      W.autopilot ? `Gaia ${meta.label} ON — ${gaiaDriveOf(W).aim}` : 'Gaia autopilot OFF');
    refreshWorldModeStrip();
    updateHUD();
    showMoment('Planet autopilot', meta.label, meta.blurb);
  };
  document.getElementById('simN')?.addEventListener('change', (e) => {
    const n = parseInt(e.target.value, 10);
    if (!N_ALLOWED.includes(n)) return;
    try {
      changeResolution(n);
      const rec = recommendGlobeSubd(n);
      const subdEl = document.getElementById('globeSubd');
      if (rec < parseInt(subdEl?.value || '2', 10)) {
        subdEl.value = String(rec);
        setGlobeSubd(rec);
      }
      remeshPlanet();
      const eng = getGpgpu();
      if (eng?.ok) {
        eng.destroySlot('primary');
        eng.createSlot('primary', { N: n });
      }
      runGenerate(W.seed, W.rule);
      const eff = effectiveGlobeSubd(n);
      const quads = (6 * globeN() * globeN()).toLocaleString();
      const cap = eff < parseInt(subdEl?.value || '2', 10) ? ` · mesh capped at ${eff}×` : '';
      const pace = n >= 384 ? ' · ticks will drop to hold the frame' : n >= 256 ? ' · heavy CPU' : '';
      showMoment('Resolution', `N=${n}`, `~${cellKm(n)} km/cell · ${(6 * n * n).toLocaleString()} cells · ${quads} quads${cap}${pace}`);
    } catch (err) {
      showErr(String(err.message || err));
    }
  });
  document.getElementById('globeSubd')?.addEventListener('change', (e) => {
    const s = parseInt(e.target.value, 10);
    if (!GLOBE_SUBD_ALLOWED.includes(s)) return;
    try {
      setGlobeSubd(s);
      remeshPlanet();
      const eff = effectiveGlobeSubd();
      const quads = (6 * globeN() * globeN()).toLocaleString();
      const cap = eff < s ? ` (capped to ${eff}×)` : '';
      showMoment('Globe mesh', `${s}×${cap}`, `${globeVertexCount().toLocaleString()} verts · ${quads} quads`);
    } catch (err) {
      showErr(String(err.message || err));
    }
  });
  document.getElementById('orreryTable')?.addEventListener('click', () => {
    TABLE.enabled = !TABLE.enabled;
    document.getElementById('orreryTable').setAttribute('aria-pressed', TABLE.enabled ? 'true' : 'false');
    if (TABLE.enabled) syncTableFromShelf(TABLE, W);
    showMoment('Orrery table', TABLE.enabled ? 'On' : 'Off',
      TABLE.enabled ? `${TABLE.slots.length} worlds · click to load` : '');
  });
  document.getElementById('export').onclick = () => {
    const text = exportChronicle(W.chron, W.rule.name, W);
    const blob = new Blob([text], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `orrery-${W.rule.id}-${W.seed}.md`;
    a.click();
  };
  document.getElementById('labRefresh')?.addEventListener('click', refreshLab);
  document.getElementById('labPaper')?.addEventListener('click', () => {
    const text = exportPaper(W, W.chron);
    const blob = new Blob([text], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `orrery-paper-${W.rule.id}-${W.seed}.md`;
    a.click();
  });
  document.getElementById('labSave')?.addEventListener('click', () => downloadSave());
  document.getElementById('labFinale')?.addEventListener('click', () => {
    const art = finaleArtefact(W, detectEnding(W) || undefined);
    const md = formatFinaleMarkdown(art);
    const blob = new Blob([md], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `orrery-finale-${W.seed}.md`;
    a.click();
    showMoment(art.ending.title, art.worldName, art.ageLabel);
  });
  document.getElementById('labPng')?.addEventListener('click', async () => {
    const svg = document.querySelector('#labstats svg');
    if (!svg) return;
    try {
      await downloadInstrumentPng(svg, `orrery-lab-${W.seed}.png`, `${W.rule?.name || 'world'} · ${W.seed}`);
    } catch (e) {
      showErr(String(e.message || e));
    }
  });
  document.getElementById('labDual')?.addEventListener('click', () => {
    const status = document.getElementById('status');
    try {
      const worker = new Worker(new URL('./sim/worker.js', import.meta.url), { type: 'module' });
      const seed = W.seed;
      const ruleId = W.rule?.id || 'terra';
      worker.onmessage = (ev) => {
        const msg = ev.data || {};
        if (msg.type === 'ready') {
          worker.postMessage({ type: 'generate', seed, ruleId, deepTime: !!W.rule?.deepTime });
          return;
        }
        if (msg.type === 'generated') {
          worker.postMessage({ type: 'tick', ticks: 24 });
          return;
        }
        if (msg.type === 'tickDone') {
          const localHint = ((W.meanTemp * 1e6 + W.meanLife * 1e3) | 0);
          if (status) {
            status.textContent = `Dual-run worker hash ${msg.hashHint} · main ~${localHint} · age ${msg.ageYr?.toFixed?.(0) ?? msg.ageYr}`;
          }
          worker.terminate();
        }
        if (msg.type === 'error') {
          showErr(msg.message);
          worker.terminate();
        }
      };
      worker.postMessage({ type: 'init' });
      if (status) status.textContent = 'Dual-run worker starting…';
    } catch (e) {
      showErr('Worker unavailable: ' + (e.message || e));
    }
  });
  document.getElementById('labDiag')?.addEventListener('click', async () => {
    const { diagnosticsText, SESSION_ID } = await import('./sim/report.js');
    const { loadShelf } = await import('./sim/god/shelf.js');
    const shelf = loadShelf();
    const shelfLine = shelf.length
      ? shelf.slice(0, 8).map((e) => `${e.label || e.ruleId || '?'}@${e.seed ?? ''}`).join('; ')
      : '(empty)';
    const text = diagnosticsText({
      session: SESSION_ID,
      seed: W.seed,
      rule: W.rule?.id,
      n: W._simN || '—',
      worldId: W.worldId || '—',
      climate: W._gpgpu && !W._gpgpuOff ? 'gpgpu' : 'cpu',
      gpgpuMs: W._gpgpuMs ?? '—',
      ageYr: W.ageYr,
      tick: W._tickIndex | 0,
      droppedTicks: W._droppedTicks || 0,
      dropReason: W._dropReason || '—',
      dark: darkEnabled() ? 'on' : 'off',
      shelf: `${shelf.length} · ${shelfLine}`,
    });
    try {
      await navigator.clipboard?.writeText(text);
      showErr(
        (W._droppedTicks
          ? `Diagnostics copied · ${W._droppedTicks} dropped ticks`
          : 'Diagnostics copied'),
      );
    } catch {
      showErr('Diagnostics ready — clipboard blocked');
      console.info(text);
    }
  });
  const opacityEl = document.getElementById('opacity');
  const opacityVal = document.getElementById('opacityVal');
  const xrayEl = document.getElementById('xray');
  const xrayAmt = document.getElementById('xrayAmt');
  const xrayVal = document.getElementById('xrayVal');
  const xrayRow = document.getElementById('xrayRow');
  const gridEl = document.getElementById('grid');
  const gridVal = document.getElementById('gridVal');
  const syncView = () => {
    S.opacity = clamp((+opacityEl.value) / 100, 0.15, 1);
    opacityVal.textContent = `${opacityEl.value}%`;
    const on = xrayEl.checked;
    xrayRow.style.opacity = on ? '1' : '0.45';
    xrayRow.style.pointerEvents = on ? 'auto' : 'none';
    // Checkbox alone does nothing at Cut 0 — bump to a visible cutaway when enabling
    if (on && +xrayAmt.value < 5) {
      xrayAmt.value = '45';
    }
    S.xray = on ? clamp((+xrayAmt.value) / 100, 0, 0.85) : 0;
    xrayVal.textContent = `${xrayAmt.value}%`;
    S.grid = clamp((+gridEl.value) / 100, 0, 1);
    gridVal.textContent = S.grid < 0.01 ? 'off' : `${gridEl.value}%`;
  };
  opacityEl.addEventListener('input', syncView);
  xrayEl.addEventListener('change', () => {
    if (xrayEl.checked && +xrayAmt.value < 5) xrayAmt.value = '45';
    syncView();
  });
  xrayAmt.addEventListener('input', syncView);
  gridEl.addEventListener('input', syncView);
  document.getElementById('viewClear')?.addEventListener('click', () => {
    opacityEl.value = '100';
    gridEl.value = '0';
    syncView();
  });
  document.getElementById('viewGhost')?.addEventListener('click', () => {
    opacityEl.value = '40';
    syncView();
  });
  const applyViewScale = (id) => {
    const p = applyScalePreset(S, id);
    if (!p) return;
    if (id === 'iss') S.localRadius = Math.max(S.localRadius, 12);
    document.querySelectorAll('#viewScale [data-scale]').forEach((b) => {
      b.setAttribute('aria-pressed', b.dataset.scale === id ? 'true' : 'false');
    });
    const rung = document.getElementById('scalerung');
    if (rung) rung.textContent = scaleRung(S.camDist, W.noSurface);
  };
  document.getElementById('viewScale')?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-scale]');
    if (b) applyViewScale(b.dataset.scale);
  });
  const showEoref = (id) => {
    const ref = eorefById(id);
    const box = document.getElementById('eoref');
    const img = document.getElementById('eorefImg');
    const cap = document.getElementById('eorefCap');
    if (!box || !img) return;
    img.src = ref.url;
    img.alt = `${ref.label} — ${ref.credit}`;
    if (cap) cap.textContent = `${ref.label} · ${ref.credit}. ${ref.note}`;
    box.classList.add('on');
    if (ref.preset) applyViewScale(ref.preset);
  };
  const hideEoref = () => {
    const box = document.getElementById('eoref');
    if (!box) return;
    box.classList.remove('on');
  };
  document.getElementById('viewEoref')?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-eoref]');
    if (b) showEoref(b.dataset.eoref);
  });
  document.getElementById('eorefHide')?.addEventListener('click', hideEoref);
  document.getElementById('eorefClose')?.addEventListener('click', hideEoref);
  const syncLook = () => {
    document.getElementById('lookPhoto')?.setAttribute('aria-pressed', S.lookMode === 'photo' ? 'true' : 'false');
    document.getElementById('lookDiagram')?.setAttribute('aria-pressed', S.lookMode === 'diagram' ? 'true' : 'false');
    document.getElementById('cloudFree')?.setAttribute('aria-pressed', S.cloudFree ? 'true' : 'false');
  };
  document.getElementById('lookPhoto')?.addEventListener('click', () => { S.lookMode = 'photo'; syncLook(); });
  document.getElementById('lookDiagram')?.addEventListener('click', () => { S.lookMode = 'diagram'; syncLook(); });
  document.getElementById('cloudFree')?.addEventListener('click', () => { S.cloudFree = !S.cloudFree; syncLook(); });
  syncLook();
  const canvasBtn = document.getElementById('canvasmode');
  const syncCanvas = () => {
    W._canvasMode = !!S.canvasMode;
    canvasBtn?.setAttribute('aria-pressed', S.canvasMode ? 'true' : 'false');
  };
  canvasBtn?.addEventListener('click', () => {
    S.canvasMode = !S.canvasMode;
    syncCanvas();
    if (S.canvasMode) {
      applyOverlayChoice('crust');
      setTool('raise');
      setSuiteDesk('tools', 'land');
      document.querySelectorAll(TOOL_BTN_SEL).forEach((x) => {
        x.setAttribute('aria-pressed', x.dataset.id === 'raise' ? 'true' : 'false');
      });
      syncToolRail();
      S.localExpanded = true;
      syncLocalLayout?.();
      showErr('Canvas: cube net on the map. Left-drag to paint. Plates frozen.');
    } else {
      showErr('Canvas off — geology ticks again.');
    }
  });
  document.getElementById('rerolland')?.addEventListener('click', () => {
    if (W.rule) W.rule.landscape = S.landscape || 'auto';
    const r = rerollTerrain();
    if (r?.ok) {
      refreshColours(1);
      needGeom();
      const rep = r.report;
      showErr(rep
        ? `New continents · ${rep.count} landmasses · ${(rep.landFrac * 100).toFixed(0)}% land`
        : 'Same climate, new continents.');
      rememberWorldId(worldIdOf(W));
      updateHUD();
      refreshLayerPanel();
    } else showErr(r?.note || 'Could not reroll land');
  });
  document.getElementById('landpickbtn')?.addEventListener('click', () => openLandPicker({ pause: true }));
  document.getElementById('landpickclose')?.addEventListener('click', () => {
    if (_landPickDone) finishLandPicker();
    else closeLandPicker();
  });
  document.getElementById('landpickcontinue')?.addEventListener('click', () => finishLandPicker());
  document.getElementById('tourbtn')?.addEventListener('click', onTourButton);
  document.getElementById('localkeybtn')?.addEventListener('click', () => {
    if (localKeyOpen()) closeLocalKey();
    else openLocalKey();
  });
  document.getElementById('localkeyclose')?.addEventListener('click', () => closeLocalKey());
  syncView();

  const phone = isPhone();
  // Phone: visible peek (200px) top-right; desktop starts at M.
  S.localSize = phone ? PHONE_LOCAL_PEEK : LOCAL_SIZE_M;
  if (phone) {
    S.localSnap = 'tr';
    S.localSeek = 'stay';
  }
  const localPanel = document.getElementById('localpanel');
  const localCvs = document.getElementById('localview');
  const localLegend = document.getElementById('locallegend');
  const localStatus = document.getElementById('localstatus');
  const mkSeg = (hostId, values, labels, get, set) => {
    const host = document.getElementById(hostId);
    if (!host) return;
    host.innerHTML = '';
    values.forEach((v, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = labels[i];
      b.dataset.v = String(v);
      b.setAttribute('aria-pressed', get() === v ? 'true' : 'false');
      b.onclick = () => {
        set(v);
        [...host.children].forEach((x) =>
          x.setAttribute('aria-pressed', x.dataset.v === String(v) ? 'true' : 'false'));
      };
      host.appendChild(b);
    });
  };
  const syncSegPressed = (hostId, get) => {
    const host = document.getElementById(hostId);
    if (!host) return;
    const cur = String(get());
    [...host.children].forEach((x) =>
      x.setAttribute('aria-pressed', x.dataset.v === cur ? 'true' : 'false'));
  };
  const syncLocalLayout = () => {
    if (S.localExpanded && S.localParked) S.localParked = false;
    layoutLocalPanel(localPanel, localCvs, {
      size: S.localSize,
      snap: S.localSnap,
      expanded: S.localExpanded,
    });
    localPanel?.classList.toggle('parked', !!S.localParked);
    const tag = document.getElementById('localframetag');
    if (tag) tag.textContent = localFrameLabel(S.localSize, S.localExpanded);
    const shrink = document.getElementById('localshrink');
    const grow = document.getElementById('localgrow');
    const fi = localFrameIndex(S.localSize, S.localExpanded);
    if (grow) grow.disabled = isPhone()
      ? !!S.localExpanded
      : fi >= LOCAL_SIZES.length;
    if (shrink) shrink.disabled = isPhone()
      ? !!S.localParked
      : (fi <= 0 && !!S.localParked);
    const park = document.getElementById('localpark');
    if (park) park.disabled = !!S.localParked;
    syncSegPressed('localSize', () => S.localSize);
    placeLocalBarOverflow();
    requestAnimationFrame(() => placeLocalBarOverflow());
    if (_playtest && S.localExpanded) _playtest.noteDescend();
    syncPhoneLocalChrome();
    syncChromeFabs();
    if (S.localExpanded || isPhone()) refreshColours(1);
  };
  applyLocalLayout = syncLocalLayout;
  /** Park bar controls into ⋯ when the panel is too narrow; restore when it grows. */
  const placeLocalBarOverflow = () => {
    const tools = document.querySelector('#localbar .local-tools');
    const moreBtn = document.getElementById('localmorebtn');
    const radius = document.getElementById('localRadius');
    const move = document.getElementById('localMove');
    const sepZoom = document.getElementById('localSepZoom');
    const sepMove = document.getElementById('localSepMove');
    const radiusWrap = document.getElementById('localRadiusMoreWrap');
    const moveWrap = document.getElementById('localMoveMoreWrap');
    if (!tools || !moreBtn || !radius || !move || !radiusWrap || !moveWrap) return;

    // Phone peek: keep nudge in the bar — drag-pan is primary but the pad helps precision.
    if (localPanel?.classList.contains('phone-peek')) {
      if (sepZoom) {
        tools.insertBefore(sepZoom, moreBtn);
        sepZoom.hidden = false;
      }
      tools.insertBefore(radius, moreBtn);
      radiusWrap.hidden = true;
      if (sepMove) {
        tools.insertBefore(sepMove, moreBtn);
        sepMove.hidden = false;
      }
      tools.insertBefore(move, moreBtn);
      moveWrap.hidden = true;
      moreBtn.dataset.packed = 'false';
      moreBtn.title = 'snap · globe';
      const more = document.getElementById('localmore');
      const bar = document.getElementById('localbar');
      if (more && bar) more.style.top = `${Math.ceil(bar.offsetHeight * 0.5 + 6)}px`;
      return;
    }

    const chrome = localPanel?.dataset.chrome || 'map';
    const tight = chrome === 'icon' || chrome === 'chip';
    const fits = () => !tight && tools.scrollWidth <= tools.clientWidth + 1;

    // Prefer everything in the bar (roomiest first).
    if (sepZoom) {
      tools.insertBefore(sepZoom, moreBtn);
      sepZoom.hidden = false;
    }
    tools.insertBefore(radius, moreBtn);
    radiusWrap.hidden = true;
    if (sepMove) {
      tools.insertBefore(sepMove, moreBtn);
      sepMove.hidden = false;
    }
    tools.insertBefore(move, moreBtn);
    moveWrap.hidden = true;

    // Drop nudge first — it's the tallest / widest secondary control.
    if (!fits()) {
      moveWrap.appendChild(move);
      moveWrap.hidden = false;
      if (sepMove) sepMove.hidden = true;
    }
    // Then zoom chips if still tight (S / icon / chip).
    if (!fits()) {
      radiusWrap.appendChild(radius);
      radiusWrap.hidden = false;
      if (sepZoom) sepZoom.hidden = true;
    }

    const packed = !moveWrap.hidden || !radiusWrap.hidden;
    moreBtn.dataset.packed = packed ? 'true' : 'false';
    const bits = [];
    if (!radiusWrap.hidden) bits.push('zoom');
    if (!moveWrap.hidden) bits.push('nudge');
    bits.push('snap', 'globe');
    moreBtn.title = bits.join(' · ');

    // Keep more panel under the (now shorter) bar when nudge left it.
    const more = document.getElementById('localmore');
    const bar = document.getElementById('localbar');
    // Bar straddles the panel top (translateY -50%), so its visual bottom is ~half height.
    if (more && bar) more.style.top = `${Math.ceil(bar.offsetHeight * 0.5 + 6)}px`;
  };
  const stepLocalFrame = (dir) => {
    if (isPhone() && stepPhoneLocalFrame(dir)) return;
    if (S.localParked && dir > 0) {
      unparkLocalMap();
      return;
    }
    const max = LOCAL_SIZES.length; // last index = full
    let i = localFrameIndex(S.localSize, S.localExpanded);
    /* Phone / quick tuck: from Full, one − jumps straight to the icon peek
       instead of walking XL→L→… — the globe should come back fast. */
    if (dir < 0 && S.localExpanded && (isPhone() || dir === -2)) {
      tuckLocalMap();
      return;
    }
    if (dir < 0 && !S.localExpanded && i <= 0) {
      parkLocalMap();
      return;
    }
    const step = dir === -2 ? -1 : dir;
    const next = Math.max(0, Math.min(max, i + step));
    if (next === i) return;
    if (next >= LOCAL_SIZES.length) {
      if (S.localParked) unparkLocalMap();
      S.localExpanded = true;
      const focus = S.focusCell ?? S.pinCell ?? S.kbCursor ?? S._localFocus ?? 0;
      const last = W.receipts?.[W.receipts.length - 1]?.tool;
      armLocalCue(focus, last);
    } else {
      S.localExpanded = false;
      S.localSize = LOCAL_SIZES[next];
    }
    syncLocalLayout();
    syncChromeFabs();
  };
  if (localLegend) {
    let legendKind = null;
    const fillLocalLegend = () => {
      const kind = W._planetKind || 'earth';
      if (kind === legendKind && localLegend.children.length) return;
      legendKind = kind;
      localLegend.innerHTML = '';
      for (const e of legendEntries(W)) {
        const el = document.createElement('span');
        el.className = 'leg';
        el.dataset.key = e.id;
        el.dataset.tip = e.tip || e.label;
        el.title = el.dataset.tip;
        const sw = document.createElement('i');
        sw.style.background = `rgb(${e.rgb[0]},${e.rgb[1]},${e.rgb[2]})`;
        el.appendChild(sw);
        el.appendChild(document.createTextNode(e.label));
        el.addEventListener('pointerenter', () => {
          S.localLegendLock = e.id;
          S.localHoverKey = e.id;
        });
        el.addEventListener('pointerleave', () => {
          if (localKeyOpen()) return;
          if (S.localLegendLock === e.id) S.localLegendLock = null;
        });
        el.addEventListener('click', () => openLocalKey(e.id));
        localLegend.appendChild(el);
      }
    };
    fillLocalLegend();
    syncLocalChrome = (patch, hoverKey) => {
    fillLocalLegend();
    if (localLegend) {
      const n = Math.max(1, patch?.status?.nCells || 1);
      const shares = patch?.status?.shares || {};
      const ranked = patch?.status?.census?.ranked || [];
      const rankOf = Object.fromEntries(ranked.map((e, i) => [e.id, i]));
      const hasCensus = !!(patch?.status?.census && !patch?.status?.net);
      const chrome = localPanel?.dataset.chrome || 'map';
      const maxLegs = chrome === 'chip' ? 6 : 14;
      const keep = new Set();
      for (const e of ranked) {
        if (keep.size >= maxLegs) break;
        if (((shares[e.id] || 0) / n) * 100 >= 1) keep.add(e.id);
      }
      for (const el of localLegend.children) {
        const k = el.dataset.key;
        const on = hoverKey && k === hoverKey;
        const share = (shares[k] || 0) / n;
        const pct = Math.round(share * 100);
        const present = pct >= 1;
        el.hidden = hasCensus && !on && (!present || !keep.has(k));
        el.style.order = String(rankOf[k] ?? 99);
        el.classList.toggle('on', !!on);
        el.classList.toggle('dim', !!(hoverKey && !on && present));
        el.style.setProperty('--share', share.toFixed(3));
        let num = el.querySelector('.leg-n');
        if (!num) {
          num = document.createElement('span');
          num.className = 'leg-n';
          el.appendChild(num);
        }
        // Fixed-width pct so chips don't reflow as 1% ↔ 11% ↔ 100%.
        num.textContent = present ? `${String(pct).padStart(2, '\u2007')}%` : '';
        const tip = el.dataset.tip || k;
        el.title = present ? `${tip} · ${pct}% of this window` : tip;
      }
    }
    if (localStatus && patch?.status) {
      const st = patch.status;
      const chip = localPanel?.dataset.chrome === 'chip';
      const bit = (k, v) =>
        `<span class="st" data-k="${k}"><em>${k}</em><b>${v}</b></span>`;
      const censusLine = chip
        ? [st.census?.coverLine, st.census?.guildLine, st.census?.critterLine]
            .filter(Boolean).join(' · ').split(' · ').slice(0, 4).join(' · ')
        : st.census?.line;
      localStatus.innerHTML = [
        censusLine ? bit('in', censusLine) : '',
        bit('here', st.place || (st.pinned ? 'pinned' : 'live')),
        !chip && !st.pinned && st.seek === 'life' ? bit('track', st.why || 'life') : '',
        st.scaleKm ? bit('view', `~${st.scaleKm} km`) : bit('view', `${st.side}×${st.side}`),
        !chip && (st.water && st.water !== 'dry' && st.water !== 'ice' ? bit('flow', st.water) : (st.rivers ? bit('flow', 'rivers') : '')),
        st.day ? bit('light', S.dayWatch ? 'a day' : (st.moonlit ? 'moonlit' : st.day)) : '',
        !chip && st.behind ? bit('look', 'far side') : '',
        !chip && st.whisper ? bit('then', st.whisper) : '',
        !chip && S.follow?.name ? bit('who', S.follow.name + (S.follow.behav ? ' · ' + S.follow.behav : '')) : '',
      ].filter(Boolean).join('');
      localStatus.title = [
        st.census?.line ? `In view: ${st.census.line}` : '',
        st.place,
        st.pinned ? 'Pinned' : (st.seek === 'life' ? (st.why ? `Touring ${st.why}` : 'Hunting recent life') : 'Live focus'),
        `Cell ${st.cell}`,
        st.label ? `Life: ${st.label}` : '',
        st.biome ? `Biome: ${st.biome}` : '',
        st.scaleNamed ? `Window ~${st.scaleKm} km${st.scaleNamed.startsWith('≈') ? ` (${st.scaleNamed} scale)` : ` · ${st.scaleNamed}`}` : '',
        st.cellKm ? `Cell ~${st.cellKm} km` : '',
        S.follow?.name ? `Following ${S.follow.name}` : '',
      ].filter(Boolean).join(' · ');
      localStatus.classList.toggle('pinned', !!st.pinned);
      localPanel?.setAttribute('data-light', st.moonlit ? 'moonlit' : (st.day || ''));
      localPanel?.classList.toggle('behind', !!st.behind);
      localPanel?.classList.toggle('daywatch', !!S.dayWatch);
    }
    const card = document.getElementById('lifecard');
    if (card) {
      const f = S.follow;
      if (f?.name) {
        card.hidden = false;
        card.querySelector('.lc-kicker').textContent = f.behav === 'flee' ? 'fleeing' : 'following';
        card.querySelector('.lc-name').textContent = f.name;
        const bits = [f.behav, f.kind === 5 ? 'settler' : null].filter(Boolean);
        card.querySelector('.lc-meta').textContent = bits.join(' · ');
      } else {
        card.hidden = true;
      }
    }
  };
  }
  mkSeg('localSnap', LOCAL_SNAPS, ['TL', 'TR', 'BL', 'BR'],
    () => S.localSnap,
    (v) => {
      S.localSnap = v;
      clearLocalFreePos();
      syncLocalLayout();
    });
  mkSeg('localGlobe', LOCAL_GLOBE, ['Off', 'Rim', 'Wash', 'Both'],
    () => S.localGlobe,
    (v) => {
      S.localGlobe = v;
      refreshColours(1);
    });
  mkSeg('localSeek', LOCAL_SEEK, LOCAL_SEEK_LABELS,
    () => S.localSeek,
    (v) => {
      S.localSeek = v;
      resetFocusCache();
      if (v === 'life') {
        S.follow = null;
        clearLocalPin();
      } else if (S._localFocus >= 0) {
        pinLocal(S._localFocus);
      }
      const auto = document.querySelector('#localMove [data-auto]');
      if (auto) auto.title = v === 'life' ? 'Hunt recent life — release pin' : 'Stay — densest life';
    });
  syncSeekSeg = () => syncSegPressed('localSeek', () => S.localSeek);
  mkSeg('localRadius', LOCAL_RADII, LOCAL_RADIUS_LABELS,
    () => S.localRadius,
    (v) => { S.localRadius = v; refreshColours(1); });
  // Optional size chips if host still present (⋯ used to have them)
  mkSeg('localSize', LOCAL_SIZES, LOCAL_SIZE_LABELS,
    () => S.localSize,
    (v) => { S.localExpanded = false; S.localSize = v; syncLocalLayout(); });
  syncLocalLayout();
  bindPhoneLocalDrag(localPanel, document.getElementById('localbar'), {
    onSnap: (snap) => {
      S.localSnap = snap;
      clearLocalFreePos();
      syncLocalLayout();
    },
  });
  {
    let shrinkTapAt = 0;
    document.getElementById('localshrink')?.addEventListener('click', () => {
      const now = performance.now();
      if (now - shrinkTapAt < 320) {
        tuckLocalMap();
        shrinkTapAt = 0;
        return;
      }
      shrinkTapAt = now;
      stepLocalFrame(-1);
    });
  }
  document.getElementById('localgrow')?.addEventListener('click', () => stepLocalFrame(1));
  document.getElementById('localpark')?.addEventListener('click', () => tuckLocalMap());
  document.getElementById('localmaptoggle')?.addEventListener('click', (e) => {
    e.stopPropagation();
    stepLocalFrame(S.localExpanded ? -1 : 1);
  });
  document.getElementById('localfab')?.addEventListener('click', () => unparkLocalMap());
  document.getElementById('localframetag')?.addEventListener('click', () => {
    if (S.localParked) { unparkLocalMap(); return; }
    if (isPhone()) {
      stepLocalFrame(S.localExpanded ? -1 : 1);
      return;
    }
    const i = localFrameIndex(S.localSize, S.localExpanded);
    if (i >= LOCAL_SIZES.length) {
      S.localExpanded = false;
      S.localSize = LOCAL_SIZES[0];
      syncLocalLayout();
      syncChromeFabs();
    } else {
      stepLocalFrame(1);
    }
  });
  document.getElementById('localmorebtn')?.addEventListener('click', () => {
    const box = document.getElementById('localmore');
    const btn = document.getElementById('localmorebtn');
    const on = !box?.classList.contains('open');
    box?.classList.toggle('open', on);
    btn?.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  window.addEventListener('resize', () => {
    syncLocalLayout();
  });

  const pinLocal = (cell) => {
    if (cell == null || cell < 0) return;
    S.localPin = cell | 0;
    requestFace(cell);
    if (S.localGlobe === 'wash' || S.localGlobe === 'both') refreshColours(1);
  };
  const clearLocalPin = () => {
    S.localPin = -1;
    S.follow = null;
    resetFocusCache();
    if (S.localGlobe === 'wash' || S.localGlobe === 'both') refreshColours(1);
  };
  const nudgeLocal = (dx, dy) => {
    const base = S.localPin >= 0 ? S.localPin : S._localFocus;
    if (base < 0) return;
    pinLocal(stepFocus(base, dx, dy));
  };
  const stepLocalZoom = (dir) => {
    const i = LOCAL_RADII.indexOf(S.localRadius);
    const fallback = Math.max(0, LOCAL_RADII.indexOf(8));
    const ni = Math.max(0, Math.min(LOCAL_RADII.length - 1, (i < 0 ? fallback : i) + dir));
    if (LOCAL_RADII[ni] === S.localRadius) return;
    S.localRadius = LOCAL_RADII[ni];
    syncSegPressed('localRadius', () => S.localRadius);
    refreshColours(1);
  };

  // Move pad
  const moveHost = document.getElementById('localMove');
  if (moveHost) {
    const mk = (label, fn, title) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      if (title) b.title = title;
      b.onclick = fn;
      moveHost.appendChild(b);
      return b;
    };
    mk('↖', () => nudgeLocal(-1, -1));
    mk('↑', () => nudgeLocal(0, -1));
    mk('↗', () => nudgeLocal(1, -1));
    mk('←', () => nudgeLocal(-1, 0));
    const auto = mk('·', clearLocalPin, S.localSeek === 'life' ? 'Hunt recent life — release pin' : 'Stay — densest life');
    auto.dataset.auto = '1';
    auto.textContent = 'A';
    mk('→', () => nudgeLocal(1, 0));
    mk('↙', () => nudgeLocal(-1, 1));
    mk('↓', () => nudgeLocal(0, 1));
    mk('↘', () => nudgeLocal(1, 1));
    placeLocalBarOverflow();
  }

  localPanel?.addEventListener('transitionend', (e) => {
    if (e.target !== localPanel) return;
    if (e.propertyName === 'width' || e.propertyName === 'transform' || e.propertyName === 'max-height') {
      placeLocalBarOverflow();
    }
  });

  // Drag / click the flat map to move the window across the globe
  let localDrag = null;
  let localAccX = 0, localAccY = 0;
  localCvs.style.cursor = 'crosshair';
  localCvs.addEventListener('contextmenu', (e) => e.preventDefault());
  localCvs.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const rect = localCvs.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    const c = hoverCellAt(S._localPatch, cssX, cssY);
    const tool = TOOLS.find((t) => t.id === activeTool);
    const paint = S.canvasMode || e.button === 2 || (e.altKey && activeTool !== 'inspect');
    if (paint && c >= 0) {
      setPinpoint(true);
      setCrustOceanic(activeTool === 'crust' && e.shiftKey);
      setBrushInvert(e.altKey && (activeTool === 'raise' || activeTool === 'lower'));
      if (tool?.drag) {
        const r = beginToolDrag(c);
        if (r?.ok) {
          S.toolDrag = true;
          localDrag = { paint: true, x: e.clientX, y: e.clientY, moved: false };
          localCvs.setPointerCapture(e.pointerId);
          onToolResult(r);
          return;
        }
      }
      dispatchIntent('act', { cell: c, opts: { oceanic: e.shiftKey } }, 'pointer');
      return;
    }
    if (S.canvasMode) return;
    localCvs.setPointerCapture(e.pointerId);
    localCvs.style.cursor = 'grabbing';
    const base = S.localPin >= 0 ? S.localPin : S._localFocus;
    localDrag = { x: e.clientX, y: e.clientY, moved: false, startPin: base };
    localAccX = 0; localAccY = 0;
  });
  localCvs.addEventListener('pointermove', (e) => {
    const rect = localCvs.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    if (!localDrag) {
      const c = hoverCellAt(S._localPatch, cssX, cssY);
      S.localHoverCell = c;
      setLocalHover(c);
      if (!S.localLegendLock) {
        S.localHoverKey = c >= 0 ? legendKeyAt(W, c) : null;
      }
      if (activeTool !== 'inspect') {
        setPinpoint(true);
        if (c >= 0) previewBrush(c);
        else { BRUSH.preview = []; BRUSH.previewCenter = -1; }
      }
      localCvs.style.cursor = beingAtLocalPixel(S._localPatch, cssX, cssY) ? 'pointer' : 'crosshair';
      return;
    }
    if (localDrag.paint) {
      const c = hoverCellAt(S._localPatch, cssX, cssY);
      S.localHoverCell = c;
      if (c >= 0 && S.toolDrag) moveToolDrag(c);
      return;
    }
    const dx = e.clientX - localDrag.x;
    const dy = e.clientY - localDrag.y;
    const dragMin = phone ? 3 : 5;
    if (!localDrag.moved && Math.hypot(dx, dy) < dragMin) return;
    localDrag.moved = true;
    const lay = S._localPatch?.layout;
    const panK = phone ? 0.62 : 1;
    const cellCss = (lay ? lay.cellPx / (lay.dpr || 1) : 12) * panK;
    localAccX += dx;
    localAccY += dy;
    localDrag.x = e.clientX;
    localDrag.y = e.clientY;
    const stepsX = Math.trunc(localAccX / cellCss);
    const stepsY = Math.trunc(localAccY / cellCss);
    if (!stepsX && !stepsY) return;
    localAccX -= stepsX * cellCss;
    localAccY -= stepsY * cellCss;
    // Drag map content with the pointer (grab-and-slide)
    const next = stepFocus(S.localPin >= 0 ? S.localPin : localDrag.startPin, -stepsX, -stepsY);
    pinLocal(next);
  });
  localCvs.addEventListener('pointerleave', () => {
    if (localDrag) return;
    S.localHoverCell = -1;
    if (!S.localLegendLock) S.localHoverKey = null;
  });
  const endLocalDrag = (e) => {
    if (!localDrag) return;
    localCvs.style.cursor = 'crosshair';
    if (localDrag.paint) {
      if (S.toolDrag) endToolDrag();
      S.toolDrag = false;
      setBrushInvert(false);
      localDrag = null;
      refreshColours(1);
      return;
    }
    if (!localDrag.moved) {
      const rect = localCvs.getBoundingClientRect();
      const cssX = e.clientX - rect.left;
      const cssY = e.clientY - rect.top;
      const who = beingAtLocalPixel(S._localPatch, cssX, cssY);
      if (who && !who.dead) {
        S.follow = who;
        pinLocal(who.cell);
        showErr(who.name ? `Following ${who.name}` : 'Following');
      } else {
        const c = hoverCellAt(S._localPatch, cssX, cssY);
        if (c >= 0) {
          pinLocal(c);
          if (activeTool === 'inspect') {
            S.inspect = inspectCell(c);
            updateHUD();
          }
        }
      }
    }
    localDrag = null;
  };
  localCvs.addEventListener('pointerup', endLocalDrag);
  localCvs.addEventListener('pointercancel', () => { localDrag = null; localCvs.style.cursor = 'crosshair'; });
  localCvs.addEventListener('dblclick', (e) => {
    if (isPhone()) {
      e.preventDefault();
      stepLocalFrame(S.localExpanded ? -1 : 1);
      return;
    }
    if (localPanel?.dataset.chrome !== 'icon') return;
    e.preventDefault();
    stepLocalFrame(1);
  });
  localPanel?.addEventListener('wheel', (e) => {
    e.preventDefault();
    if (e.altKey || e.metaKey) {
      stepLocalFrame(e.deltaY > 0 ? -1 : 1);
      return;
    }
    stepLocalZoom(e.deltaY > 0 ? 1 : -1);
  }, { passive: false });

  /* M22 — pinch-and-step on touch: pinch-out descends / grows local, pinch-in steps back.
   * Button equivalents: local +/− and keyboard Enter / Esc (M24). */
  const _pinchPts = new Map();
  let _pinchD0 = null;
  let _pinchArmed = false;
  const pinchDist = () => {
    if (_pinchPts.size < 2) return null;
    const [a, b] = [..._pinchPts.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  const pinchMid = () => {
    const pts = [..._pinchPts.values()];
    if (pts.length < 2) return { x: 0, y: 0 };
    return { x: (pts[0].x + pts[1].x) * 0.5, y: (pts[0].y + pts[1].y) * 0.5 };
  };
  canvas.addEventListener('touchstart', (e) => {
    for (const t of e.changedTouches) _pinchPts.set(t.identifier, { x: t.clientX, y: t.clientY });
    if (_pinchPts.size >= 2) {
      _pinchD0 = pinchDist();
      _pinchArmed = true;
      dragging = false;
      panning = false;
    }
  }, { passive: true });
  canvas.addEventListener('touchmove', (e) => {
    for (const t of e.changedTouches) {
      if (_pinchPts.has(t.identifier)) _pinchPts.set(t.identifier, { x: t.clientX, y: t.clientY });
    }
    if (!_pinchArmed || _pinchPts.size < 2 || !_pinchD0) return;
    const d = pinchDist();
    if (d == null) return;
    const ratio = d / _pinchD0;
    if (ratio > 1.22) {
      e.preventDefault();
      _pinchD0 = d;
      _pinchArmed = false;
      const mid = pinchMid();
      const cell = S.focusCell ?? S.pinCell ?? S.kbCursor ?? desktopPick(mid.x, mid.y);
      if (!S.localExpanded) {
        if (cell != null && cell >= 0) lookAtCell(cell, { ms: 1400, descend: true, zoom: true });
        S.localExpanded = true;
        try { applyLocalLayout(); } catch { expected('ORR-EXPECTED-LAYOUT', 'local layout'); }
        dispatchIntent('descend', { cell, via: 'pinch' }, 'touch');
        announcePlanet('Pinch descend — local map');
        showErr('Pinched in — Esc or pinch-in to step back');
      } else {
        stepLocalFrame(1);
      }
    } else if (ratio < 0.82) {
      e.preventDefault();
      _pinchD0 = d;
      _pinchArmed = false;
      if (S.localExpanded) stepLocalFrame(-1);
      else stepLocalZoom(1);
    }
  }, { passive: false });
  const endPinch = (e) => {
    for (const t of e.changedTouches) _pinchPts.delete(t.identifier);
    if (_pinchPts.size < 2) {
      _pinchD0 = null;
      _pinchArmed = false;
    }
  };
  canvas.addEventListener('touchend', endPinch, { passive: true });
  canvas.addEventListener('touchcancel', endPinch, { passive: true });

  canvas.addEventListener('pointerdown', (e) => {
    try { canvas.focus({ preventScroll: true }); } catch { try { canvas.focus(); } catch { expected('ORR-EXPECTED-FOCUS', 'canvas'); } }
    audioInit();
    installDarkAudioBus();
    if (TABLE.enabled) {
      const slot = desktopTablePick(e.clientX, e.clientY);
      if (slot) {
        loadTableSlot(slot);
        e.preventDefault();
        return;
      }
    }
    if (e.ctrlKey || e.metaKey) {
      const cell = desktopPick(e.clientX, e.clientY);
      if (cell != null && cell >= 0) {
        pinLocal(cell);
        e.preventDefault();
        return;
      }
    }
    // Shift-drag / middle-mouse: slide the globe aside so the map can breathe.
    if (e.button === 1 || (e.shiftKey && !e.altKey && e.button === 0)) {
      panning = true;
      lastX = e.clientX;
      lastY = e.clientY;
      S.angVel[0] = 0;
      S.angVel[1] = 0;
      canvas.style.cursor = 'grabbing';
      canvas.setPointerCapture(e.pointerId);
      e.preventDefault();
      return;
    }
    if (e.button === 2 || e.altKey || (activeTool !== 'inspect' && !e.shiftKey)) {
      const cell = desktopPick(e.clientX, e.clientY);
      if (activeTool === 'buster') {
        startCommitHold(cell);
        return;
      }
      if (activeTool === 'meteor') {
        dispatchIntent('act', {
          cell,
          opts: {
            mass: 0.8 + (W.rngGod?.() ?? 0.5) * 0.6,
            velocity: 0.7 + (W.rngGod?.() ?? 0.5) * 0.5,
            angle: 30 + (W.rngGod?.() ?? 0.5) * 50,
          },
        }, 'pointer');
      } else if (e.altKey && (activeTool === 'inspect' || !TOOLS.find((t) => t.id === activeTool)?.drag)) {
        fingerOfGod(cell, e.shiftKey ? 'delete' : 'boost');
        playEvent('seed', 0.5);
      } else {
        const tool = TOOLS.find((t) => t.id === activeTool);
        setPinpoint(false);
        setBrushInvert(e.altKey && (activeTool === 'raise' || activeTool === 'lower'));
        if (tool?.drag && cell >= 0) {
          setCrustOceanic(activeTool === 'crust' && e.shiftKey);
          const r = beginToolDrag(cell);
          if (r?.ok) { S.toolDrag = true; canvas.setPointerCapture(e.pointerId); }
          else dispatchIntent('act', { cell, opts: { oceanic: e.shiftKey } }, 'pointer');
        } else {
          dispatchIntent('act', { cell, opts: { oceanic: e.shiftKey } }, 'pointer');
        }
      }
      return;
    }
    dragging = true;
    lastX = e.clientX; lastY = e.clientY;
    canvas._spinStartX = e.clientX;
    canvas._spinStartY = e.clientY;
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener('pointerup', (e) => {
    if (S.toolDrag) { endToolDrag(); S.toolDrag = false; setBrushInvert(false); refreshColours(1); }
    if (S.commitHold) cancelCommitHold(false);
    if (panning) {
      panning = false;
      canvas.style.cursor = '';
    }
    if (dragging && activeTool === 'inspect' && e) {
      const dist = Math.hypot(e.clientX - (canvas._spinStartX || 0), e.clientY - (canvas._spinStartY || 0));
      if (dist > 22 && S.lessonId === 'hold-earth') S._lessonSpun = true;
      if (dist < 6) {
        const cell = desktopPick(e.clientX, e.clientY);
        if (cell != null && cell >= 0) {
          pinLocal(cell);
          dispatchIntent('act', { cell }, 'pointer');
        }
      }
    }
    dragging = false;
  });
  canvas.addEventListener('dblclick', (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const cell = desktopPick(e.clientX, e.clientY);
    if (Math.abs(S.camPanX) > 1e-4 || Math.abs(S.camPanY) > 1e-4) {
      if (cell == null || cell < 0) resetCamPan();
      else lookAtCell(cell);
      return;
    }
    if (cell == null || cell < 0) return;
    lookAtCell(cell);
  });
  canvas.addEventListener('pointercancel', () => {
    panning = false;
    dragging = false;
    canvas.style.cursor = '';
  });
  canvas.addEventListener('pointermove', (e) => {
    if (S.toolDrag) {
      const cell = desktopPick(e.clientX, e.clientY);
      if (cell >= 0) { moveToolDrag(cell); refreshColours(0.4); }
      return;
    }
    if (panning) {
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      panGlobe(dx, dy);
      dispatchIntent('pan', { dx, dy }, 'pointer');
      lastX = e.clientX;
      lastY = e.clientY;
      return;
    }
    if (!dragging) {
      setPinpoint(false);
      const cell = desktopPick(e.clientX, e.clientY);
      setLocalHover(cell);
      const tool = TOOLS.find((t) => t.id === activeTool);
      if (cell >= 0 && (tool?.drag || tool?.group === 'dis' || tool?.id === 'seedGuild' || tool?.id === 'seed')) {
        previewBrush(cell);
      } else {
        BRUSH.preview = [];
        BRUSH.previewCenter = -1;
      }
      requestPreview();
      return;
    }
    const dx = (e.clientX - lastX) / 220, dy = (e.clientY - lastY) / 220;
    lastX = e.clientX; lastY = e.clientY;
    // Angular momentum. Item 11.
    S.angVel[0] = S.angVel[0] * 0.4 + dy * 0.6;
    S.angVel[1] = S.angVel[1] * 0.4 + dx * 0.6;
    qAxis(tmpQ, 0, 1, 0, dx); qmul(S.q, tmpQ, S.q);
    qAxis(tmpQ, 1, 0, 0, dy); qmul(S.q, tmpQ, S.q);
    qnorm(S.q);
    dispatchIntent('spin', { dx, dy }, 'pointer');
  });
  canvas.addEventListener('auxclick', (e) => {
    if (e.button === 1) e.preventDefault();
  });
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    const factor = 1 + Math.sign(e.deltaY) * (S.camDist > 8 ? 0.14 : 0.09);
    dispatchIntent('zoom', { factor, dir: Math.sign(e.deltaY) }, 'pointer');
  }, { passive: false });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  bindPlanetKeys(window, {
    planetCanvasFocused,
    localActive: () => !!(S.localExpanded || S.localPin >= 0),
    spinPlanet,
    zoomPlanet,
    zoomByFactor: (factor) => {
      S.camDist = clamp(S.camDist * factor, camDistMin(W), CAM_DIST_MAX);
    },
    panGlobe,
    moveKbCursor,
    descendKeyboard,
    applyToolAtKbCursor,
    nudgeLocal,
    adoptTool,
    announcePlanet,
    toolsUnlocked,
    W,
    TOOLS,
    handleEscape(e) {
      audioInit();
      installDarkAudioBus();
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT')) return true;
      if (e.key !== 'Escape') return false;
      if (isPhone() && document.getElementById('dock')?.classList.contains('is-open')) {
        e.preventDefault();
        setPhoneDock(false);
        return true;
      }
      if (S.originSketch) {
        e.preventDefault();
        skipOriginCeremony();
        return true;
      }
      if (doorIsOpen()) {
        e.preventDefault();
        void startLesson('hold-earth');
        return true;
      }
      if (localKeyOpen()) {
        e.preventDefault();
        closeLocalKey();
        return true;
      }
      const kbdSheet = document.getElementById('kbdSheet');
      if (kbdSheet && !kbdSheet.hidden) {
        e.preventDefault();
        closeKbdSheet();
        return true;
      }
      const cat = document.getElementById('catpanel');
      if (cat?.classList.contains('open')) {
        e.preventDefault();
        cat.classList.remove('open');
        document.getElementById('catbtn')?.setAttribute('aria-pressed', 'false');
        restoreOverlayFocus();
        return true;
      }
      const land = document.getElementById('landpick');
      if (land?.classList.contains('open')) {
        e.preventDefault();
        closeLandPicker();
        return true;
      }
      const lim = document.querySelector('.limits-sheet-host');
      if (lim) {
        e.preventDefault();
        lim.remove();
        return true;
      }
      if (S.localExpanded) {
        e.preventDefault();
        stepLocalFrame(-1);
        return true;
      }
      return false;
    },
    handleGlobal(e) {
      audioInit();
      installDarkAudioBus();
      const overlay = activeOverlayRoot();
      if (overlay && trapTab(overlay, e)) return;
      if ((e.key === '?' && e.shiftKey) || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        const sheet = document.getElementById('kbdSheet');
        if (sheet?.hidden === false) closeKbdSheet();
        else openKbdSheet();
        return;
      }
      if (e.key === '?' && !e.shiftKey) {
        e.preventDefault();
        onTourButton();
        return;
      }
      const onPlanet = planetCanvasFocused();
      if (e.key === 'Home') {
        e.preventDefault();
        resetCamPan();
        dispatchIntent('recenter', {}, 'keyboard');
        return;
      }
      if (e.key === '[' || e.key === ']') {
        e.preventDefault();
        stepCatalogueWorld(e.key === ']' ? 1 : -1);
        return;
      }
      if (e.key >= '1' && e.key <= '5') setRuleset(+e.key - 1);
      else if (e.key === 'r' || e.key === 'R') runGenerate(freshSeed(), W.rule);
      else if (e.key === ' ') { e.preventDefault(); togglePause(); }
      else if (e.key === ',' || e.key === '<') {
        e.preventDefault();
        applyTimeRate(-1);
      }
      else if (e.key === '.' || e.key === '>') {
        e.preventDefault();
        applyTimeRate(1);
      }
      else if ((e.key === 'z' || e.key === 'Z') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        const r = e.shiftKey ? redoStroke() : undoStroke();
        if (r) { showErr(r.note); refreshColours(1); needGeom(); refreshLayerPanel(); }
      }
      else if (e.shiftKey && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        const on = audioMute();
        showErr(on ? 'Sound off' : 'Sound on');
      }
      else if (e.shiftKey && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        if (S.follow) { S.follow = null; showErr('Released'); }
        else {
          const t = followTarget();
          if (t) {
            S.follow = t;
            pinLocal(t.cell);
            showErr(t.name ? `Following ${t.name}` : 'Following');
          }
        }
      }
      else if (e.shiftKey && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        setDayWatch(!S.dayWatch);
      }
      else if (!onPlanet && (e.key === '+' || e.key === '=')) {
        setTool('solar');
        onToolResult(useToolAt(S._localFocus >= 0 ? S._localFocus : 0, { delta: 0.05 }));
      }
      else if (!onPlanet && (e.key === '-' || e.key === '_')) {
        setTool('solar');
        onToolResult(useToolAt(S._localFocus >= 0 ? S._localFocus : 0, { delta: -0.05 }));
      }
      else if (!onPlanet) {
        const t = TOOLS.find((x) => x.key && x.key === e.key)
          || TOOLS.find((x) => x.key && x.key.toLowerCase() === e.key.toLowerCase());
        if (t) {
          if (toolsUnlocked(W)[t.id] === false) return;
          adoptTool(t);
        }
      }
    },
  });

  // R43 — pointer / touch / XR `act` lands here (keyboard handled in bindPlanetKeys).
  onIntent('act', (i) => {
    if (i.source === 'keyboard') return;
    const cell = i.payload?.cell;
    if (cell == null || cell < 0) return;
    onToolResult(useToolAt(cell, i.payload.opts || {}));
  });

  const applyPitchShot = async (id) => {
    S.pitchShot = true;
    _pitchHoldMoment = true;
    setPaused(true);
    closeLandPicker();
    closeDoor();
    document.getElementById('catpanel')?.classList.remove('open');
    document.getElementById('catbtn')?.setAttribute('aria-pressed', 'false');
    document.getElementById('dock')?.classList.remove('collapsed');
    document.getElementById('reveal')?.setAttribute('hidden', '');
    S.localGlobe = 'rim';
    refreshColours(1);
    if (id === 'hud') {
      applyScalePreset(S, 'hold');
      setDockTab('tools');
      setSuiteDesk('tools', 'land');
      showMoment('First occurrence', 'First free oxygen', 'present', momentRGB('firstOxygen'));
    } else if (id === 'currents') {
      applyScalePreset(S, 'hold');
      setDockTab('view');
      setSuiteDesk('view', 'layers');
      applyOverlayChoice('current');
      S.localExpanded = false;
      S.localSize = LOCAL_SIZE_M;
      pinLocal(findCoastalCell());
      syncLocalLayout();
    } else if (id === 'local') {
      applyScalePreset(S, 'hold');
      setDockTab('tools');
      setSuiteDesk('tools', 'land');
      applyOverlayChoice('none');
      S.localExpanded = true;
      S.localSize = LOCAL_SIZE_M;
      pinLocal(findCoastalCell());
      syncLocalLayout();
    } else if (id === 'worlds') {
      applyScalePreset(S, 'hold');
      setDockTab('tools');
      setSuiteDesk('tools', 'land');
      applyOverlayChoice('none');
      await fillCat();
      logCatalogueValidation();
      document.getElementById('catpanel')?.classList.add('open');
      document.getElementById('catbtn')?.setAttribute('aria-pressed', 'true');
      await renderCatalogue();
      enterOverlayFocus(document.getElementById('catpanel'), '#catclose');
    }
    updateHUD();
    refreshColours(1);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      globalThis.__orreryPitchReady = true;
    }));
  };

  initXR({
    gl, W, S, hands, tmpQ, TABLE,
    get activeTool() { return activeTool; },
    update, drawScene, showErr, audioInit, installDarkAudioBus,
    desktopFrame, setRuleset, loadTableSlot, RULESETS,
    get grabbing() { return grabbing; },
    set grabbing(v) { grabbing = v; },
  });
  setupXRModule();
  initHUD({
    S,
    refreshToolGates, bindRibbonTips, refreshLivedRibbonDom,
    announceNewMoments, refreshWorldModeStrip, showMoment,
    ensureDarkUi,
    darkHud: () => _darkHud,
    darkSpec: () => _darkSpec,
    refreshClimatePanel, refreshPlatesPanel,
  });
  // Catalogue validate deferred until first Worlds open / pitch worlds (K17).

  const gardened = maybeGardenReturn();
  let openingPinned = false;
  if (!gardened) {
    const opening = isDemoMode()
      ? { seed: 20260808, landscape: 'familiar', pinned: false }
      : pickOpening();
    openingPinned = !!opening.pinned;
    const bootRule = isDemoMode()
      ? (RULESETS.find((r) => r.id === 'thrive') || RULESETS[0])
      : RULESETS[0];
    const showWelcome = !gardened && !isDemoMode() && !isPlaytestMode() && !shouldOfferDoor();
    applyOpening(opening, { rule: bootRule, welcome: showWelcome });
    if (isDemoMode()) setLifeSpeed(2);
  }
  let pitch = null;
  try { pitch = new URLSearchParams(location.search).get('pitch'); } catch { expected('ORR-EXPECTED-URL', 'pitch'); }
  try {
    if (new URLSearchParams(location.search).get('profile') === '1') W._profileBag = true;
  } catch { expected('ORR-EXPECTED-URL', 'profile'); }
  if (pitch) void applyPitchShot(pitch);
  else if (isPlaytestMode()) {
    setPaused(false);
    skipReveal();
    markDoorSeen();
    document.getElementById('reveal')?.setAttribute('hidden', '');
    _playtest = mountPlaytestUI({ build: 'local' });
    if (!vandalDone()) startVandalHook({ demo: true });
    else showMoment('Playtest', 'One Strike act', 'Ignite or Meteor, then descend into the map.');
  } else if (isDemoMode()) {
    setPaused(false);
    paintLessonChip();
    if (!vandalDone()) startVandalHook({ demo: true });
    else {
      showMoment('Demo', 'Strike first', 'Ignite or Meteor — then open the local map. Tour is optional.');
    }
  } else if (!gardened && !vandalDone()) {
    setPaused(false);
    skipReveal();
    markDoorSeen();
    hideDockForOverlay();
    document.getElementById('reveal')?.setAttribute('hidden', '');
    startVandalHook();
  } else if (!gardened && !openingPinned && shouldOfferDoor()) {
    setPaused(true);
    setSuiteDesk('tools', 'land');
    skipReveal();
    hideDockForOverlay();
    openLandPicker({
      firstVisit: true,
      pause: true,
      onDone: () => {
        skipReveal();
        openDoor();
        paintLessonChip();
      },
    });
  }
  addEventListener('pagehide', persistGardenLeave);
  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') persistGardenLeave();
  });
  // desktopFrame already started after initGL (Q41)
  const quads = (6 * globeN() * globeN()).toLocaleString();
  console.log(`[orrery] foundations rebuild · ${NC.toLocaleString()} cells · ${quads} globe quads (${effectiveGlobeSubd()}× mesh) · ${(vIdx.length / 3).toLocaleString()} tris`);
}

function resize() {
  const d = Math.min(devicePixelRatio || 1, 2);
  const w = Math.floor(innerWidth * d), h = Math.floor(innerHeight * d);
  if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
}
addEventListener('resize', resize);

function desktopFrame(t) {
  if (xrSession) return;
  requestAnimationFrame(desktopFrame);
  resize();
  update(t);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, canvas.width, canvas.height);
  const sky = W.rule.sky;
  gl.clearColor(sky[0], sky[1], sky[2], 1);
  gl.clearDepth(1);
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  gl.enable(gl.DEPTH_TEST);
  const eye = [0, 0.28, S.camDist];
  m4persp(PROJ, CAM_FOV, canvas.width / canvas.height, 0.02, 900);
  m4lookAt(VIEW, eye, [0, 0, 0], [0, 1, 0]);
  drawScene(PROJ, VIEW, eye, false, S, hands);
}


boot();
